<template>
  <div class="main">
    <div class="left-menu-container">
      <Menus
        :menu="menu"
        icon="https://dsmlib.logistics.systems/Icons/icons8-maintenance-64.png"
        @menuClicked="menuClicked"
    /></div>
    <div class="right-container">
      <div class="right-inner-container flex-shrink-0 d-flex justify-content-end align-items-center" style="gap: 8px">
        {{ activeDateString }}
        <ButtonFilter
          buttonText="Change Filter"
          filterPosition="right"
          :filterWidth="420"
          :isOpen.sync="filterOpen"
        >
          <div class="d-flex flex-column">
            <BoxContainer
              title="Date"
              class="d-flex"
              :isCheckBox="true"
              :isChecked.sync="dateFilterActive"
              :checkBoxIsDisabled="workshopJobsTableLoading"
            >
              <DateTimePicker
                placeholder="From"
                :value.sync="filterStartDate"
                :showTime="false"
                :isDisabled="!dateFilterActive || workshopJobsTableLoading"
              />
              <DateTimePicker
                placeholder="To"
                :value.sync="filterEndDate"
                :showTime="false"
                :isDisabled="!dateFilterActive || workshopJobsTableLoading"
              />
            </BoxContainer>
            <BoxContainer
              title="Job #"
              class="d-flex"
              :isCheckBox="true"
              :isChecked.sync="jobNoFilterActive"
              :checkBoxIsDisabled="workshopJobsTableLoading"
            >
              <Input
                placeholder="Job #"
                :inputValue.sync="jobNoFilterValue"
                :isDisabled="!jobNoFilterActive || workshopJobsTableLoading"
                :errorMessage.sync="jobNoFilterValueError"
                validator="text,25"
              />
            </BoxContainer>
            <BoxContainer
              title="Vehicle"
              class="d-flex flex-column"
              :isCheckBox="true"
              :isChecked.sync="vehicleFilterActive"
              :checkBoxIsDisabled="workshopJobsTableLoading"
            >
              <InputQuery
                placeholder="Vehicle # - Vehicle Code"
                :selectedObject.sync="vehicleFilterValue"
                :items="vehicleList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :isDisabled="!vehicleFilterActive || workshopJobsTableLoading"
              />
              <Dropdown
                placeholder="Vehicle Category"
                :selectedObject.sync="vehicleCategoryFilterValue"
                :items="vehicleCategoryList"
                itemKey="label"
                :isDisabled="!vehicleFilterActive || workshopJobsTableLoading"
              />
            </BoxContainer>
            <BoxContainer
              title="Workshop"
              class="d-flex"
              :isCheckBox="true"
              :isChecked.sync="workshopFilterActive"
              :checkBoxIsDisabled="workshopJobsTableLoading"
            >
              <InputQuery
                placeholder="Workshop"
                :selectedObject.sync="workshopFilterValue"
                :items="supplierList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :isDisabled="!workshopFilterActive || workshopJobsTableLoading"
              />
            </BoxContainer>
            <CheckBox
              title="Show Cancelled Jobs"
              :isChecked.sync="showCancelledJobs"
              :isDisabled="workshopJobsTableLoading"
            />
            <Button class="ml-auto mt-2" @click="filterRefreshClick">Refresh</Button>
          </div>
        </ButtonFilter>
      </div>
      <div class="right-inner-container flex-grow-1 pt-0" style="min-height: 0">
        <TableBox
          :key="hasCancelledJobs ? 'workshop-jobs-cancelled' : 'workshop-jobs-active'"
          class="workshop-jobs-table"
          :tableData="workshopJobsTableDisplayData"
          :selectedRow.sync="workshopJobsTableSelectedRows"
          title="Workshop Jobs"
          :dataType="workshopJobsTableDataType"
          :loading="workshopJobsTableLoading"
          :maxSelection="1"
          :viewMode="viewMode"
          :hideColumns="workshopJobsTableHideColumns"
          :columnOrderViews.sync="workshopJobsTableColumnOrderViews"
          :columnOrderID="workshopJobsTableColumnOrderID"
        />
      </div>
    </div>

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      class="popup-entry"
      :updatedBy="lastUpdatedInfo"
      :createdBy="createdInfo"
      :title="popUpTitle"
      :viewMode="viewMode"
      :canPrint="true"
      :printViewOnly="true"
      :additionalButtons="workshopJobAdditionalButtons"
      printButton="Print Work Order"
      :requiredError="popupEntryRequiredError"
      @requiredClicked="popupEntryRequiredClicked"
      :successMessages="popupEntrySuccessMessages"
      :errorMessages="popupEntryErrorMessages"
      @save="popupEntrySaveClicked"
      @print="popupEntryPrintClicked"
      @additionalButtonClicked="resubmitApprovalClicked"
    >
      <div class="top-info-container">
        <div class="vehicle-selector-container">
          <InputQuery
            placeholder="Vehicle # - Vehicle Code"
            :isRequired="true"
            :selectedObject.sync="jobInventoryValue"
            :errorMessage.sync="jobInventoryValueError"
            :items="vehicleList"
            itemKey="label"
            :tableBoxHideColumns="['label']"
            :isDisabled="viewMode || menuMode == 'Edit'"
            ref="inventoryNo"
            @selected="inventoryNoSelected"
          />
          <div v-if="menuMode == 'New' && !jobInventoryValue" class="vehicle-selection-note">
            Select a vehicle first to continue with the rest of the form.
          </div>
        </div>
        <div class="job-date-summary">
          <DateTimePicker
            placeholder="Job Date"
            :isRequired="true"
            :value.sync="jobDateValue"
            :errorMessage.sync="jobDateValueError"
            :isDisabled="viewMode"
            ref="jobDate"
          />
        </div>
        <div class="job-price-approval-summary">
          <div class="job-total-summary" aria-live="polite">
            Total Job Price: <b>{{ localCurrency }} {{ totalJobPriceValue }}</b>
          </div>
          <span v-if="approvalSetup && approvalSetup.Enabled && approvalStatus && approvalStatus !== 'Disabled'" class="job-approval-status status-chip" :class="`status-chip--${approvalStatusTone}`" aria-live="polite">Approval: {{ approvalStatus }}</span>
        </div>
      </div>

      <div class="tab-component-container">
        <Tab
          class="tab-component"
          :tabs="tabItems"
          :tabIndex.sync="selectedTabIndex"
          @tabClicked="tabClick"
          :preventTabSwitch="true"
        >
          <div class="tab-container job-details-layout" slot="Job Details" :class="{ 'with-approval-history': approvalSetup && approvalSetup.Enabled || approvalHistory.length, 'with-audit-warning': approvalSetup && approvalSetup.Enabled && jobAuditWarning }">
            <div class="job-details-fields">
            <InputQuery
              placeholder="Trailer"
              :selectedObject.sync="trailerNoValue"
              :items="trailerList"
              itemKey="label"
              :tableBoxHideColumns="['label']"
              :isDisabled="!jobInventoryValue || viewMode"
            />
            <InputQuery
              placeholder="Requested By"
              :selectedObject.sync="requestedByValue"
              :items="employeeList"
              itemKey="label"
              :tableBoxHideColumns="['label', 'Employee']"
              :isDisabled="!jobInventoryValue || viewMode"
            />
            <div>
              <RadioButtonSet
                labelKey="label"
                :isHorizontal="true"
                :showBox="true"
                :isRequired="true"
                title="Repair Type"
                :buttons="repairTypeOptions"
                :selected.sync="repairTypeValue"
                :errorMessage.sync="repairTypeValueError"
                :isDisabled="!jobInventoryValue || viewMode"
                ref="repairType"
              />
            </div>
            <div style="margin-bottom: 12px">
              <RadioButtonSet
                labelKey="label"
                :isHorizontal="true"
                :showBox="true"
                :isRequired="true"
                title="Internal / External Job"
                :buttons="intExtOptions"
                :selected.sync="internalExternalValue"
                :errorMessage.sync="internalExternalValueError"
                :isDisabled="!jobInventoryValue || viewMode"
                ref="intext"
              />
            </div>
            <Input
              placeholder="Service Person"
              :inputValue.sync="servicePersonValue"
              :isDisabled="!jobInventoryValue || viewMode"
              validator="text,200"
            />
            <InputQuery
              placeholder="Workshop"
              :selectedObject.sync="workshopValue"
              :errorMessage.sync="workshopValueError"
              :items="supplierList"
              itemKey="label"
              :tableBoxHideColumns="['label']"
              :isDisabled="!jobInventoryValue || viewMode"
            />
            <Input
              placeholder="Mileage (KM)"
              :inputValue.sync="mileageValue"
              :isRequired="true"
              :errorMessage.sync="mileageValueError"
              :isDisabled="!jobInventoryValue || viewMode"
              ref="mileage"
              validator="positiveInteger,7"
            />
            <!-- Notes for Mileage -->
            <!-- should be autofill from vehicle -->
            <!-- this field will be saved into a real column in db -->
            <!-- validator set to 7 digits integer, but not sure if adding a new 'distance' to real validator is needed -->
            <!-- if set to real, by default it will only allow 5,2 which I think this input will easily exceed 5 whole digits -->
            <RemarksBox
              title="Remark"
              :text.sync="remark1Value"
              :maxLength="200"
              :isDisabled="!jobInventoryValue || viewMode"
              style="margin-top: 8px; margin-bottom: 8px"
            />
            <div style="display: flex; gap: 16px; margin-left: 5px; margin-bottom: 5px">
              <DateTimePicker
                placeholder="Date/Time In"
                :value.sync="dateTimeInValue"
                @dateChanged="dateTimeInDefaultOverridden = true"
                :errorMessage.sync="dateTimeInValueError"
                :isDisabled="!jobInventoryValue || viewMode"
                ref="dateTimeIn"
                :isRequired="false"
              />
              <!-- defaults to today -->
              <DateTimePicker
                placeholder="Date/Time Out"
                :value.sync="dateTimeOutValue"
                :isDisabled="!jobInventoryValue || viewMode"
                ref="dateTimeOut"
              />
            </div>
            <div class="job-confirmation-actions">
              <CheckBoxV2
                title="Confirm Job"
                style="margin: 0"
                :isChecked.sync="confirmJobValue"
                :isDisabled="!jobInventoryValue || viewMode"
              />
            </div>
            <div
              v-if="jobInventoryValue"
              style="margin-left: 5px; margin-top: 8px; display: flex; gap: 5px; align-items: baseline; flex-wrap: nowrap"
            >
              <RadioButtonSet
                labelKey="label"
                :isHorizontal="true"
                :showBox="true"
                title="Vehicle Availability"
                :buttons="availabilityOptions"
                style="display: flex; flex-shrink: 0"
                :selected.sync="inventoryAvailabilityValue"
                :isDisabled="!availabilityEnabled || viewMode"
              />
              <Input
                v-show="inventoryAvailabilityValue ? inventoryAvailabilityValue.keyValue == '0' : false"
                placeholder="Vehicle Remark"
                :isRequired="true"
                :inputValue.sync="inventoryUnavailableRemarkValue"
                :errorMessage.sync="inventoryUnavailableRemarkValueError"
                :isDisabled="!availabilityEnabled || viewMode"
                validator="text,150"
                ref="inventoryUnavailableRemark"
              />
            </div>
            </div>
            <div v-if="approvalSetup && approvalSetup.Enabled || approvalHistory.length" class="job-approval-history">
              <TableBox
                v-if="approvalHistory.length"
                title="Approval History"
                :tableData="approvalHistoryTableData"
                :allowExport="false"
                :allowEmptyNumeric="true"
                :columnWidth="[40, 100, 95, 150, 130, 220]"
                :dataType="{ '#': 'number', ['Amount (' + localCurrency + ')']: 'currency' }"
                :hideSearch="true"
                :hideBottomBlock="true"
                :maxSelection="0"
              />
              <template v-else>
                <div class="job-approval-history-title">Approval History</div>
                <p class="job-approval-history-empty">No approval history yet.</p>
              </template>
            </div>
            <footer v-if="approvalSetup && approvalSetup.Enabled && jobAuditWarning" class="job-audit-warning" role="status"><b>Change tracking unavailable.</b> {{ jobAuditWarning }}</footer>
          </div>
          <div class="tab-container table-tab-container" :slot="docTabTitle">
            <div class="table-container">
              <div class="cta-group" v-if="!viewMode">
                <Button @click="newDocClick" :disabled="docSelectedItem || !jobInventoryValue">Add</Button>
                <Button
                  @click="docDeleteClick"
                  :disabled="docSelectedItem || docTableSelectedRows.length < 1 || !jobInventoryValue"
                  >Delete</Button
                >
              </div>
              <TableBox
                :tableData="docTableDataFiltered"
                style="height: auto; flex-grow: 1; min-height: 0"
                :selectedRow.sync="docTableSelectedRows"
                :maxSelection="1"
                :hideColumns="['JobNo', 'Time Received', 'action', 'PicDocFile', 'PicDocType', 'seqNo', 'DocumentGUID', 'FileLoaded']"
                :dataType="docTableDataType"
                :additionalButtons="tableAdditionalButton"
                @buttonClicked="tableRowClicked"
                title="Documents"
                :columnOrderViews.sync="docTableColumnOrderViews"
                :columnOrderID="docTableColumnOrderID"
                hideBottomBlock
              />
            </div>
            <div v-if="docSelectedItem">
              <div v-if="!viewMode" class="cta-group">
                <Button @click="docSaveClick">Save</Button>
                <Button @click="tabFieldsCancel">Cancel</Button>
              </div>
              <div v-if="viewMode" class="cta-group">
                <Button @click="tabFieldsCancel">Close</Button>
              </div>
              <div style="margin-top: 5px">Attachment</div>

              <div v-if="docFields.docPicDocValue" style="display: flex; gap: 10px; align-items: center">
                <Button @click="docFileDownload">Download</Button>
                <div>{{ docFields.docPicDocName }}</div>
                <Tooltip
                  class="close-icon"
                  icon="https://dsmapi.logistics.systems/dsm6gIcons/icons8-close-90.png"
                  tooltipText="Clear all selected files"
                  @click.native="docFileChanged([])"
                />
              </div>

              <Input
                placeholder="Select File"
                textType="file"
                :isDisabled="!jobInventoryValue || viewMode"
                :inputValue.sync="docFields.docPicDocName"
                ref="PicDocClear"
                @fileChanged="docFileChanged"
                v-else
              />

              <Input
                placeholder="Reference No"
                :isDisabled="!jobInventoryValue || viewMode"
                :inputValue.sync="docFields.docReferenceNoValue"
                :errorMessage.sync="docReferenceNoError"
                validator="text,20"
              />
              <Dropdown
                placeholder="Doc Type"
                :isDisabled="!jobInventoryValue || viewMode"
                :items="docTypeList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :selectedObject.sync="docFields.docDocTypeValue"
              />
              <DateTimePicker
                placeholder="Document Date"
                :showTime="false"
                :isDisabled="!jobInventoryValue || viewMode"
                :value.sync="docFields.docDocDateValue"
              />
              <DateTimePicker
                placeholder="Date Received"
                :isDisabled="!jobInventoryValue || viewMode"
                :value.sync="docFields.docDateReceivedValue"
              />
              <Dropdown
                placeholder="Doc Code"
                :isDisabled="!jobInventoryValue || viewMode"
                :items="docCodeList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :selectedObject.sync="docFields.docDocCodeValue"
              />

              <RemarksBox
                title="Description / Notes"
                :isDisabled="!jobInventoryValue || viewMode"
                :text.sync="docFields.docRemarkValue"
                :maxLength="200"
                style="margin-top: 8px"
              />
            </div>
          </div>
          <div class="tab-container table-tab-container" :slot="taskTabTitle">
            <div class="table-container">
              <div class="workshop-task-toolbar">
                <div class="workshop-task-import">
                  <Button
                    @click="openVehicleTodoPopup"
                    :disabled="(!viewMode && taskSelectedItem) || !jobInventoryValue || (menuMode != 'New' && !jobNoValue)"
                    >{{ viewMode ? 'View Vehicle Task' : 'Attach Vehicle Task' }}</Button
                  >
                  <span class="vehicle-todo-attached-count">Attached: {{ vehicleTodoAttachedCount }}</span>
                </div>
                <div class="cta-group workshop-task-actions" v-if="!viewMode">
                  <Button @click="openMaintenanceImport" :disabled="taskSelectedItem || !jobInventoryValue">Import Service</Button>
                  <Button @click="openTaskAdd" :disabled="taskSelectedItem || !jobInventoryValue">Add Service</Button>
                  <Button
                    @click="openTaskGroupEdit"
                    :disabled="taskSelectedItem || !jobInventoryValue"
                    >Edit Service</Button
                  >
                  <Button
                    @click="taskDeleteClick"
                    :disabled="taskSelectedItem || taskTableSelectedRows.length < 1 || !jobInventoryValue"
                    >Delete</Button
                  >
                </div>
              </div>
              <TableBox
                :tableData="taskTableDataFiltered"
                style="height: auto; flex-grow: 1; min-height: 0"
                :maxSelection="1"
                :selectedRow.sync="taskTableSelectedRows"
                :hideColumns="['JobNo', 'Service Code', 'Service Description', 'Time Start', 'Time End', 'action', 'seqNo', '_groupBy', 'TaskGuid']"
                groupBy="_groupBy"
                :dataType="taskTableDataType"
                :additionalButtons="taskTableAdditionalButton"
                @buttonClicked="tableRowClicked"
                title="Tasks"
                :columnOrderViews.sync="taskTableColumnOrderViews"
                :columnOrderID="taskTableColumnOrderID"
                hideBottomBlock
              />
            </div>
            <div v-if="taskSelectedItem && tabFieldsAction != 'N'">
              <div v-if="!viewMode" class="cta-group">
                <Button @click="taskSaveClick">Save</Button>
                <Button @click="tabFieldsCancel">Cancel</Button>
              </div>
              <div v-if="viewMode" class="cta-group">
                <Button @click="tabFieldsCancel">Close</Button>
              </div>
              <Dropdown
                placeholder="Service Code"
                :isRequired="true"
                :items="taskServicesList"
                itemKey="label"
                :selectedObject.sync="taskFields.taskServiceValue"
                @selected="taskServiceSelected"
                :errorMessage.sync="taskServiceError"
                :isDisabled="!jobInventoryValue || viewMode"
              />
              <Dropdown
                placeholder="Task"
                :isRequired="!taskServiceIsTask"
                :items="taskServicesTaskList"
                itemKey="label"
                :selectedObject.sync="taskFields.taskServiceTaskValue"
                :errorMessage.sync="taskServiceTaskError"
                :isDisabled="!jobInventoryValue || viewMode"
              />
              <Input
                placeholder="Service Person"
                :inputValue.sync="taskFields.taskServicePersonValue"
                :isDisabled="!jobInventoryValue || viewMode"
                validator="text,200"
              />
              <DateTimePicker
                placeholder="Date/Time Start"
                :value.sync="taskFields.taskDateTimeStartValue"
                :isDisabled="!jobInventoryValue || viewMode"
              />
              <!-- submit date and time seperately -->
              <DateTimePicker
                placeholder="Date/Time End"
                :value.sync="taskFields.taskDateTimeEndValue"
                :isDisabled="!jobInventoryValue || viewMode"
              />
              <!-- submit date and time seperately -->
              <RemarksBox
                title="Remark"
                :text.sync="taskFields.taskRemarkValue"
                :maxLength="200"
                :isDisabled="!jobInventoryValue || viewMode"
                style="margin-top: 8px"
              />
            </div>
          </div>
          <div class="tab-container table-tab-container" :slot="partTabTitle">
            <div class="table-container">
              <div class="cta-group" v-if="!viewMode">
                <div
                  v-if="
                    !isLatestTyreJob &&
                    partTableSelectedRows[0] &&
                    partTableSelectedRows[0].tyreSeqNo &&
                    !partSelectedItem
                  "
                  style="color: red"
                  >Job is not latest for {{ jobInventoryValue['Inventory #'] }}! Unable to delete part.</div
                >

                <Button @click="newPartClick" :disabled="partSelectedItem || !jobInventoryValue">Add</Button>
                <Button
                  @click="partDeleteClick"
                  :disabled="
                    partSelectedItem ||
                    partTableSelectedRows.length < 1 ||
                    !jobInventoryValue ||
                    (isLatestTyreJob == false && partTableSelectedRows[0] && partTableSelectedRows[0].tyreSeqNo)
                  "
                  >Delete</Button
                >
              </div>
              <TableBox
                :tableData="partTableDataFiltered"
                style="height: auto; flex-grow: 1; min-height: 0"
                :maxSelection="1"
                :selectedRow.sync="partTableSelectedRows"
                :hideColumns="['seqNo', 'tyreSeqNo']"
                :dataType="partTableDataType"
                :additionalButtons="tableAdditionalButton"
                @buttonClicked="tableRowClicked"
                title="Parts / Labour"
                :columnOrderViews.sync="partTableColumnOrderViews"
                :columnOrderID="partTableColumnOrderID"
                hideBottomBlock
              />
            </div>
            <div v-if="partSelectedItem" class="part-fields-container">
              <div v-if="!viewMode" class="cta-group">
                <Button @click="partSaveClick">Save</Button>
                <Button @click="tabFieldsCancel">Cancel</Button>
              </div>
              <div v-if="viewMode" class="cta-group">
                <Button @click="tabFieldsCancel">Close</Button>
              </div>
              <div style="display: flex; gap: 5px;">
                <InputQuery
                  placeholder="Type"
                  :isRequired="true"
                  :selectedObject.sync="partFields.partTypeValue"
                  :errorMessage.sync="partTypeError"
                  :items="partTypeListNoTyres"
                  itemKey="label"
                  :isDisabled="!jobInventoryValue || viewMode"
                  :tableBoxHideColumns="[
                    'label',
                    'Expense Code',
                    'Unique Code',
                    'IsExpenses',
                    'isExpense',
                    'Unit Price',
                    'SuppCode',
                    'Spp_Name',
                    'ProdTxnID',
                    'Active',
                  ]"
                  @selected="partTypeSelected"
                />
              </div>
              <div style="margin-top: 8px; margin-bottom: 4px">
                <DropdownSearch
                  placeholder="Warehouse Item"
                  primaryKey="label"
                  primaryLabel="Serial No (Warehouse Code)"
                  :items="warehouseItemListFiltered"
                  :isLoading="stockListsLoading"
                  :selectedObject.sync="partFields.partWarehouseItemValue"
                  :search.sync="partFields.partWarehouseItemSearch"
                  :searchKeys="warehouseItemSearchKeys"
                  :inlineSecondaryFields="warehouseItemInlineSecondaryFields"
                  :secondaryFields="warehouseItemSecondaryFields"
                  :hideEmptySecondaryFields="true"
                  :showAllSecondaryFields="true"
                  :showAllResultsOnSelectedFocus="true"
                  :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                  :minimumSearchLength="1"
                  @selected="warehouseItemSelected"
                />
              </div>
              <div
                style="margin-top: 4px; margin-bottom: 8px; padding: 3px 5px; color: #664d03; background-color: #fff3cd; border: 1px solid #ffecb5; font-size: 11px; line-height: 14px"
              >
                <b>Note:</b> The previous <b>Taken From</b> and <b>Serial No</b> fields have been combined into <b>Warehouse Item</b>. For items not taken from warehouse, details can still be entered manually.
              </div>
              <div style="width: 100%">
                <div class="serial-no-input">
                  <Input
                    placeholder="Serial No"
                    :isRequired="serialNoRequired"
                    :inputValue.sync="partFields.partSerialNoInputValue"
                    :errorMessage.sync="partSerialNoError"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,25"
                    @inputChanged="partSerialNoInputChanged"
                  />
                  <div class="serial-no-status">
                    <img
                      v-if="partSerialNoChecking || partSerialNoCheckResult !== null"
                      :src="partSerialNoChecking ? 'https://dsmlib.logistics.systems/Icons/LoadingCircle.gif' : partSerialNoCheckResult ? 'https://dsmlib.logistics.systems/Icons/GreenTick.png' : 'https://dsmlib.logistics.systems/Icons/Cross.png'"
                      width="14" height="14"
                      :title="partSerialNoChecking ? 'Checking Serial No' : partSerialNoCheckResult ? 'serial no available' : partSerialNoError"
                      :alt="partSerialNoChecking ? 'Checking Serial No' : partSerialNoCheckResult ? 'serial no available' : partSerialNoError"
                    />
                  </div>
                </div>
              </div>
              <div class="flex-container" style="align-items: baseline">
                <div class="w-100">
                  <Input
                    placeholder="Part No"
                    :inputValue.sync="partFields.partPartNoValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Size"
                    :inputValue.sync="partFields.partSizeValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                    validator="text,25"
                  />
                  <InputQuery
                    placeholder="Warehouse"
                    :selectedObject.sync="partFields.partTakenFromValue"
                    :search.sync="partFields.partTakenFromInputValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :items="warehouseList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                    @selected="partWarehouseSelected"
                  />
                  <DateTimePicker
                    placeholder="MFG Date"
                    :value.sync="partFields.partMFGDateValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :showTime="false"
                  />
                  <DateTimePicker
                    placeholder="Date Purchase"
                    :value.sync="partFields.partDatePurchaseValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :showTime="false"
                  />
                  <Input
                    placeholder="Invoice No"
                    :inputValue.sync="partFields.partInvoiceNoValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                    validator="text,25"
                  />
                  <!-- <InputQuery
                    placeholder="Service Code"
                    :selectedObject.sync="partServiceCodeValue"
                    :isDisabled="!jobInventoryValue || viewMode || !(partFields.partTypeValue)"
                  /> -->
                </div>
                <div class="w-100">
                  <Input
                    placeholder="Replace"
                    :inputValue.sync="partFields.partReplaceValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                    v-if="partTyreMode"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Model"
                    :inputValue.sync="partFields.partModelValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    validator="text,60"
                  />
                  <InputQuery
                    placeholder="Made In"
                    :selectedObject.sync="partFields.partMadeInValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :items="countryList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                  />
                  <InputQuery
                    placeholder="Manufacturer"
                    :selectedObject.sync="partFields.partManufacturerValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :items="manufacturerList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                  />
                  <InputQuery
                    placeholder="Supplier"
                    :selectedObject.sync="partFields.partPurchaseFromValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue || partWarehouseItemSelected"
                    :items="supplierList"
                    itemKey="label"
                  />
                  <Dropdown
                    placeholder="PO No."
                    :allowFreeText="true"
                    :search.sync="partFields.partPONoValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Insurance Claim No."
                    :inputValue.sync="partFields.partInsuranceNoValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                    validator="text,25"
                  />
                </div>
              </div>
              <div style="display: flex; align-items: baseline; gap: 5px">
                <div style="flex: 1; min-width: 0">
                  <Input
                    placeholder="Quantity"
                    :inputValue.sync="partFields.partQuantityValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                    validator="positiveDecimal,,2"
                    :errorMessage.sync="partQuantityError"
                    @focusChanged="partQuantityFocusChanged"
                  />
                  <div
                    v-if="partQuantityMaximum !== null"
                    style="color: darkslategray; font-size: 11px; line-height: 14px; white-space: nowrap"
                    >Available quantity: {{ partQuantityMaximum }}</div
                  >
                </div>
                <div style="flex: 1; min-width: 0">
                  <Input
                    :placeholder="`Unit Price (${localCurrency})`"
                    :inputValue.sync="partFields.partUnitPriceValue"
                    :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                    validator="money"
                  />
                </div>
                <div style="flex: 1; min-width: 0">
                  <Input
                    :placeholder="`Total Cost (${localCurrency})`"
                    :inputValue="partTotalCostValue"
                    :isDisabled="true"
                  />
                </div>
              </div>
              <RemarksBox
                title="Remark"
                :text.sync="partFields.partRemarkValue"
                :maxLength="200"
                :isDisabled="!jobInventoryValue || viewMode || !partFields.partTypeValue"
                style="margin-top: 8px"
              />
            </div>
          </div>
          <div class="tab-container tyre-config-tab" :slot="tyreTabTitle">
            <div v-if="!isLatestTyreJob" class="tyre-history-notice">
              Only tyre changes recorded for this older job are shown. To view the current tyre configuration, create a
              new workshop job or open the Vehicle page.
            </div>
            <div class="tyre-config-tab-container">
              <BoxContainer title="Tyre Positions" class="tyre-config-wrapper">
                <TyreGrid
                  :tyreLayout="tyreConfigLayout"
                  layoutType="LeftRight"
                  :selectedTyre.sync="tyreConfigLayoutSelectedItem"
                  @tyreClicked="tyreGridClicked"
                />
                <Dropdown
                  placeholder="Selected Tyre Position"
                  :items="tyreConfigLayout"
                  :selectedObject.sync="tyrePositionSelectedValue"
                  itemKey="label"
                  @selected="tyreDropdownSelected"
                />
              </BoxContainer>
              <div class="table-wrapper">
                <div class="cta-group">
                  <Button
                    @click="tyreConfigPopupOpen"
                    :disabled="viewMode ? !tyreConfigTableSelectedRow[0] : !tyrePositionSelectedValue"
                    >{{ tyreConfigButtonText }}</Button
                  >
                  <Button
                    v-if="!viewMode && tyreChangeAllowed"
                    :disabled="tyreConfigTableSelectedRow.length < 1"
                    @click="tyreConfigDeleteClicked"
                    >Delete</Button
                  ></div
                >
                <TableBox
                  :tableData="tyreConfigUpdateListFiltered"
                  :selectedRow.sync="tyreConfigTableSelectedRow"
                  title="Tyre Changes"
                  :dataType="tyreConfigUpdateListDataType"
                  @rowSelected="tyreTableRowSelected"
                  :hideColumns="['seqno', 'tyreindex', 'action', 'otherjobno', 'Date Changed', 'rotatedwith']"
                  :columnOrderViews.sync="tyreConfigTableColumnOrderViews"
                  :columnOrderID="tyreConfigTableColumnOrderID"
                  hideBottomBlock
                />
              </div>
            </div>
          </div>
        </Tab>
      </div>
    </PopupEntry>
    <div v-if="taskAddShow" class="vehicle-todo-popup">
      <div
        class="vehicle-todo-popup-panel task-add-panel"
        role="dialog"
        aria-modal="true"
        :aria-label="taskAddGroupAction == 'E' ? 'Edit Service and Tasks' : 'Add Service and Tasks'"
      >
        <div class="vehicle-todo-popup-header">
          <div class="vehicle-todo-popup-title">{{ taskAddGroupAction == 'E' ? 'Edit Service and Tasks' : 'Add Service and Tasks' }}</div>
        </div>
        <div class="task-add-body">
          <div class="task-add-toolbar">
            <Dropdown
              class="task-add-service"
              placeholder="Service Code"
              :isRequired="true"
              :items="taskServicesList"
              itemKey="label"
              :selectedObject.sync="taskAddServiceValue"
              @selected="taskAddServiceSelected"
              :errorMessage.sync="taskAddServiceError"
              :isDisabled="taskAddEditorOpen && taskAddGroupAction != 'E'"
            />
            <div class="cta-group task-add-list-actions">
              <Button @click="startTaskAdd" :disabled="!taskAddServiceValue || taskAddEditorOpen">Add Task</Button>
              <Button @click="deleteTaskAdd" :disabled="taskAddSelectedRows.length != 1 || taskAddEditorOpen">Delete Task</Button>
            </div>
          </div>
          <div :class="['task-add-layout', { 'task-add-layout-editor-open': taskAddEditorOpen }]">
            <div class="task-add-list">
              <TableBox
                :tableData="taskAddTableRows"
                :selectedRow.sync="taskAddSelectedRows"
                :maxSelection="1"
                :hideColumns="['_rowid', 'Service Code', 'Service Description', 'action', 'TaskGuid']"
                :dataType="taskTableDataType"
                :additionalButtons="taskAddTableButtons"
                @buttonClicked="taskAddTableRowClicked"
                title="Tasks"
                style="height: 100%; flex: 1; min-height: 220px"
              />
            </div>
            <div v-if="taskAddEditorOpen" class="task-add-editor">
              <div class="cta-group task-add-editor-actions">
                <Button @click="saveTaskAdd">Save</Button>
                <Button @click="taskAddEditorOpen = false">Cancel</Button>
              </div>
              <Dropdown
                placeholder="Task"
                :isRequired="true"
                :items="taskServicesTaskList"
                itemKey="label"
                :selectedObject.sync="taskAddTaskValue"
                :errorMessage.sync="taskAddTaskError"
              />
              <Input placeholder="Service Person" :inputValue.sync="taskAddPersonValue" validator="text,200" />
              <DateTimePicker placeholder="Date/Time Start" :value.sync="taskAddDateStartValue" />
              <DateTimePicker placeholder="Date/Time End" :value.sync="taskAddDateEndValue" />
              <RemarksBox title="Remark" :text.sync="taskAddRemarkValue" :maxLength="200" />
            </div>
          </div>
        </div>
        <div class="vehicle-todo-popup-footer">
          <div class="vehicle-todo-popup-actions">
            <Button :disabled="taskAddEditorOpen" @click="saveTaskAddGroup">
              {{ taskAddGroupAction == 'E' ? 'Save Service' : 'Add Service' }}
            </Button>
            <Button @click="taskAddShow = false">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="maintenanceImportShow" class="vehicle-todo-popup">
      <div class="vehicle-todo-popup-panel maintenance-import-panel" role="dialog" aria-modal="true" aria-label="Import Service">
        <div class="maintenance-import-list">
          <div v-if="maintenanceImportLoading">Getting maintenance due dates...</div>
          <TableBox
            v-else
            :tableData="maintenanceImportTableData"
            :dataType="maintenanceImportTableDataType"
            :allowEmptyNumeric="true"
            :maxSelection="0"
            :stickColumn="[0]"
            :hideColumns="['key', '_rowid', 'Tasks']"
            :additionalButtons="maintenanceImportTableButtons"
            @buttonClicked="importVehicleMaintenance"
            title="Import Service"
            style="height: 100%"
          />
        </div>
        <div class="vehicle-todo-popup-footer">
          <div class="vehicle-todo-popup-actions">
            <Button @click="maintenanceImportShow = false">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="vehicleTodoPopupShow" class="vehicle-todo-popup">
      <div class="vehicle-todo-popup-panel">
        <div class="vehicle-todo-popup-header">
          <div class="vehicle-todo-popup-title-row">
            <div class="vehicle-todo-popup-title">{{ vehicleTodoPopupViewOnly ? 'View Vehicle Task' : 'Attach Vehicle Task' }}</div>
            <Tooltip
              v-if="!vehicleTodoPopupViewOnly"
              tooltipText="Select or deselect tasks to attach or detach them."
              style="cursor: pointer"
            />
          </div>
        </div>
        <div class="vehicle-todo-popup-body">
          <VehicleTodoList
            ref="vehicleTodoLinkList"
            class="vehicle-todo-popup-content"
            mode="workshop-link"
            :vehicleNo="vehicleTodoPopupVehicleNo"
            :jobNo="vehicleTodoPopupJobNo"
            :viewOnly="vehicleTodoPopupViewOnly"
            :fitContentWidth="false"
            :deferLinking="true"
            :deferredSelectedTodoIDs="vehicleTodoPendingSelection"
            :deferredTodoUpdates="vehicleTodoPendingChanges.updates.length ? vehicleTodoPendingChanges.updates : null"
            :showAttachedTasks="vehicleTodoPopupShowAttached"
            :showCancelledTasks.sync="vehicleTodoPopupShowCancelled"
            @linkSelectionChanged="vehicleTodoSelectionChanged"
          />
        </div>
        <div class="vehicle-todo-popup-footer">
          <div v-if="!vehicleTodoPopupViewOnly" class="vehicle-todo-popup-filters">
            <CheckBoxV2
              title="Show Attached"
              :isChecked.sync="vehicleTodoPopupShowAttached"
              :isDisabled="vehicleTodoPopupViewOnly || vehicleTodoPopupSaving"
              @checkBoxClicked="vehicleTodoShowAttachedChanged"
            />
          </div>
          <div class="vehicle-todo-popup-actions">
            <Button v-if="!vehicleTodoPopupViewOnly" :disabled="vehicleTodoLinkSaving || vehicleTodoPopupSaving" @click="saveVehicleTodoPopup">Save</Button>
            <Button :disabled="vehicleTodoLinkSaving || vehicleTodoPopupSaving" @click="cancelVehicleTodoPopup">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
    <div class="tyre-config" v-if="tyreConfigPopupShow && showPopup">
      <div class="tyre-config-inner">
        <div class="tyre-form-row" :class="{ 'tyre-form-row-with-grid': tyreConfigSelectedVehicleLayout }">
        <div class="tyre-grid-container" v-if="tyreConfigSelectedVehicleLayout">
          <BoxContainer :title="`Tyres From: ${tyreConfigSelectedVehicleNo}`" class="w-100"
            ><TyreGrid
              :tyreLayout="tyreConfigSelectedVehicleLayout"
              layoutType="LeftRight"
              :selectedTyre.sync="tyreConfigRotateSelectedTyre"
              @tyreClicked="tyreConfigRotateClicked"
            />
            <Dropdown
              placeholder="Selected Tyre Position"
              :items="tyreConfigSelectedVehicleDropdownList"
              :selectedObject.sync="tyreConfigSelectedVehicleDropdownValue"
              itemKey="label"
              @selected="tyreConfigSelectedVehicleDropdownSelected"
              :isDisabled="!tyreConfigCanEditTyreChange || tyreConfigRotateOtherDisabled"
            />
          </BoxContainer>
        </div>
        <div class="form-container flex-container">
          <div class="tyre-info-container w-100 flex-container flex-column" style="align-self: flex-start">
            <BoxContainer :title="`Current Tyre [${tyrePopupSelectedPosition}]`" class="tyre-config-box">
              <Dropdown
                placeholder="Set Current Tyre Status"
                :items="tyreStatusOptions"
                :selectedObject.sync="tyreStatusValue"
                itemKey="label"
                :isRequired="true"
                :errorMessage.sync="tyreStatusValueError"
                :isDisabled="tyreStatusOptions.length == 1 || !tyreConfigCanEditTyreChange || tyreConfigRotateOtherDisabled"
                @selected="tyreStatusSelected"
              />
              <InputQuery
                placeholder="Send to warehouse"
                :isRequired="true"
                v-if="tyreStatusValue ? tyreStatusValue.keyValue == '5' : false"
                :items="warehouseList"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                :selectedObject.sync="tyreStoreInValue"
                style="margin-top: 4px"
                :errorMessage.sync="tyreStoreInValueError"
                :isDisabled="!tyreConfigCanEditTyreChange"
              />
              <RadioButtonSet
                title="Replace Tyre With:"
                :showBox="false"
                :buttons="tyreReplaceRadio"
                :selected.sync="tyreReplaceValue"
                labelKey="label"
                style="margin: 4px"
                :isRequired="true"
                @radioChanged="tyreConfigRadioChanged"
                :errorMessage.sync="tyreReplaceValueError"
                :isDisabled="!tyreConfigCanEditTyreChange || tyreConfigRotateOtherDisabled"
              />
              <InputQuery
                placeholder="Tyre From Warehouse"
                :isRequired="true"
                v-if="tyreReplaceValue ? tyreReplaceValue.keyValue == 'WHSE' : false"
                :items="tyreNewList"
                :isLoading="stockListsLoading"
                itemKey="Serial No"
                :selectedObject.sync="tyreExistingValue"
                @selected="tyreConfigWarehouseSelected"
                :errorMessage.sync="tyreExistingValueError"
                :isDisabled="!tyreConfigCanEditTyreChange"
                :tableBoxHideColumns="[
                  'ID',
                  'ProdTxnID',
                  'label',
                  'MadeIn',
                  'Model',
                  'ProdDate',
                  'InvoiceNo',
                  'PONo',
                  'AllocGUID',
                  'PickQtyBal',
                  'BinLoc,',
                ]"
              />
              <InputQuery
                placeholder="Select Other Vehicle"
                :isRequired="true"
                v-if="tyreReplaceValue ? tyreReplaceValue.keyValue == 'OTHER' : false"
                :selectedObject.sync="tyreConfigOtherVehicleValue"
                :items="vehicleList.filter((item) => item['Inventory #'] != jobInventoryValue['Inventory #'])"
                itemKey="label"
                :tableBoxHideColumns="['label']"
                @selected="tyreConfigOtherVehicleSelected"
                :errorMessage.sync="tyreConfigOtherVehicleValueError"
                :isDisabled="!tyreConfigCanEditTyreChange || tyreConfigRotateOtherDisabled"
              />
            </BoxContainer>
            <BoxContainer title="Tyre Info" class="flex-1 w-100 pb-3">
              <InputQuery
                placeholder="Tyre Type"
                :isRequired="true"
                :isDisabled="
                  (tyreConfigTyreInfoDisabled && tyreConfigTyreTypeDisabled) ||
                  !tyreConfigCanEditTyreChange ||
                  tyreConfigRotateOtherDisabled
                "
                :selectedObject.sync="tyreConfigTyreTypeValue"
                :items="tyreTypeListActive"
                itemKey="label"
                :tableBoxHideColumns="[
                  'label',
                  'Expense Code',
                  'Unique Code',
                  'IsExpenses',
                  'isExpense',
                  'Unit Price',
                  'SuppCode',
                  'Spp_Name',
                  'ProdTxnID',
                  'Active',
                ]"
                :errorMessage.sync="tyreConfigTyreTypeValueError"
                @selected="tyreTypeSelected"
              />
              <div class="flex-container">
                <div class="w-100">
                  <div class="serial-no-input">
                    <Input
                      placeholder="Serial No"
                      :isRequired="true"
                      :isDisabled="tyreConfigTyreInfoDisabled"
                      :inputValue.sync="tyreConfigSerialNoValue"
                      :errorMessage.sync="tyreConfigSerialNoValueError"
                      validator="text,25"
                      @inputChanged="tyreConfigSerialNoChanged"
                    />
                    <div class="serial-no-status">
                      <img
                        v-if="tyreConfigSerialNoChecking || tyreConfigSerialNoCheckResult !== null"
                        :src="tyreConfigSerialNoChecking ? 'https://dsmlib.logistics.systems/Icons/LoadingCircle.gif' : tyreConfigSerialNoCheckResult ? 'https://dsmlib.logistics.systems/Icons/GreenTick.png' : 'https://dsmlib.logistics.systems/Icons/Cross.png'"
                        width="14" height="14"
                        :title="tyreConfigSerialNoChecking ? 'Checking Serial No' : tyreConfigSerialNoCheckResult ? 'serial no available' : tyreConfigSerialNoValueError"
                        :alt="tyreConfigSerialNoChecking ? 'Checking Serial No' : tyreConfigSerialNoCheckResult ? 'serial no available' : tyreConfigSerialNoValueError"
                      />
                    </div>
                  </div>
                  <Input
                    placeholder="Part No"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigPartNoValue"
                    validator="text,25"
                  />
                  <Input
                    placeholder="Size"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigSizeValue"
                    validator="text,25"
                  />
                  <InputQuery
                    placeholder="Warehouse"
                    :isDisabled="
                      tyreConfigTyreInfoDisabled || (tyreReplaceValue ? tyreReplaceValue.keyValue == 'NEW' : false)
                    "
                    :selectedObject.sync="tyreConfigTakenFromValue"
                    :search.sync="tyreConfigTakenFromInputValue"
                    :items="warehouseList"
                    itemKey="label"
                    :allowFreeText="takenFromFreeText"
                    :tableBoxHideColumns="['label']"
                  />
                  <Input
                    :placeholder="`Unit Price (${localCurrency})`"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigUnitPriceValue"
                    @inputChanged="tyreConfigUnitPriceOnInput"
                    validator="money"
                  />
                  <DateTimePicker
                    placeholder="MFG Date"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :value.sync="tyreConfigMfgDateValue"
                    :showTime="false"
                  />
                </div>
                <div class="w-100">
                  <Input
                    placeholder="Model"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigModelValue"
                    validator="text,60"
                  />
                  <InputQuery
                    placeholder="Made In"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :selectedObject.sync="tyreConfigMadeInValue"
                    :items="countryList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                  />
                  <InputQuery
                    placeholder="Manufacturer"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :selectedObject.sync="tyreConfigManufacturerValue"
                    :items="manufacturerList"
                    itemKey="label"
                    :tableBoxHideColumns="['label']"
                  />
                  <InputQuery
                    placeholder="Supplier"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :selectedObject.sync="tyreConfigSupplierValue"
                    :items="supplierList"
                    itemKey="label"
                  />
                  <Input
                    placeholder="PO No."
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :inputValue.sync="tyreConfigPONoValue"
                    validator="text,25"
                  />
                  <DateTimePicker
                    placeholder="Date Purchase"
                    :isDisabled="tyreConfigTyreInfoDisabled"
                    :value.sync="tyreConfigDatePurchaseValue"
                    :showTime="false"
                  />
                </div>
              </div>
            </BoxContainer>
            <BoxContainer title="Job Part Info" class="w-100 flex-1">
              <Input
                v-if="tyreJobPartInfoVisible"
                placeholder="Previous Tyre Serial No"
                :inputValue.sync="tyreConfigOldSerialNoValue"
                :isDisabled="true"
              />
              <Input
                v-if="tyreJobPartInfoVisible"
                placeholder="Price"
                :inputValue.sync="tyreConfigJobPriceValue"
                :isDisabled="viewMode || (tyreReplaceValue ? tyreReplaceValue.keyValue == 'NEW' : false)"
                validator="money"
              />
              <Input
                v-if="tyreJobPartInfoVisible"
                placeholder="Invoice No."
                :inputValue.sync="tyreConfigInvoiceNoValue"
                :isDisabled="viewMode"
                validator="text,25"
              />
              <Input
                v-if="tyreJobPartInfoVisible"
                placeholder="Insurance Claim No."
                :inputValue.sync="tyreConfigInsuranceNoValue"
                :isDisabled="viewMode"
                validator="text,25"
              />
              <RemarksBox
                title="Remarks"
                :text.sync="tyreConfigRemarksValue"
                :maxLength="200"
                :isDisabled="viewMode"
                style="margin-top: 8px"
              />
            </BoxContainer>
            <BoxContainer
              title="Link To Existing Job?"
              class="w-100"
              v-if="tyreReplaceValue ? tyreReplaceValue.keyValue == 'OTHER' : false"
            >
              <Dropdown
                placeholder="Workshop Job No."
                :items="tyreRotateOtherVehicleWorkshopJobList"
                :selectedObject.sync="tyreRotateOtherVehicleWorkshopJobValue"
                :search.sync="tyreRotateOtherVehicleWorkshopJobInputValue"
                :errorMessage.sync="tyreRotateOtherVehicleWorkshopJobValueError"
                itemKey="label"
                :isDisabled="!tyreConfigSelectedVehicleLayout || disableAttachJob"
                :isRequired="true"
              />
            </BoxContainer>
          </div>
        </div>
        </div>
        <div class="cta-group">
          <div class="tyre-config-error-messages">
            <div class="error-text" v-if="tyreConfigErrorText">{{ tyreConfigErrorText }}</div>
            <div class="error-text" v-if="!viewMode && tyreConfigEditWarningText">{{ tyreConfigEditWarningText }}</div>
            <div class="error-text" v-if="!viewMode && tyreConfigRotateOtherDisabled"
              >Edits to tyre changes are not allowed for saved changes with other vehicles.</div
            >
            <div class="error-text" v-if="!viewMode && tyreConfigRotateOtherWorkshopJobIsNotLatest"
              >Changes exists for non latest job. Pleae choose another vehicle.</div
            >
          </div>
          <Button v-if="tyreConfigCanSavePopup" @click="tyreConfigPopupSave">Confirm</Button>
          <Button @click="tyreConfigPopupShow = false">{{ viewMode || !tyreConfigCanSavePopup ? 'Close' : 'Cancel' }}</Button>
        </div>
      </div>
    </div>
    <UpdateTyreStatus :popupOpen.sync="updateTyrePopupOpen" />
  </div>
</template>

<script>
import TyreGrid from '@/components/TyreGrid.vue';
import UpdateTyreStatus from './UpdateTyreStatus.vue';
import RemarksBox from '@/components/RemarksBox.vue';
import VehicleTodoList from '@/components/tabComponents/Vehicle/VehicleTodoList.vue';

const cleanText = (value) => (typeof value == 'string' ? value.trim() : value);
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);

export default {
  components: {
    TyreGrid,
    UpdateTyreStatus,
    RemarksBox,
    VehicleTodoList,
  },
  data() {
    const activeEndDate = new Date();
    const activeStartDate = new Date(activeEndDate);
    activeStartDate.setDate(activeStartDate.getDate() - 14);

    return {
      filterOpen: false,
      dateFilterActive: true,
      jobNoFilterActive: false,
      vehicleFilterActive: false,
      workshopFilterActive: false,

      defaultWeeksToShow: 2,

      activeStartDate,
      activeEndDate,
      filterStartDate: new Date(activeStartDate),
      filterEndDate: new Date(activeEndDate),
      showCancelledJobs: false,

      jobNoFilterValue: '',
      jobNoFilterValueError: '',
      vehicleFilterValue: null,
      vehicleCategoryFilterValue: null,
      workshopFilterValue: null,

      //workshop table items
      workshopJobsTableData: [],
      workshopJobsTableSelectedRows: [],
      workshopJobsTableDataType: {
        '#': 'number',
        'Job #': 'string',
        'Job Date': 'datetime',
        'Vehicle # - Vehicle Code': 'string',
        'Repair Type': 'string',
        'Internal / External': 'string',
        Confirmed: 'string',
        'Approval Status': 'string',
        'Pending Level': 'string',
        'Approved Total': 'currency',
        'Tyre Change': 'string',
        'Requested By': 'string',
        Workshop: 'string',
        'Service Person': 'string',
        'Mileage (KM)': 'number',
        Remark: 'string',
        'Date / Time In': 'datetime',
        'Date / Time Out': 'datetime',
        'Trailer #': 'string',
        'Last Update Time': 'datetime',
        'Last Update UserID': 'string',
        IsLatest: 'string',
        Cancelled: 'string',
        canDelete: 'number',
        canDeleteReason: 'string',
      },
      workshopJobsTableLoading: true,
      workshopPageLoading: true,
      workshopJobsTableColumnOrderViews: [],
      workshopJobsTableColumnOrderID: null,
      docTableColumnOrderViews: [],
      docTableColumnOrderID: null,
      taskTableColumnOrderViews: [],
      taskTableColumnOrderID: null,
      partTableColumnOrderViews: [],
      partTableColumnOrderID: null,
      tyreConfigTableColumnOrderViews: [],
      tyreConfigTableColumnOrderID: null,

      //popupstuff
      showPopup: false,
      selectedTabIndex: 0,
      viewMode: false,
      menuMode: '',
      popupEntryRequiredError: [],
      popupEntrySuccessMessages: [],
      popupEntryErrorMessages: [],
      vehicleTodoPopupShow: false,
      vehicleTodoPopupVehicleNo: '',
      vehicleTodoPopupJobNo: '',
      vehicleTodoPopupViewOnly: false,
      vehicleTodoLinkSaving: false,
      vehicleTodoPopupSaving: false,
      vehicleTodoPopupShowAttached: false,
      vehicleTodoPopupShowCancelled: false,
      vehicleTodoAttachedCount: 0,
      vehicleTodoPendingChanges: { toLink: [], toUnlink: [], updates: [] },
      vehicleTodoPendingSelection: null,
      vehicleTodoPopupInitialAttachedCount: 0,
      vehicleTodoPopupSelectionInitialized: false,

      selectedVehicleInfo: [],
      tyreLayoutCache: {},

      jobFetched: null,

      //general details
      jobNoValue: '',
      jobNoValueError: '',
      jobDateValue: new Date(), //defaults to today
      jobDateValueError: '',
      jobInventoryValue: null,
      jobInventoryValueError: null,

      createdInfo: '',
      lastUpdatedInfo: '',

      //#region Job Details Tab
      trailerNoValue: null,
      requestedByValue: null,
      repairTypeValue: null,
      repairTypeValueError: '',
      internalExternalValue: null,
      internalExternalValueError: '',
      servicePersonValue: '',
      workshopValue: null,
      workshopValueError: '',
      mileageValue: '',
      mileageValueError: '',
      remark1Value: '',
      dateTimeInValue: null,
      dateTimeInDefaultEnabled: false,
      dateTimeInDefaultOverridden: false,
      dateTimeInValueError: '',
      dateTimeOutValue: null,
      confirmJobValue: false,
      approvalStatus: '',
      lastApprovalDate: null,
      approvedAmount: null,
      pendingLevel: null,
      approvalHistory: [],
      approvalSetup: null,
      jobAuditWarning: '',
      resubmitApproval: false,
      inventoryAvailabilityValue: null,
      inventoryUnavailableRemarkValue: '',
      inventoryUnavailableRemarkValueError: '',

      isLatestJob: true,
      isLatestTyreJob: true,

      ////trailer list in computed
      vehicleList: [],
      vehicleCategoryList: [],
      employeeList: [],
      supplierList: [],

      intExtOptions: [
        { label: 'Internal', keyValue: '0' },
        { label: 'External', keyValue: '1' },
      ],
      availabilityOptions: [
        { label: 'Available', keyValue: '1' },
        { label: 'Unavailable', keyValue: '0' },
      ],

      repairTypeOptions: [
        { label: 'Preventitive', keyValue: 'P' },
        { label: 'Repair', keyValue: 'R' },
      ],

      //#endregion

      //#region Document Tab
      docTableData: [],
      docTableSelectedRows: [],
      docTableDataType: {
        '#': 'number',
        'Ref No': 'string',
        Type: 'string',
        Code: 'string',
        Date: 'datetime',
        'Date Received': 'datetime',
        'Pic/Doc': 'string',
        Remark: 'string',
      },
      docSelectedItem: null,

      docFields: {
        docReferenceNoValue: '',
        docDocTypeValue: null,
        docDocDateValue: null,
        docDateReceivedValue: null,
        docDocCodeValue: null,
        docPicDocValue: null,
        docPicDocType: '',
        docPicDocName: '',
        docRemarkValue: '',
      },
      docFieldsChangedCheck: {},

      docReferenceNoError: '',
      docDocTypeError: '',
      docDocDateError: '',
      docDateReceivedError: '',
      docDocCodeError: '',

      docTypeList: [],
      docCodeList: [],
      //#endregion

      //#region Task Tab
      taskTableData: [],
      taskTableSelectedRows: [],
      taskTableDataType: {
        '#': 'number',
        'Service Code': 'string',
        'Service Description': 'string',
        'Task Code': 'string',
        'Task Description': 'string',
        'Service Person': 'string',
        'Date Start': 'datetime',
        'Date End': 'datetime',
        'Date Created': 'datetime',
        'Date Updated': 'datetime',
        'After Approval': 'string',
        Remark: 'string',
      },
      taskSelectedItem: null,

      taskFields: {
        taskServiceValue: null,
        taskServiceTaskValue: null,
        taskServicePersonValue: '',
        taskDateTimeStartValue: null,
        taskDateTimeEndValue: null,
        taskRemarkValue: '',
      },
      taskFieldsChangedCheck: {},

      taskServiceError: '',
      taskServiceTaskError: '',
      taskServiceType: '',
      taskServicesList: [],
      taskServicesTaskList: [],
      taskAddShow: false,
      taskAddGroupAction: 'N',
      taskAddOriginalRows: [],
      taskAddServiceValue: null,
      taskAddServiceError: '',
      taskAddServiceType: '',
      taskAddRows: [],
      taskAddSelectedRows: [],
      taskAddRowId: 0,
      taskAddEditorOpen: false,
      taskAddEditorAction: 'N',
      taskAddEditorRowId: null,
      taskAddTaskValue: null,
      taskAddTaskError: '',
      taskAddPersonValue: '',
      taskAddDateStartValue: null,
      taskAddDateEndValue: null,
      taskAddRemarkValue: '',
      maintenanceImportShow: false,
      maintenanceImportLoading: false,
      maintenanceDueRows: [],
      maintenanceImportRequestId: 0,

      //#endregion

      //#region Part Tab
      partTableData: [],
      partTableSelectedRows: [],
      partTableDataType: {
        '#': 'number',
        Type: 'string',
        'Serial No': 'string',
        Manufacturer: 'string',
        'Made In': 'string',
        Size: 'string',
        Quantity: 'number',
        'Unit Price': 'currency',
        'Cost (RM)': 'currency',
        Replace: 'string',
        Supplier: 'string',
        'Date Purchase': 'datetime',
        'Insurance Claim No': 'string',
        'Invoice No': 'string',
        'Taken From': 'string',
        Remark: 'string',
        PONo: 'string',
        UserID: 'string',
        'Date Created': 'datetime',
        'Date Updated': 'datetime',
        'After Approval': 'string',
      },
      partSelectedItem: null,

      partFields: {
        partTypeValue: null,
        partWarehouseItemValue: null,
        partWarehouseItemSavedValue: null,
        partWarehouseItemSearch: '',
        partSerialNoValue: null,
        partSerialNoInputValue: '',
        partPartNoValue: '',
        partSizeValue: '',
        partTakenFromValue: null,
        partTakenFromInputValue: '',
        partUnitPriceValue: '',
        partMFGDateValue: null,
        partDatePurchaseValue: null,
        partInvoiceNoValue: '',
        // partServiceCodeValue: null,
        partModelValue: '',
        partReplaceValue: '',
        partMadeInValue: null,
        partQuantityValue: '1',
        partManufacturerValue: null,
        partPurchaseFromValue: null,
        partPONoValue: '',
        partInsuranceNoValue: '',
        partRemarkValue: '',
      },
      partFieldsChangedCheck: {},

      partTypeError: '',
      partSerialNoError: '',
      partSerialNoChecking: false,
      partSerialNoCheckResult: null,
      partSerialNoSaving: false,
      partSerialNoCheckTimer: null,
      partSerialNoCheckRequestId: 0,
      partQuantityError: '',

      partTypeList: [],
      serialNoList: [],
      stockListsLoading: false,
      stockListsRequestId: 0,
      takenFromList: [],
      countryList: [],
      manufacturerList: [],
      ////supplier list declared in jobdetails

      serialNoListDataType: {
        'Serial No': 'string',
        Warehouse: 'string',
        'Qty Bal': 'number',
        Model: 'string',
        ID: 'string',
        'Part Desc': 'string',
        'Part Type': 'string',
        MadeIn: 'string',
        Manufacturer: 'string',
        ProdDate: 'datetime',
        PurcDate: 'datetime',
        NoOfRetread: 'number',
        PONo: 'string',
        'Part No': 'string',
        'Unit Price': 'currency',
        Spp_Code: 'string',
      },

      //#endregion

      //#region Tyre Tab
      tyreConfigLayoutSelectedItem: null,
      tyrePositionSelectedValue: null,

      tyreConfigTyresList: [],
      tyreConfigTableSelectedRow: [],
      tyreLayout: [],

      //#endregion

      //#region Tyre Config POPUP
      tyreConfigPopupShow: false,

      tyreConfigSelectedVehichleTyreConfig: [],
      tyreConfigSelectedVehichleTyreConfigFull: [],
      tyreConfigSelectedVehicleLayout: null,
      tyreConfigSelectedVehicleNo: '',
      tyreConfigRotateSelectedTyre: null,

      tyreConfigSelectedVehicleDropdownValue: null,

      tyreConfigUpdateList: [],

      tyreConfigUpdateListDataType: {
        '#': 'number',
        'New Serial No': 'string',
        'Old Serial No': 'string',
        'Tyre Position': 'string',
        'Replace Type': 'string',
        'Rotated With': 'string',
        'Store In': 'string',
        'Prev Tyre Status': 'string',
        Remarks: 'string',
        'Cost (RM)': 'currency',
      },

      tyreStatusList: [
        { label: 'Unavailable', keyValue: '0' },
        { label: 'Rotate', keyValue: '0' },
        { label: 'Send to warehouse', keyValue: '5' },
        { label: 'Send for retread', keyValue: '3' },
        { label: 'Discard', keyValue: '4' },
      ],

      tyreStatusValue: null,
      tyreStatusValueError: '',
      tyreStoreInValue: null,
      tyreStoreInValueError: '',

      tyreReplaceValue: null,
      tyreReplaceValueError: '',

      tyreExistingValue: null,
      tyreExistingValueError: '',

      tyreConfigOtherVehicleValue: null,
      tyreConfigOtherVehicleValueError: '',

      tyreConfigTyreTypeDisabled: false,

      tyreConfigTyreTypeValue: null,
      tyreConfigSerialNoValue: '',
      tyreConfigPartNoValue: '',
      tyreConfigSizeValue: '',
      tyreConfigTakenFromValue: null,
      tyreConfigTakenFromInputValue: '',
      tyreConfigUnitPriceValue: '',
      tyreConfigMfgDateValue: null,
      tyreConfigModelValue: '',
      tyreConfigMadeInValue: null,
      tyreConfigManufacturerValue: null,
      tyreConfigSupplierValue: null,
      tyreConfigPONoValue: null,
      tyreConfigDatePurchaseValue: null,

      tyreConfigTyreTypeValueError: '',
      tyreConfigSerialNoValueError: '',
      tyreConfigSerialNoChecking: false,
      tyreConfigSerialNoCheckResult: null,
      tyreConfigSerialNoCheckTimer: null,
      tyreConfigSerialNoCheckRequestId: 0,

      tyreConfigOldSerialNoValue: '',
      tyreConfigJobPriceValue: '',
      tyreConfigInvoiceNoValue: null,
      tyreConfigInsuranceNoValue: null,
      tyreConfigRemarksValue: '',

      tyreUpdateMode: '',

      warehouseList: [],
      jobPartTyreList: [],
      jobPartTyreSaveList: [],

      tyreConfigErrorText: '',
      tyreConfigPositionEditAllowed: false,
      tyreConfigPositionEditChecked: false,
      tyreConfigPositionEditMessage: '',
      //#endregion

      tyreRotateOtherVehicleWorkshopJobValue: null,
      tyreRotateOtherVehicleWorkshopJobInputValue: '',
      tyreRotateOtherVehicleWorkshopJobValueError: '',
      tyreRotateOtherVehicleWorkshopJobs: null,
      tyreConfigUpdateOtherVehicle: [],

      partUpdateOtherVehicle: [],

      disableAttachJob: false,

      updateTyrePopupOpen: false,

      //other stuff
      tabFieldsAction: '',
      SASControl: [],
      localCurrency: 'MYR',
    };
  },

  computed: {
    approvalStatusTone() {
      return ({ Pending: 'warn', Submitted: 'warn', Approved: 'ok', Rejected: 'bad' })[this.approvalStatus] || 'neutral';
    },
    canSendApproval() {
      return (
        Boolean(this.jobNoValue) &&
        this.approvalSetup?.Enabled === true &&
        this.approvalSetup?.Status === 'Configured' &&
        ['Edit', 'View'].includes(this.menuMode) &&
        ['', 'Not Submitted', 'Not Configured', 'Rejected', 'Approved'].includes(this.approvalStatus)
      );
    },
    workshopJobAdditionalButtons() {
      const pending = this.approvalSetup?.Enabled === true && this.approvalStatus === 'Pending';
      const approved = this.approvalSetup?.Enabled === true && this.approvalStatus === 'Approved';
      return this.viewMode && (this.canSendApproval || pending || approved) ? [{
        key: 'sendApproval',
        text: this.resubmitApproval ? 'Sending...' : pending ? 'Pending Approval' : approved || this.approvalStatus === 'Rejected' ? 'Re-send for approval' : 'Send for Approval',
        disabled: !this.canSendApproval || pending || this.resubmitApproval,
        icon: this.approvalStatus === 'Rejected' || (pending && this.approvalHistory.some((entry) => entry.Status === 'Rejected')) ? 'https://dsmlib.logistics.systems/Icons/pending.png' : undefined,
      }] : [];
    },

    approvalHistoryTableData() {
      return this.approvalHistory.map((entry, index) => ({
        '#': index + 1,
        Status: ['Submitted', 'Rejected', 'Approved'].includes(entry.Status) ?
          `<span class="status-chip status-chip--${({ Submitted: 'warn', Approved: 'ok', Rejected: 'bad' })[entry.Status]}">${escapeHtml(entry.Status)}</span>` : escapeHtml(entry.Status),
        User: escapeHtml(entry.UserID),
        Date: escapeHtml(this.approvalHistoryDate(entry.Date)),
        ['Amount (' + this.localCurrency + ')']: entry.Amount == null || String(entry.Amount).trim() === '' ? '' : Number(entry.Amount) || 0,
        Remark: escapeHtml(entry.Remark),
      }));
    },

    workshopJobsTableDisplayData() {
      const tones = { Pending: 'warn', Submitted: 'warn', Approved: 'ok', Rejected: 'bad' };
      return this.workshopJobsTableData.map((item) => {
        if (!Object.prototype.hasOwnProperty.call(item, 'Approval Status')) return { ...item };
        const status = String(item['Approval Status'] ?? '').trim();
        const tone = tones[status] || 'neutral';
        return { ...item, 'Approval Status': status ? `<span class="status-chip status-chip--${tone}">${escapeHtml(status)}</span>` : '' };
      });
    },

    hasCancelledJobs() {
      return this.workshopJobsTableData.some((item) => item?.Cancelled == 'Yes');
    },

    workshopJobsTableHideColumns() {
      const columns = ['TimeIn', 'TimeOut', 'IsLatest', 'canDelete', 'canDeleteReason', 'Vehicle #', 'Vehicle Code', 'Pending Level', 'Approved Total'];
      if (this.approvalSetup?.Enabled !== true) columns.push('Approval Status');
      if (!this.hasCancelledJobs) columns.push('Cancelled');
      return columns;
    },

    menu() {
      const menuEnabled = !this.workshopPageLoading;
      let menuItems = [
        {
          title: 'New Workshop Job',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-add-new-50.png',
          key: 'New',
          visible: true,
          enabled: menuEnabled,
          control: 'mnNew',
        },
        {
          title: 'Edit Workshop Job',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-edit-64%20(1).png',
          key: 'Edit',
          visible: true,
          enabled: menuEnabled,
          control: 'mnEdit',
        },
        {
          title: 'Cancel Workshop Job',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-reduce-50.png',
          key: 'Delete',
          visible: true,
          enabled: menuEnabled,
          control: 'mnDelete',
        },
        {
          title: 'View Workshop Job',
          icon: 'https://dsmapi.logistics.systems/dsm6gicons/icons8-view-64.png',
          key: 'View',
          visible: true,
          enabled: menuEnabled,
          control: 'mnQuery',
        },
        {
          title: 'Print Work Order',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          key: 'Print',
          visible: true,
          enabled: menuEnabled,
          control: 'printWorkOrder',
        },
        {
          title: 'Update Retread Tyres',
          icon: 'https://dsmlib.logistics.systems/Icons/Reverse.png',
          key: 'UpdateRetreadTyres',
          visible: true,
          enabled: menuEnabled,
          control: 'mnUpdateTyreStatus',
        },
      ];

      menuItems = menuItems.map((item) => {
        const { control } = item;
        const SAS = this.SASControl.find((sas) => sas.Control == control) || null;
        if (!SAS) return item;
        const { Properties, PropertiesValue } = SAS;
        return {
          ...item,
          [Properties.toLowerCase()]: PropertiesValue == 'True',
        };
      });

      return menuItems;
    },
    activeDateString() {
      const filters = [];
      if (this.dateFilterActive) {
        filters.push(
          `Date: ${new Date(this.activeStartDate).formatString('dd-MM-yyyy')} to ${new Date(this.activeEndDate).formatString('dd-MM-yyyy')}`,
        );
      }
      if (this.jobNoFilterActive && this.jobNoFilterValue) {
        filters.push(`Job # Contains: ${this.jobNoFilterValue}`);
      }
      if (this.vehicleFilterActive) {
        const vehicleFilter = this.activeVehicleFilterText;
        filters.push(`Vehicle${vehicleFilter || ': All'}`);
      }
      if (this.workshopFilterActive) filters.push(`Workshop: ${this.workshopFilterValue?.label || 'All'}`);
      if (this.showCancelledJobs) filters.push('Cancelled jobs included');
      return filters.length > 0 ? filters.join(' | ') : 'No Filter Active';
    },
    activeVehicleFilterText() {
      const filters = [];
      if (this.vehicleFilterValue) filters.push(`Vehicle: ${this.vehicleFilterValue.label}`);
      if (this.vehicleCategoryFilterValue) filters.push(`Vehicle Category: ${this.vehicleCategoryFilterValue.label}`);
      return filters.length > 0 ? ` (${filters.join(', ')})` : '';
    },

    tabItems() {
      return [
        { title: 'Job Details' },
        { title: this.docTabTitle, disabled: !this.jobInventoryValue },
        { title: this.taskTabTitle, disabled: !this.jobInventoryValue },
        { title: this.partTabTitle, disabled: !this.jobInventoryValue },
        { title: this.tyreTabTitle, disabled: !this.jobInventoryValue },
      ];
    },
    docTabTitle() {
      return `Documents${this.docTableDataFiltered.length > 0 ? ` (${this.docTableDataFiltered.length})` : ''}`;
    },
    taskTabTitle() {
      return `Tasks${this.taskTableDataFiltered.length > 0 ? ` (${this.taskTableDataFiltered.length})` : ''}`;
    },
    partTabTitle() {
      return `Parts / Labour${this.partTableDataFiltered.length > 0 ? ` (${this.partTableDataFiltered.length})` : ''}`;
    },
    tyreTabTitle() {
      const length = this.tyreConfigUpdateListFiltered?.length ?? 0;
      return `Tyres${length > 0 ? ` (${length})` : ''}`;
    },
    popUpTitle() {
      return `${this.menuMode} Workshop Job ${this.menuMode != 'New' && this.jobNoValue ? `- ${this.jobNoValue}` : ''}`;
    },
    availabilityEnabled() {
      return this.isLatestJob;
    },
    trailerList() {
      return this.vehicleList.filter((item) => String(item['Vehicle Category'] ?? '').toUpperCase().includes('TR'));
    },
    serialNoListFiltered() {
      const { partTypeValue } = this.partFields || {};
      if (!partTypeValue) return [];

      return this.serialNoList?.filter(
        (item) =>
          (item.PartType == partTypeValue['Part Type'] &&
            !this.partTableDataFiltered.some((part) => part['Serial No'] == item['Serial No'])) ||
          item['Serial No'] == this.partSelectedItem?.['Serial No'],
      );
    },
    serialNoRequired() {
      const { 'Unique Code': uniqueCode } = this.partFields.partTypeValue || {};
      return uniqueCode == 'Y';
    },
    takenFromFreeText() {
      const { partTakenFromInputValue: partTakenFromInputValueSaved } = this.partFieldsChangedCheck || {};
      const { partTakenFromInputValue, partSerialNoValue } = this.partFields || {};
      const { Warehouse } = partSerialNoValue || {};
      const fromPopulate = partTakenFromInputValue == partTakenFromInputValueSaved;
      const fromSerial = Warehouse && Warehouse == partTakenFromInputValue;
      return fromPopulate || fromSerial;
      //enable free text to prevent 'Taken From' from locking out other actions
      //enable free text if taken from is gottne from Serial No
    },
    takenFromListFiltered() {
      const { partTypeValue } = this.partFields || {};
      const { ProdTxnID: PartFieldsProdTxnID, 'Part Type': PartsFieldsPartType } = partTypeValue || {};
      if (!PartFieldsProdTxnID && !PartsFieldsPartType) return [];
      const selectedWarehouseItems = [
        this.partFields.partWarehouseItemValue,
        this.partFields.partWarehouseItemSavedValue,
      ].filter(Boolean);
      const list = this.takenFromList.filter((item) => {
        const validateTxn = Boolean(PartFieldsProdTxnID) && item.ProdTxnID == PartFieldsProdTxnID;
        const validatePartType = Boolean(PartsFieldsPartType) && item['Part Type'] == PartsFieldsPartType;
        const validateType = validateTxn || validatePartType;
        const isSelectedWarehouseItem = selectedWarehouseItems.some((part) =>
          this.warehouseItemMatchesPart(item, part),
        );
        return isSelectedWarehouseItem || (validateType && item['Quantity Balance'] > 0);
      });
      return list;
    },
    partWarehouseItemSelected() {
      return Boolean(this.partFields.partWarehouseItemValue);
    },
    partQuantityMaximum() {
      const selectedItem = this.partFields.partWarehouseItemValue;
      if (
        !selectedItem ||
        selectedItem['Quantity Balance'] === null ||
        selectedItem['Quantity Balance'] === undefined ||
        selectedItem['Quantity Balance'] === ''
      ) {
        return null;
      }
      return this.warehouseItemEffectiveQuantityBalance(selectedItem);
    },
    serialNoLookup() {
      const lookup = Object.create(null);
      this.serialNoList.forEach((item) => {
        const serialNo = String(item?.['Serial No'] ?? '');
        if (serialNo && !lookup[serialNo]) lookup[serialNo] = item;
      });
      return lookup;
    },
    warehouseItemListFiltered() {
      const list = this.mergeWarehouseItemRows(this.takenFromListFiltered.map((item) => {
        const serialItem = this.serialNoLookup[String(item['Serial No'] ?? '')] || {};
        const unitPrice = item['Unit Price'] ?? serialItem.UnitPrice;
        const purchaseDate = item['Date In'] || serialItem.PurcDate || '';
        return {
          ...item,
          warehouseItemRawQuantityBalance: item['Quantity Balance'],
          'Quantity Balance': item['Quantity Balance'],
          'Unit Price': unitPrice === null || unitPrice === undefined || unitPrice === '' ? '' : this.$currencyFormat(this.parsePartAmount(unitPrice)),
          Manufacturer: item.Manufacturer || serialItem.Manufacturer || '',
          Model: item['Model No'] || serialItem.Model || '',
          'Date Purchase': purchaseDate ? new Date(purchaseDate).formatString('yyyy-MM-dd') : '',
          'Manufacture Date': item.ProdDate || serialItem.ProdDate || '',
          label: this.warehouseItemPrimaryLabel(item),
        };
      })).map((item) => ({
        ...item,
        'Quantity Balance': this.warehouseItemEffectiveQuantityBalance(item),
      }));
      const savedPart = this.partFields.partWarehouseItemSavedValue;
      if (savedPart && !list.some((item) => this.warehouseItemMatchesPart(item, savedPart))) {
        const savedWarehouseItem = this.warehouseItemFromSavedPart(savedPart);
        savedWarehouseItem['Quantity Balance'] = this.warehouseItemEffectiveQuantityBalance(savedWarehouseItem);
        list.push(savedWarehouseItem);
      }
      if (this.partWarehouseItemSelected) return list;

      const warehouse = String(
        this.partFields.partTakenFromValue?.Warehouse ||
          this.partFields.partTakenFromValue?.['Warehouse Code'] ||
          this.partFields.partTakenFromInputValue ||
          '',
      ).trim();
      const serialNo = String(this.partFields.partSerialNoInputValue || '').trim();
      if (!warehouse && !serialNo) return list;

      return list.filter((item) => {
        const warehouseMatches = !warehouse || String(item.Warehouse ?? '').trim() == warehouse;
        const serialMatches = !serialNo || String(item['Serial No'] ?? '').trim() == serialNo;
        return warehouseMatches && serialMatches;
      });
    },
    warehouseItemSearchKeys() {
      return [
        'Warehouse',
        'Serial No',
        'Part No',
        'Model',
        'Model No',
        'Manufacturer',
        'MadeIn',
        'Supplier',
        'Unit Price',
        'Quantity Balance',
        'PONo',
        'InvoiceNo',
        'BinLoc',
        'Date In',
        'ProdDate',
        'Date Purchase',
        'Manufacture Date',
      ];
    },
    warehouseItemSecondaryFields() {
      return [
        { key: 'Date Purchase', label: 'Date Purchase' },
        { key: 'Part No', label: 'Part No' },
        { key: 'Manufacture Date', label: 'Manufacture Date' },
        { key: 'InvoiceNo', label: 'Invoice No' },
        { key: 'Model', label: 'Model' },
        { key: 'MadeIn', label: 'Made In' },
        { key: 'Manufacturer', label: 'Manufacturer' },
        { key: 'Supplier', label: 'Supplier' },
        { key: 'PONo', label: 'PO No.' },
        { key: 'BinLoc', label: 'Bin Location' },
      ];
    },
    warehouseItemInlineSecondaryFields() {
      return [
        { key: 'Unit Price', label: 'Unit Price' },
        { key: 'Quantity Balance', label: 'Quantity' },
      ];
    },
    tableAdditionalButton() {
      if (this.viewMode) return [{ text: 'View', index: 0, width: 60 }];
      return [{ text: 'Edit', index: 0, width: 60 }];
    },
    taskTableAdditionalButton() {
      if (this.viewMode) return [{ text: 'View', index: 0, width: 60 }];
      return [];
    },
    taskAddTableButtons() {
      return [{ text: 'Edit', index: 0, width: 60 }];
    },
    maintenanceImportTableButtons() {
      return [{ text: 'Select', index: 0, width: 75 }];
    },
    partTypeListNoTyres() {
      const filterTyres = this.partTypeList.filter((item) => {
        return item.Active == '1' && !item?.['Part Group']?.toLowerCase().includes('tyre');
      });

      const selectedType = this.partFields.partTypeValue;
      if (
        selectedType &&
        !filterTyres.some((item) => item['Part Type'] == selectedType['Part Type'])
      ) {
        return [selectedType, ...filterTyres];
      }

      return filterTyres;
    },
    tyreTypeList() {
      const filterTyres = this.partTypeList.filter((item) => {
        return item?.['Part Group']?.toLowerCase().includes('tyre');
      });

      return filterTyres;
    },
    tyreTypeListActive() {
      const list = this.tyreTypeList.filter((item) => item.Active == '1');
      const selectedType = this.tyreConfigTyreTypeValue;
      if (selectedType && !list.some((item) => item['Part Type'] == selectedType['Part Type'])) {
        return [selectedType, ...list];
      }
      return list;
    },

    partTyreMode() {
      return this.partFields.partTypeValue?.['Part Group']?.toLowerCase().includes('tyre');
    },
    partTotalCostValue() {
      const quantity = this.parsePartAmount(this.partFields.partQuantityValue) || 1;
      const unitPrice = this.parsePartAmount(this.partFields.partUnitPriceValue);
      return this.$currencyFormat(unitPrice * quantity);
    },
    totalJobPriceValue() {
      // Expense/warehouse joins can repeat a saved PartID; each job part contributes once.
      const seen = new Set();
      const parts = this.partTableData.filter((part) => {
        if (part.action == 'D') return false;
        const id = String(part.PartID || '').trim();
        if (id && seen.has(id)) return false;
        if (id) seen.add(id);
        return true;
      });
      let total = parts.reduce((sum, part) => sum + Math.round(this.parsePartAmount(part['Cost (RM)']) * 10000), 0);
      if (this.partSelectedItem && !this.viewMode) {
        const savedCost = this.tabFieldsAction == 'N' ? 0 : this.parsePartAmount(this.partSelectedItem['Cost (RM)']);
        const rawQuantity = String(this.partFields.partQuantityValue == null ? '' : this.partFields.partQuantityValue).trim();
        const editedQuantity = rawQuantity == '' ? 1 : this.parsePartAmount(rawQuantity);
        const editedCost = this.parsePartAmount(this.partFields.partUnitPriceValue) * editedQuantity;
        total += Math.round(editedCost * 10000) - Math.round(savedCost * 10000);
      } else if (this.tyreConfigPopupShow && !this.viewMode) {
        const seqno = (this.tyreConfigTableSelectedRow[0] || {}).seqno;
        const part = parts.find((row) => seqno && row.tyreSeqNo == seqno);
        const type = (this.tyreReplaceValue || {}).keyValue;
        const editedCost = type == 'ROTATE' || type == 'OTHER' ? 0 :
          this.parsePartAmount(this.tyreConfigJobPriceValue) * (part ? this.parsePartAmount(part.Quantity) || 1 : 1);
        total += Math.round(editedCost * 10000) - Math.round(this.parsePartAmount(part && part['Cost (RM)']) * 10000);
      }
      return this.$currencyFormat(total / 10000);
    },
    docTableDataFiltered() {
      const table = this.reorderTableNo(this.docTableData.filter((doc) => doc.action != 'D'));
      return table;
    },
    taskTableDataFiltered() {
      const names = new Map();
      const numbers = new Map();
      const tasks = this.taskTableData
        .filter((task) => task.action != 'D')
        .sort((a, b) => Number(a.seqNo) - Number(b.seqNo));
      return tasks.map((task) => {
        const code = cleanText(task['Service Code']);
        if (!names.has(code)) names.set(code, `Service: ${code} - ${task['Service Description'] || ''}`);
        const number = (numbers.get(code) || 0) + 1;
        numbers.set(code, number);
        return { ...task, '#': number, _groupBy: names.get(code),
          'After Approval': this.changedAfterApproval(task) };
      });
    },
    taskAddTableRows() {
      return this.reorderTableNo(this.taskAddRows);
    },
    taskServiceIsTask() {
      return this.taskServiceType == '2';
    },
    vehicleMaintenanceGroups() {
      const services = this.selectedVehicleInfo?.Service;
      const rows = Array.isArray(services) ? services : services ? [services] : [];
      const groups = [];
      const byKey = new Map();
      rows.forEach((row) => {
        const serviceCode = cleanText(row.ServiceCode);
        const taskCode = cleanText(row['Service Task']);
        if (!serviceCode || !taskCode) return;
        const key = JSON.stringify([serviceCode, row.Interval, row.Mileage, row.Duration, row.Occurence, row.ExpireDate]);
        let group = byKey.get(key);
        if (!group) {
          const interval = row.Interval == '0' ? `Every ${row.Mileage} KM` :
            row.Interval == '1' ? `Every ${row.Duration} Month(s)` :
            row.Interval == '2' ? `Every ${row.Duration} Day(s)` :
            row.Interval == '3' ? `Every ${row.Mileage} KM or ${row.Duration} Month(s)` : '';
          const occurrence = row.Occurence == '1' ? `Routine until ${row.ExpireDate}` : 'Once Only';
          group = {
            key,
            label: `${serviceCode} - ${row['Code Description']} | ${interval} | ${occurrence}`,
            tasks: [],
          };
          byKey.set(key, group);
          groups.push(group);
        }
        group.tasks.push(row);
      });
      return groups;
    },
    maintenanceImportTableData() {
      const sameDate = (a, b) => String(a || '').slice(0, 10) == String(b || '').slice(0, 10);
      const getDueRows = (group) => {
        const service = group.tasks[0];
        return this.maintenanceDueRows.filter((row) => {
          if (cleanText(row.ServiceCode) != cleanText(service.ServiceCode)) return false;
          if (row.SeqNo != null && group.tasks.every((task) => task.SeqNo != null)) {
            return group.tasks.some((task) => cleanText(task.SeqNo) == cleanText(row.SeqNo));
          }
          return String(row.SerVariant) == String(service.Interval) &&
            Number(row.Mileage || 0) == Number(service.Mileage || 0) &&
            Number(row.Duration || 0) == Number(service.Duration || 0) &&
            String(row.Repetitive) == String(service.Occurence) &&
            sameDate(row.ExpireDate, service.ExpireDate) &&
            group.tasks.some((task) => cleanText(task['Service Task']) == cleanText(row.Task));
        });
      };
      const importableGroups = this.vehicleMaintenanceGroups.filter((group) => {
        const service = group.tasks[0];
        if (String(service.Occurence) != '0') return true;
        const completedTasks = new Set(getDueRows(group)
          .filter((row) => cleanText(row.JobNo))
          .map((row) => cleanText(row.Task)));
        return !group.tasks.every((task) => completedTasks.has(cleanText(task['Service Task'])));
      });

      return importableGroups.map((group, index) => {
        const service = group.tasks[0];
        const dueRows = getDueRows(group);
        const lastService = dueRows.reduce((latest, row) => {
          if (!cleanText(row.JobNo)) return latest;
          if (!latest) return row;
          const rowDate = new Date(row.LastServiceDate || row.JobDate || 0).getTime() || 0;
          const latestDate = new Date(latest.LastServiceDate || latest.JobDate || 0).getTime() || 0;
          return rowDate > latestDate ||
            (rowDate == latestDate && cleanText(row.JobNo) > cleanText(latest.JobNo)) ? row : latest;
        }, null);
        const lastServiceDate = lastService?.LastServiceDate || lastService?.JobDate ?
          new Date(lastService.LastServiceDate || lastService.JobDate) : null;
        const variant = String(service.Interval);
        const currentMileage = Number(this.selectedVehicleInfo?.MeterReading) || 0;
        const dueItems = dueRows.map((row) => {
          const nextMileage = service.Mileage != null && ['0', '3'].includes(variant) ?
            Number(row.LastJobMileage || 0) + Number(service.Mileage) : '';
          const start = row.LastServiceDate || row.JobDate || row.DateCreated || row.DatePurchase;
          const nextDate = start && service.Duration ? new Date(start) : null;
          if (nextDate && !isNaN(nextDate.getTime())) {
            if (variant == '1' || variant == '3') nextDate.setMonth(nextDate.getMonth() + Number(service.Duration));
            else if (variant == '2') nextDate.setDate(nextDate.getDate() + Number(service.Duration));
          }
          const dueDate = nextDate && !isNaN(nextDate.getTime()) && ['1', '2', '3'].includes(variant) ? nextDate : null;
          const completed = row.JobNo && row.Repetitive == '0';
          const overdue = !completed && ((nextMileage !== '' && currentMileage >= nextMileage) || (dueDate && new Date() >= dueDate));
          const upcoming = !completed && dueDate && (dueDate - new Date()) / 86400000 <= 1;
          return { nextMileage, dueDate, status: overdue ? 2 : upcoming ? 1 : 0 };
        });
        const mostUrgent = dueItems.reduce((current, item) => !current || item.status > current.status ? item : current, null);
        const variantText = {
          '0': 'Mileage',
          '1': 'Months',
          '2': 'Days',
          '3': 'Which Comes First',
        }[variant] || '';
        const durationText = variant == '1' || variant == '3' ? 'Months' : variant == '2' ? 'Days' : '';
        return {
          _rowid: `maintenance-${index}`,
          key: group.key,
          '#': index + 1,
          'Service / Task': `[${service.ServiceCode}] ${service['Code Description'] || ''}`,
          Remarks: '',
          Due: mostUrgent ? (mostUrgent.status ? 'Yes' : 'No') : '',
          'Current Mileage': this.selectedVehicleInfo?.MeterReading ?? '',
          'Mileage Interval': service.Mileage ?? '',
          'Next Service Mileage': mostUrgent ? mostUrgent.nextMileage : '',
          'Date Interval': durationText && service.Duration ? `${service.Duration} ${durationText}` : '',
          'Next Service Date': mostUrgent?.dueDate ? mostUrgent.dueDate.formatString('yyyy-MM-dd') : '',
          Interval: variantText,
          Repeat: service.Occurence == '1' ? 'Routine' : 'Once Only',
          'Expiry Date': service.ExpireDate ? new Date(service.ExpireDate).formatString('yyyy-MM-dd') : '',
          Tasks: group.tasks.map((task) => `${task['Service Task']} - ${task['Task Description']}`).join(', '),
          'Job No': cleanText(lastService?.JobNo) || '',
          'Job Date': lastServiceDate && !isNaN(lastServiceDate.getTime()) ?
            lastServiceDate.formatString('yyyy-MM-dd') : '',
        };
      });
    },
    maintenanceImportTableDataType() {
      return {
        '#': 'number',
        'Service / Task': 'string',
        Remarks: 'string',
        Due: 'string',
        'Current Mileage': 'number',
        'Mileage Interval': 'number',
        'Next Service Mileage': 'number',
        'Next Service Date': 'date',
        'Date Interval': 'string',
        Interval: 'string',
        Repeat: 'string',
        'Expiry Date': 'date',
        Tasks: 'string',
        'Job No': 'string',
        'Job Date': 'date',
      };
    },
    partTableDataFiltered() {
      const tableLimitedColumns = this.partTableData
        .filter((part) => part.action != 'D')
        .map((part) => {
          const {
            Type,
            'Serial No': SerialNo,
            'Part No': PartNo,
            Manufacturer,
            'Made In': MadeIn,
            Size,
            Quantity,
            'Cost (RM)': Cost,
            Replace,
            'Purchase From': PurchaseFrom,
            'Insurance Claim No': InsNo,
            'Invoice No': InvNo,
            'Taken From': TakenFrom,
            Remark,
            PONo,
            UserID,
            'Date Created': DateCreated,
            'Date Updated': DateUpdated,
            seqNo,
          } = part || {};

          const update = this.tyreConfigUpdateList.find(
            (tyre) =>
              cleanText(tyre.serialno) == cleanText(part['Serial No']) &&
              cleanText(tyre.tyreindex) == cleanText(part.TyreIndex),
          );
          const partType = this.partTypeList.find((item) => item['Part Type'] == Type) || {};
          const { Description: PartDesc } = partType;
          const quantityValue = this.parsePartAmount(Quantity) || 1;
          const totalCostValue = this.parsePartAmount(Cost);
          const unitPriceValue = totalCostValue / quantityValue;

          return {
            Type: `${Type}${PartDesc ? ` - ${PartDesc}` : ''}`,
            'Serial No': SerialNo,
            'Part No': PartNo,
            Manufacturer,
            'Made In': MadeIn,
            Size,
            Quantity: quantityValue,
            'Unit Price': unitPriceValue,
            'Cost (RM)': totalCostValue,
            Replace,
            Supplier: PurchaseFrom,
            'Insurance Claim No': InsNo,
            'Invoice No': InvNo,
            'Taken From': TakenFrom,
            Remark,
            PONo,
            UserID,
            'Date Created': DateCreated,
            'Date Updated': DateUpdated,
            'After Approval': this.changedAfterApproval(part),
            seqNo,
            tyreSeqNo: update ? update.seqno : null,
          };
        })
        .filter((part) => !part.tyreSeqNo);
      return this.reorderTableNo(tableLimitedColumns);
    },

    fieldsChanged() {
      if (this.viewMode) return false;
      let current = {};
      let saved = {};
      if (this.docSelectedItem) {
        current = this.docFields;
        saved = this.docFieldsChangedCheck;
      }
      if (this.taskSelectedItem) {
        current = this.taskFields;
        saved = this.taskFieldsChangedCheck;
      }
      if (this.partSelectedItem) {
        current = this.partFields;
        saved = this.partFieldsChangedCheck;
      }
      //the order doesnt really matter
      current = JSON.stringify(current);
      saved = JSON.stringify(saved);

      return current != saved;
    },

    tyreConfigButtonText() {
      if (this.viewMode) return 'View Tyre Change';
      if (this.tyreConfigTableSelectedRow.length > 0)
        return this.tyreChangeAllowed ? 'Edit Tyre Change' : 'View Tyre Change';
      if (!this.tyreChangeAllowed) return 'View Tyre Change';
      return 'New Tyre Change';
    },

    tyreChangeAllowed() {
      return this.isLatestTyreJob;
    },

    tyreConfigCanEditJobPartInfo() {
      return !this.viewMode && this.tyreConfigTableSelectedRow.length > 0;
    },

    tyreConfigSelectedSavedTyreChange() {
      const selected = this.tyreConfigTableSelectedRow[0];
      return selected && selected.action != 'N';
    },

    tyreConfigCanEditTyreChange() {
      if (this.viewMode) return false;
      if (this.tyreConfigSelectedSavedTyreChange)
        return this.tyreChangeAllowed && this.tyreConfigPositionEditAllowed;
      return this.tyreChangeAllowed;
    },

    tyreConfigCanSavePopup() {
      return this.tyreConfigCanEditTyreChange || this.tyreConfigCanEditJobPartInfo;
    },

    tyreConfigPartInfoOnlyEdit() {
      return this.tyreConfigCanEditJobPartInfo && !this.tyreConfigCanEditTyreChange;
    },

    tyreConfigEditWarningText() {
      if (!this.tyreConfigPartInfoOnlyEdit) return '';
      if (this.tyreConfigPositionEditMessage) return this.tyreConfigPositionEditMessage;
      return 'Tyre change cannot be edited because future changes may exist for this position. Only Job Part Info is allowed for editing.';
    },

    tyreStatusOptions() {
      if (this.tyreConfigOldSerialNoValue) return this.tyreStatusList;
      return [{ label: 'None', keyValue: '0' }];
    },

    tyrePopupSelectedPosition() {
      if (this.tyreConfigTableSelectedRow[0]) {
        return `${this.tyreConfigTableSelectedRow[0]['Tyre Position']}${this.tyreConfigTableSelectedRow[0]['Old Serial No'] ? ` - ${this.tyreConfigTableSelectedRow[0]['Old Serial No']}` : ''}`;
      }
      if (this.tyrePositionSelectedValue) return this.tyrePositionSelectedValue.label;
      return '';
    },

    tyreNewList() {
      const { 'New Serial No': serialno } = this.tyreConfigTableSelectedRow?.[0] || {};
      const list = this.jobPartTyreList.filter((item) => {
        return (
          (!this.tyreConfigUpdateListFiltered.some((tyre) => tyre['New Serial No'] == item['Serial No']) &&
            item.tyreindex != this.tyreConfigLayoutSelectedItem?.index &&
            item['Qty Bal'] > 0) ||
          (cleanText(item['Serial No']) == cleanText(serialno) && this.tyreReplaceValue?.keyValue == 'WHSE')
        );
      });
      const selectedRow = this.tyreConfigTableSelectedRow?.[0];
      const savedPart = this.partTableData.find((part) => part.tyreSeqNo == selectedRow?.seqno);
      if (selectedRow?.seqno && serialno && savedPart && this.tyreReplaceValue?.keyValue == 'WHSE' &&
        !list.some((item) => this.warehouseItemMatchesPart(item, savedPart))) {
        const savedItem = this.warehouseItemFromSavedPart(savedPart);
        list.push({
          ...savedItem,
          'Serial No': cleanText(serialno),
          ID: savedItem.ProdTxnID,
          'Qty Bal': this.parsePartAmount(savedPart.Quantity),
          UnitPrice: this.parsePartAmount(savedItem['Unit Price']),
          PurcDate: savedItem['Date In'],
        });
      }
      return list;
    },

    tyreConfigLayout() {
      let returnLayout = [...this.tyreLayout];
      const canViewCurrentTyres = this.tyreChangeAllowed;

      // 1) Check latest job usage
      returnLayout = returnLayout.map((layout) => {
        const found =
          canViewCurrentTyres ?
            this.tyreConfigTyresList.findLast(
              (item) =>
                layout.index == item.tyreindex && (item.inuse == '1' || item.inuse == '2') && item.action != 'D',
            )
          : null;

        return {
          ...layout,
          state: found ? 'saved' : 'normal',
          tyreInfo: found,
        };
      });

      // Helper to avoid repeating findLast logic
      const findUpdateForIndex = (index, checkFn) =>
        this.tyreConfigUpdateList?.findLast((u) => u.tyreindex == index && checkFn(u));

      // 2) Check reverted previous update
      returnLayout = returnLayout.map((tyre) => {
        const found = findUpdateForIndex(tyre.index, (u) => u.prevupdate);
        return found ? { ...tyre, state: 'saved', tyreInfo: found } : tyre;
      });

      // 3) Check revert flag
      returnLayout = returnLayout.map((tyre) => {
        const found = findUpdateForIndex(tyre.index, (u) => u.revert);
        return found ? { ...tyre, state: 'saved', tyreInfo: found } : tyre;
      });

      // 4) Check normal update (no revert, no prevupdate)
      returnLayout = returnLayout.map((tyre) => {
        const found = findUpdateForIndex(tyre.index, (u) => !u.revert && !u.prevupdate && u.action != 'D');

        if (!found) return tyre;

        const prevTyre = this.tyreConfigTyresList.findLast((item) => item.serialno == found.oldserialno);

        return {
          ...tyre,
          state: 'changes',
          tyreInfo: prevTyre ? { ...prevTyre, inuse: '1' } : tyre.tyreInfo,
        };
      });

      // 5) Add label
      return returnLayout.map((item) => ({
        ...item,
        label: `${item.description}${item.tyreInfo?.serialno ? ` - ${item.tyreInfo.serialno}` : ''}`,
      }));
    },

    tyreConfigSelectedVehicleDropdownList() {
      const { seqno } = this.tyreConfigTableSelectedRow?.[0] || {};
      return this.tyreConfigSelectedVehicleLayout?.filter(
        (item) => item.state == 'saved' && item.tyreInfo?.seqno != seqno,
      );
    },

    tyreConfigUpdateListFiltered() {
      const newItem = this.tyreConfigUpdateList.filter(
        (tyreConfig) =>
          (!this.tyreConfigTyresList.some((tyre) => tyre.seqno == tyreConfig.seqno) ||
            cleanText(tyreConfig.jobno) == cleanText(this.jobNoValue)) &&
          tyreConfig.action != 'D' &&
          !tyreConfig.revert,
      );

      const isRotate = (item) => cleanText(item?.type) == 'ROTATE';

      // Check for mirrored ROTATE
      let filteredList = newItem.map((item) => {
        if (isRotate(item)) {
          const isSecondOfPair = newItem.some(
            (other) =>
              other != item &&
              isRotate(other) &&
              cleanText(other.serialno) == cleanText(item.oldserialno) &&
              cleanText(other.oldserialno) == cleanText(item.serialno) &&
              Number(other.seqno) < Number(item.seqno),
          );
          // skip if this is the second item (i.e., found a mirrored ROTATE)
          if (isSecondOfPair) {
            return {
              ...item,
              isSecondPair: true,
            };
          }
        }
        return item;
      });
      // Add rotatedWithPosition
      filteredList = filteredList.map((item) => {
        if (isRotate(item)) {
          const findPair = filteredList.find(
            (pair) =>
              isRotate(pair) &&
              pair.isSecondPair &&
              cleanText(pair.serialno) == cleanText(item.oldserialno) &&
              cleanText(pair.oldserialno) == cleanText(item.serialno) &&
              Number(pair.seqno) > Number(item.seqno),
          );
          if (findPair) {
            return {
              ...item,
              rotatedWithPosition: findPair.tyreposition,
            };
          }
        }
        return item;
      });
      // Remove mirrored ROTATE
      filteredList = filteredList.filter((item) => !item.isSecondPair);
      // Add storein
      filteredList = filteredList.map((item) => {
        if (item.storein) return item;
        const findPrev = this.tyreConfigTyresList?.findLast(
          (tyre) =>
            cleanText(tyre.serialno) == cleanText(item.oldserialno) &&
            cleanText(tyre.tyreindex) == cleanText(item.tyreindex) &&
            tyre.seqno <= item.seqno,
        );
        const { whsecode, inuse } = findPrev || {};
        if (inuse == '5') {
          return {
            ...item,
            storein: whsecode,
          };
        }
        return item;
      });

      filteredList = filteredList.map((item) => {
        let prevStatus = '';
        const type = cleanText(item.type);
        if (!item.oldserialno) prevStatus = 'None';
        else if (type == 'ROTATE') prevStatus = 'Rotated';
        else if (type == 'OTHER') prevStatus = 'Rotated (Other Vehicle)';
        else {
          const findPrev = this.tyreConfigTyresList?.findLast(
            (tyre) =>
              cleanText(tyre.serialno) == cleanText(item.oldserialno) &&
              cleanText(tyre.tyreindex) == cleanText(item.tyreindex) &&
              parseInt(tyre.seqno) < parseInt(item.seqno),
          );

          const { inuse } = findPrev || {};
          const { label: statusName } =
            this.tyreStatusList.find((status) => status.keyValue == String(inuse ?? '')) || {};
          prevStatus = statusName;
        }
        return {
          ...item,
          prevStatus: prevStatus,
        };
      });

      const list = filteredList.map((item) => {
        const findPart = this.partTableData.find((part) => part.tyreSeqNo == item.seqno) ?? {};
        const type = cleanText(item.type);
        const otherInventoryNo = cleanText(item.otherinventoryno);
        const otherJobNo = cleanText(item.otherjobno);
        const hasOtherVehicle = type == 'OTHER' && otherInventoryNo;
        const rotatedWith = hasOtherVehicle ?
            `${otherInventoryNo} (${otherJobNo || 'New Job'})`
          : isRotate(item) ? (item.rotatedWithPosition ?? '') : '';
        return {
          'New Serial No': item.serialno ?? '',
          'Old Serial No': item.oldserialno ?? '',
          'Tyre Position': item.tyreposition ?? '',
          'Replace Type': type ?? '',
          'Rotated With': rotatedWith,
          'Store In': item.storein ?? '',
          'Prev Tyre Status': item.prevStatus ?? '',
          'Date Changed': item.datechange ?? '',
          Remarks: findPart.Remark ?? '',
          'Cost (RM)': findPart['Cost (RM)'] ?? 0,
          seqno: item.seqno,
          tyreindex: item.tyreindex,
          otherjobno: otherJobNo,
          rotatedwith: hasOtherVehicle ? otherInventoryNo : isRotate(item) ? (item.rotatedWithPosition ?? '') : '',
          action: item.action,
        };
      });
      return this.reorderTableNo(list.sort((a, b) => (a.seqno ?? 0) - (b.seqno ?? 0)));
    },

    tyreReplaceRadio() {
      return [
        {
          label: 'New Tyre',
          keyValue: 'NEW',
          isDisabled: this.tyreStatusValue?.label == 'Rotate' || !this.tyreStatusValue,
        },
        {
          label: 'Tyre From Warehouse',
          keyValue: 'WHSE',
          isDisabled: this.tyreStatusValue?.label == 'Rotate' || !this.tyreStatusValue,
        },
        {
          label: 'Tyre From Current Vehicle',
          keyValue: 'ROTATE',
          isDisabled: this.tyreStatusValue?.label != 'Rotate' || !this.tyreStatusValue,
        },
        {
          label: 'Tyre From Other Vehicle',
          keyValue: 'OTHER',
          isDisabled: this.tyreStatusValue?.label != 'Rotate' || !this.tyreStatusValue,
        },
      ];
    },

    tyreRotateOtherVehicleWorkshopJobList() {
      const workshopJobs =
        this.tyreRotateOtherVehicleWorkshopJobs === null ?
          this.workshopJobsTableData
        : this.tyreRotateOtherVehicleWorkshopJobs;

      return [
        ...workshopJobs
          .filter(
            (item) =>
              cleanText(item['Job #']) != cleanText(this.jobNoValue) &&
              cleanText(item['Vehicle #']) == cleanText(this.tyreConfigOtherVehicleValue?.['Inventory #']) &&
              String(item.IsLatest).toLowerCase() == 'true',
          )
          .map((item) => ({ ...item, label: `Link To Latest ${item['Job #']} (${item['Job Date']})` })),
        { label: 'Create New Workshop Job', 'Job #': '' },
      ];
    },

    tyreConfigTyreInfoDisabled() {
      if (!this.tyreConfigCanEditTyreChange) return true;
      if (this.tyreReplaceValue?.keyValue != 'NEW') return true;
      return false;
    },

    tyreJobPartInfoVisible() {
      const replaceType = this.tyreReplaceValue?.keyValue;
      return replaceType != 'ROTATE' && replaceType != 'OTHER';
    },

    tyreConfigRotateOtherDisabled() {
      const selected = this.tyreConfigTableSelectedRow?.[0];
      const { action, 'Replace Type': replaceType } = selected || {};
      return selected && this.tyreReplaceValue?.keyValue == 'OTHER' && action != 'N' && replaceType == 'OTHER';
    },

    tyreConfigRotateOtherWorkshopJobIsNotLatest() {
      return this.disableAttachJob && !this.tyreRotateOtherVehicleWorkshopJobValue;
    },

    jobDateDate() {
      return this.jobDateValue?.formatString('yyyy-MM-dd') ?? '';
    },
    jobDateTime() {
      return this.jobDateValue?.formatString('HHmm') ?? '';
    },
  },

  methods: {
    async getWorkOrderApprovalSetup() {
      try {
        const result = await this.$apiCallV2('/DocumentApproval/DocApprovalWorkOrder', { actionflag: 'SETTINGS' });
        this.approvalSetup = result?.WorkOrderSettings || null;
      } catch (error) { this.approvalSetup = null; }
    },
    changedAfterApproval(row) {
      const approvedAt = this.lastApprovalDate ? new Date(this.lastApprovalDate).getTime() : NaN;
      if (!Number.isFinite(approvedAt)) return '-';

      const toTimestamp = (value) => {
        if (!value) return null;
        const timestamp = new Date(value).getTime();
        return Number.isFinite(timestamp) ? timestamp : null;
      };
      const createdAt = toTimestamp(row['Date Created']);
      const updatedAt = toTimestamp(row['Date Updated']);
      if (createdAt == null && updatedAt == null) return 'Dates unavailable';
      if (createdAt != null && createdAt > approvedAt) return 'Added after approval';
      if (updatedAt != null && updatedAt > approvedAt) return 'Updated after approval';
      return 'Unchanged';
    },

    approvalHistoryDate(value) {
      const date = value ? new Date(value) : null;
      return date && Number.isFinite(date.getTime()) ? date.formatString('dd/MM/yyyy HH:mm') : '';
    },
    async menuClicked(e) {
      this.viewMode = false;
      this.menuMode = e.key;
      this.selectedTabIndex = 0;
      switch (e.key) {
        case 'New': {
          this.showPopup = true;
          this.viewMode = false;
          this.clearPopupFields();
          this.jobDateValue = new Date();
          if (this.dateTimeInDefaultEnabled) this.dateTimeInValue = new Date(this.jobDateValue);
          await this.refreshStockLists();
          break;
        }

        case 'Edit': {
          if (this.workshopJobsTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Workshop Job is selected', 'Please select a Workshop Job to Edit');
            break;
          }
          if (this.workshopJobsTableSelectedRows[0].Cancelled == 'Yes') {
            this.$showAlert('error', 'Workshop Job cannot be edited', 'Cancelled Workshop Jobs are view-only.');
            break;
          }
          this.showPopup = true;
          this.populatePopupEntryFields(this.workshopJobsTableSelectedRows[0]['Job #']);
          break;
        }
        case 'View': {
          if (this.workshopJobsTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Workshop Job is selected', 'Please select a Workshop Job to View');
            break;
          }
          this.showPopup = true;
          this.viewMode = true;
          this.populatePopupEntryFields(this.workshopJobsTableSelectedRows[0]['Job #']);
          break;
        }
        case 'Delete': {
          if (this.workshopJobsTableSelectedRows.length < 1) {
            this.$showAlert('error', 'No Workshop Job is selected', 'Please select a Workshop Job to Delete');
            break;
          }

          const selectedJob = this.workshopJobsTableSelectedRows[0] || {};
          const { 'Job #': jobNo, canDelete, canDeleteReason, Cancelled } = selectedJob;

          if (canDelete != 1) {
            const reason = {
              CANCELLED: 'This Workshop Job is already cancelled.',
              POSTED_EXPENSE: 'Expenses are already posted for some of the parts.',
              TYRE_STATE: 'A later tyre change exists for this vehicle.',
            }[canDeleteReason] || (Cancelled == 'Yes' ? 'This Workshop Job is already cancelled.' : 'This Workshop Job cannot be safely reverted.');
            this.$showAlert(
              'error',
              'Workshop Job cannot be cancelled',
              reason,
            );
            break;
          }

          const discard = await this.$showAlert('warning', 'Are you sure you want to cancel this Workshop Job?', '', [
            { text: 'Cancel Job', color: 'Red', textColor: 'white' },
            { text: 'Cancel' },
          ]);
          if (discard == 'Cancel') return false;
          this.$fullscreenLoading('Cancelling Workshop Job');

          const result = (
            await this.$apiCallV2('/WorkshopJob/SaveEMSJob', {
              jobno: jobNo,
              action: 'D',
            })
          )[0];
          const { Results, ErrMsg } = result || {};

          if (Results == '0') {
            this.$showAlert('success', 'Workshop Job Cancelled', ErrMsg, [{ text: 'OK' }]);
          } else {
            this.$showAlert('error', 'Workshop Job Could Not Be Cancelled', ErrMsg, [{ text: 'OK' }]);
          }
          await this.getWorkshopJobs();
          this.$fullscreenLoading();
          break;
        }
        case 'Print': {
          const { 'Job #': jobno } = this.workshopJobsTableSelectedRows[0] || {};

          const printAckRoute = this.$router.resolve(
            `/Home/${this.$route.params.user}/Workshop/PrintWorkOrder${jobno ? `?job=${jobno.trim()}` : ''}`,
          );

          window.open(printAckRoute.href, '_blank');
          break;
        }

        case 'UpdateRetreadTyres': {
          this.updateTyrePopupOpen = true;
          break;
        }
      }
    },

    popupEntryPrintClicked() {
      const jobno = this.jobNoValue;
      const printAckRoute = this.$router.resolve(
        `/Home/${this.$route.params.user}/Workshop/PrintWorkOrder${jobno ? `?job=${jobno.trim()}` : ''}`,
      );

      window.open(printAckRoute.href, '_blank');
    },

    async filterRefreshClick() {
      this.jobNoFilterValueError = '';

      const noActiveFilters = !this.dateFilterActive && !this.jobNoFilterActive && !this.vehicleFilterActive && !this.workshopFilterActive;
      if (noActiveFilters) {
        this.dateFilterActive = true;
        this.filterEndDate = new Date();
        this.filterStartDate = this.getFilterStartDate(this.filterEndDate);
      }

      if (this.dateFilterActive) {
        if (!this.filterEndDate) {
          this.filterEndDate = new Date();
        }
        if (!this.filterStartDate || this.filterStartDate >= this.filterEndDate) {
          this.filterStartDate = this.getFilterStartDate(this.filterEndDate);
        }
        this.activeStartDate = this.filterStartDate;
        this.activeEndDate = this.filterEndDate;
      }

      if (this.jobNoFilterActive) {
        this.jobNoFilterValue = String(this.jobNoFilterValue ?? '').trim();
        if (!this.jobNoFilterValue) {
          this.jobNoFilterValueError = 'Job # is required';
          return;
        }
      }

      this.filterOpen = false;
      await this.getWorkshopJobs();
    },

    // #region API fetching data

    async getWorkshopJobColumnOrders() {
      const tableConfigs = [
        {
          viewsKey: 'workshopJobsTableColumnOrderViews',
          idKey: 'workshopJobsTableColumnOrderID',
          formctrlname: 'Workshop Job',
        },
        {
          viewsKey: 'docTableColumnOrderViews',
          idKey: 'docTableColumnOrderID',
          formctrlname: 'Workshop Job Documents',
        },
        {
          viewsKey: 'taskTableColumnOrderViews',
          idKey: 'taskTableColumnOrderID',
          formctrlname: 'Workshop Job Tasks',
        },
        {
          viewsKey: 'partTableColumnOrderViews',
          idKey: 'partTableColumnOrderID',
          formctrlname: 'Workshop Job Parts',
        },
        {
          viewsKey: 'tyreConfigTableColumnOrderViews',
          idKey: 'tyreConfigTableColumnOrderID',
          formctrlname: 'Workshop Job Tyre Changes',
        },
      ];

      await Promise.all(
        tableConfigs.map(async ({ viewsKey, idKey, formctrlname }) => {
          if (this[idKey] !== null) return;

          const columnOrderID = {
            formname: 'EMS_WorkshopJob',
            formctrlname,
          };
          const columnOrderViews = await this.$apiCallV2('/TableBox/GetColumnOrder', columnOrderID);
          this[viewsKey] =
            viewsKey == 'workshopJobsTableColumnOrderViews' ?
              this.normalizeWorkshopJobColumnOrderViews(columnOrderViews)
            : columnOrderViews;
          this[idKey] = columnOrderID;
        }),
      );
    },

    async getTyreRotateOtherVehicleWorkshopJobs(inventoryNo) {
      if (!inventoryNo) {
        this.tyreRotateOtherVehicleWorkshopJobs = [];
        return;
      }

      const result = await this.$apiCallV2('/WorkshopJob/GetEMSJobList', {
        startdate: '',
        enddate: '',
        inventoryno: inventoryNo,
      });
      this.tyreRotateOtherVehicleWorkshopJobs = Array.isArray(result) ? result.map(this.normalizeWorkshopJobListItem) : [];
    },

    async getWorkshopJobs() {
      this.workshopJobsTableLoading = true;
      this.workshopJobsTableData = [];
      this.workshopJobsTableSelectedRows = [];

      const joinDateTime = (date, time) => {
        if (!time) return date;
        const timeAdd = `${time.slice(0, 2)}:${time.slice(2, 5)}`;
        return `${date.slice(0, -5)}${timeAdd}`;
      };

      const filterBody = () => {
        const body = {};
        if (this.dateFilterActive) {
          body.startdate = new Date(this.activeStartDate).formatString('yyyy-MM-dd');
          body.enddate = new Date(this.activeEndDate).formatString('yyyy-MM-dd');
        }
        if (this.jobNoFilterActive && this.jobNoFilterValue) body.jobno = this.jobNoFilterValue;
        if (this.vehicleFilterActive) {
          body.inventoryno =
            this.vehicleFilterValue?.['Inventory #'] ||
            this.vehicleFilterValue?.['Lorry No'] ||
            this.vehicleFilterValue?.Lorry_No ||
            null;
          body.vehiclecategory = this.vehicleCategoryFilterValue?.['Vehicle Category'] || null;
        }
        if (this.workshopFilterActive) body.workshop = String(this.workshopFilterValue?.['Supplier Code'] ?? '').trim() || null;
        body.showcancelled = this.showCancelledJobs;
        return body;
      };

      const jobsPromise = this.$apiCallV2('/WorkshopJob/GetEMSJobList', filterBody());
      const columnOrdersPromise = this.getWorkshopJobColumnOrders();
      this.workshopJobsTableData = (await jobsPromise).map((item) => {
        const normalizedItem = this.normalizeWorkshopJobTableItem(item);
        const {
          'Date / Time In': dateTimeIn,
          TimeIn,
          'Date / Time Out': dateTimeOut,
          TimeOut,
        } = normalizedItem;
        const dateTimeInFormat = dateTimeIn ? joinDateTime(dateTimeIn, TimeIn) : null;
        const dateTimeOutFormat = dateTimeOut ? joinDateTime(dateTimeOut, TimeOut) : null;
        return {
          ...normalizedItem,
          'Date / Time In': dateTimeInFormat,
          'Date / Time Out': dateTimeOutFormat,
        };
      });

      this.workshopJobsTableLoading = false;
      await columnOrdersPromise;
    },

    async getWorkshopJobDetails(jobNo) {
      const result = await this.$apiCallV2('/WorkshopJob/GetWorkshopJobDetails', {
        recordid: jobNo,
      });
      return result.EMSJob;
    },

    async getVehicleList() {
      const result = await this.$apiCallV2('/Vehicle/GetVehicleSelectorList', {});
      this.vehicleList = (Array.isArray(result) ? result : []).map((item) => ({
        ...item,
        label: `${item['Inventory #']} - ${item['Inventory Code']} `,
      }));
    },

    async getVehicleCategoryList() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'VEHICLECATEGORY_UNIQUE' });
      this.vehicleCategoryList = result
        .map((item) => {
          const code = String(item['Category Code'] ?? item.CategoryCode ?? item.Category ?? '').trim();
          const description = String(item.Description ?? item.CategoryDesc ?? item['Category Description'] ?? code).trim();
          return {
            ...item,
            'Vehicle Category': code,
            label: description && description != code ? `${code} - ${description}` : code,
          };
        })
        .filter((item) => item['Vehicle Category']);
    },

    normalizeWorkshopJobListItem(item) {
      const source = item || {};
      const vehicleNo = source['Vehicle #'] ?? source['Inventory #'] ?? '';
      const vehicleCode = source['Vehicle Code'] ?? source['Inventory Code'] ?? source.InventoryNo ?? '';
      const normalized = {};
      let vehicleNoAdded = false;
      let vehicleCodeAdded = false;

      Object.keys(source).forEach((key) => {
        if (key == 'Inventory #' || key == 'Vehicle #') {
          if (!vehicleNoAdded) {
            normalized['Vehicle #'] = vehicleNo;
            vehicleNoAdded = true;
          }
        } else if (key == 'InventoryNo' || key == 'Inventory Code' || key == 'Vehicle Code') {
          if (!vehicleCodeAdded) {
            normalized['Vehicle Code'] = vehicleCode;
            vehicleCodeAdded = true;
          }
        } else {
          normalized[key] = source[key];
        }
      });

      if (!vehicleNoAdded) normalized['Vehicle #'] = vehicleNo;
      if (!vehicleCodeAdded) normalized['Vehicle Code'] = vehicleCode;
      return normalized;
    },

    normalizeWorkshopJobTableItem(item) {
      const normalized = this.normalizeWorkshopJobListItem(item);
      const vehicleNo = cleanText(normalized['Vehicle #']);
      const vehicleCode = cleanText(normalized['Vehicle Code']);
      const vehicle = [vehicleNo, vehicleCode].filter((value) => value).join(' - ');
      const tableItem = {};
      let vehicleColumnAdded = false;
      const hasCancelledColumn = Object.prototype.hasOwnProperty.call(normalized, 'Cancelled');
      const approvalBeforeConfirmed = Object.prototype.hasOwnProperty.call(normalized, 'Approval Status') && Object.prototype.hasOwnProperty.call(normalized, 'Confirmed');
      let cancelledInserted = false;

      Object.keys(normalized).forEach((key) => {
        if (key == 'Cancelled') return;
        if (key == 'Approval Status' && approvalBeforeConfirmed) return;

        if ((key == 'Vehicle #' || key == 'Vehicle Code') && !vehicleColumnAdded) {
          tableItem['Vehicle # - Vehicle Code'] = vehicle;
          vehicleColumnAdded = true;
        }
        if (key == 'Confirmed' && approvalBeforeConfirmed) {
          tableItem['Approval Status'] = normalized['Approval Status'];
        }
        tableItem[key] = normalized[key];
        if (key == 'Job #' && hasCancelledColumn) {
          tableItem.Cancelled = normalized.Cancelled;
          cancelledInserted = true;
        }
      });

      if (!vehicleColumnAdded) tableItem['Vehicle # - Vehicle Code'] = vehicle;
      if (hasCancelledColumn && !cancelledInserted) tableItem.Cancelled = normalized.Cancelled;
      return tableItem;
    },

    normalizeWorkshopJobColumnOrderViews(views) {
      const columnNameMap = {
        'Inventory #': 'Vehicle # - Vehicle Code',
        InventoryNo: 'Vehicle # - Vehicle Code',
        Submitted: 'Confirmed',
      };

      return (Array.isArray(views) ? views : []).map((view) => {
        const columns = [];
        (Array.isArray(view.Columns) ? view.Columns : []).forEach((column) => {
          const columnName = columnNameMap[column['Column Name']] || column['Column Name'];
          if (!columnName) return;

          const normalizedColumn = {
            ...column,
            'Column Name': columnName,
          };
          const existingIndex = columns.findIndex((existingColumn) => existingColumn['Column Name'] == columnName);
          if (existingIndex == -1) {
            columns.push(normalizedColumn);
          } else if (
            String(normalizedColumn.Show) == '1' &&
            String(columns[existingIndex].Show) != '1'
          ) {
            columns[existingIndex] = {
              ...columns[existingIndex],
              Show: normalizedColumn.Show,
            };
          }
        });

        const jobColumnIndex = columns.findIndex((column) => column['Column Name'] == 'Job #');
        if (jobColumnIndex >= 0) {
          const cancelledColumnIndex = columns.findIndex((column) => column['Column Name'] == 'Cancelled');
          const cancelledColumn =
            cancelledColumnIndex >= 0 ?
              columns.splice(cancelledColumnIndex, 1)[0]
            : { 'Column Name': 'Cancelled', Show: '1' };
          const adjustedJobColumnIndex = columns.findIndex((column) => column['Column Name'] == 'Job #');
          columns.splice(adjustedJobColumnIndex + 1, 0, cancelledColumn);
        }

        const confirmedIndex = columns.findIndex((column) => column['Column Name'] == 'Confirmed');
        if (confirmedIndex >= 0) {
          const approvalIndex = columns.findIndex((column) => column['Column Name'] == 'Approval Status');
          const approvalColumn = approvalIndex >= 0 ? columns.splice(approvalIndex, 1)[0]
            : { 'Column Name': 'Approval Status', Show: '1' };
          const adjustedConfirmedIndex = columns.findIndex((column) => column['Column Name'] == 'Confirmed');
          columns.splice(adjustedConfirmedIndex, 0, approvalColumn);
        }

        return { ...view, Columns: columns };
      });
    },

    async getEmployeeList() {
      this.employeeList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'EMPLOYEE' })).map((item) => ({
        '#': item['#'],
        EmployeeID: item.EmployeeID,
        'Employee Name': item['Employee Name'],
        ...item,
        label: `${item['EmployeeID']} - ${item['Employee Name']}`,
      }));
    },
    async getSupplierList() {
      this.supplierList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'SUPPLIER' }))
        .filter((item) => item.Active == 'YES' || item.Active == '1')
        .map((item) => ({
          ...item,
          label: `${item['Supplier Code']} - ${item['Supplier Name']}`,
        }));
    },

    async getDocType() {
      this.docTypeList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'doctype' })).map((item) => ({
        ...item,
        label: `${item['Doc Type']} - ${item.Description}`,
      }));
    },
    async getDocCode() {
      this.docCodeList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'doc' })).map((item) => ({
        ...item,
        label: `${item['Doc Code']} - ${item.Description}`,
      }));
    },

    async getServices() {
      this.taskServicesList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'services' })).map(
        (item) => ({
          ...item,
          label: `${item.ServiceCode} - ${item.Description}`,
        }),
      );
    },

    async getServicesTask() {
      this.taskServicesTaskList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'servicestask' })).map(
        (item) => ({
          ...item,
          label: `${item.ServiceCode} - ${item.Description}`,
        }),
      );
    },

    async getPartType() {
      const result = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTTYPEWITHPRODUCTINFO' });
      this.partTypeList = result.map((item) => {
        const { 'Part Type': PartType, Description, 'Part Group': PartGroup, ProdName, Size, Shade } = item;

        const partTypeLabel = `${PartType}${PartGroup ? ` [${PartGroup}]` : ''}`;
        const infoArray = [];
        if (ProdName && ProdName != Description) infoArray.push(ProdName);
        if (Size && Size != '-') infoArray.push(Size);
        if (Shade && Shade != '-') infoArray.push(Shade);
        const infoLabel = infoArray.length > 0 ? `[${infoArray.join('|')}]` : '';
        const descriptionLabel = `${Description} ${infoLabel}`;

        return {
          ...item,
          isExpense: item.IsExpenses,
          'Is Expense': ['Y', 'YES', '1', 'TRUE'].includes(String(item.IsExpenses ?? '').trim().toUpperCase()) ? 'Yes' : 'No',
          label: `${partTypeLabel} - ${descriptionLabel}`,
        };
      });
    },

    async refreshStockLists() {
      const requestId = ++this.stockListsRequestId;
      this.stockListsLoading = true;
      try {
        const results = await Promise.allSettled([
          this.getSerialNo(),
          this.getTakenFrom(),
          this.getJobPartTyreList(),
          this.getJobPartTyreSaveList(),
        ]);
        const failed = results.find((result) => result.status == 'rejected');
        if (failed) throw failed.reason;
      } finally {
        if (requestId === this.stockListsRequestId) this.stockListsLoading = false;
      }
    },

    async getSerialNo() {
      this.serialNoList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBPARTSERIALNO' });
    },

    async getTakenFrom() {
      this.takenFromList = (
        await this.$apiCallV2('/WorkshopJob/GetWorkshopJobWarehouseItems', {
          jobno: this.jobNoValue || '',
        })
      ).map((item) => ({
        ...item,
        label: `${item.Warehouse}${item['Serial No'] ? `- ${item['Serial No']}` : ''}`,
      }));
    },

    async getCountry() {
      this.countryList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'country' })).map((item) => ({
        ...item,
        label: `${item['Country Code']} - ${item.Country}`,
      }));
    },
    async getManufacturer() {
      this.manufacturerList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'manufacturer' })).map(
        (item) => ({ ...item, label: `${item.MFGCode} - ${item.MFGName}` }),
      );
    },
    async getVehicleInfo(vehicleNo) {
      const result = await this.$apiCallV2('/WorkshopJob/GetWorkshopJobVehicleInfo', { recordid: vehicleNo });
      const vehicle = result.Vehicle;
      return vehicle ? { ...vehicle, MeterReading: vehicle.MeterReading ?? 0 } : vehicle;
    },
    async getVehicleTyreLayout(vehicleCategory, vehicleType) {
      const key = JSON.stringify([vehicleCategory, vehicleType]);
      if (!this.tyreLayoutCache[key]) {
        this.tyreLayoutCache[key] = this.$apiCallV2(`/Vehicle/GetVehicleTyreConfiguration?category=${encodeURIComponent(vehicleCategory || '')}&type=${encodeURIComponent(vehicleType || '')}`)
          .then((rows) => rows.map((tyre) => ({ index: tyre.tyreindex, description: tyre.tyrenaming })));
      }
      try {
        return await this.tyreLayoutCache[key];
      } catch (error) {
        delete this.tyreLayoutCache[key];
        throw error;
      }
    },
    async getVehicleDriver(invNo) {
      const result = await this.$apiCallV2('/WorkshopJob/GetDriverDetails', { vehicleno: invNo });
      return result;
    },
    async getWarehouse() {
      this.warehouseList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'WAREHOUSE' })).map(
        (warehouse) => {
          return {
            ...warehouse,
            label: `${warehouse['Warehouse Code']} - ${warehouse.Name}`,
          };
        },
      );
    },
    async getJobPartTyreList() {
      this.jobPartTyreList = (await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBPARTTYRELIST' })).map(
        (item) => ({ ...item, label: `${item['Serial No']} - ${item['Part Type']}` }),
      );
    },
    async getJobPartTyreSaveList() {
      this.jobPartTyreSaveList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'JOBPARTTYRESAVE' });
    },

    async getSAS() {
      this.SASControl = await this.$apiCallV2(`/SASControl/GetSASControlAccess?system=EMS`);
    },

    async getLocalCurrency() {
      const companyDetails = await this.$apiCallV2('/Master/GetDetailsMaster', {
        masterfile: 'COMPANY',
        module: 'MASTER',
        recordid: this.$store.state.loginDetails.CoCode,
      });
      if (companyDetails.CompanyLocalCur) this.localCurrency = companyDetails.CompanyLocalCur;
    },

    //#endregion

    // #region clears
    clearPopupFields() {
      this.jobNoValue = '';
      this.jobDateValue = null;
      this.clearPopupFieldsWithoutJobNo();
    },

    clearPopupFieldsWithoutJobNo() {
      this.jobInventoryValue = null;
      this.selectedVehicleInfo = null;
      this.taskAddShow = false;
      this.maintenanceImportShow = false;

      this.clearErrorsGeneral();

      this.createdInfo = '';
      this.lastUpdatedInfo = '';

      this.trailerNoValue = null;
      this.requestedByValue = null;
      this.repairTypeValue = null;
      this.repairTypeValueError = '';
      this.internalExternalValue = null;
      this.internalExternalValueError = '';
      this.servicePersonValue = '';
      this.workshopValue = null;
      this.workshopValueError = '';
      this.mileageValue = '';
      this.mileageValueError = '';
      this.remark1Value = '';
      this.dateTimeInValue = null;
      this.dateTimeInDefaultOverridden = false;
      this.dateTimeOutValue = null;
      this.confirmJobValue = false;
      this.approvalStatus = '';
      this.lastApprovalDate = null;
      this.approvedAmount = null;
      this.pendingLevel = null;
      this.approvalHistory = [];
      this.jobAuditWarning = '';
      this.resubmitApproval = false;
      this.inventoryAvailabilityValue = null;
      this.inventoryUnavailableRemarkValue = '';
      this.inventoryUnavailableRemarkValueError = '';

      this.docTableData = [];
      this.taskTableData = [];
      this.partTableData = [];
      this.tyreConfigUpdateList = [];
      this.tyreRotateOtherVehicleWorkshopJobs = null;
      this.tyreConfigUpdateOtherVehicle = [];
      this.partUpdateOtherVehicle = [];
      this.vehicleTodoPopupShow = false;
      this.vehicleTodoPopupVehicleNo = '';
      this.vehicleTodoPopupJobNo = '';
      this.vehicleTodoPopupViewOnly = false;
      this.vehicleTodoLinkSaving = false;
      this.vehicleTodoPopupSaving = false;
      this.vehicleTodoPopupShowAttached = false;
      this.vehicleTodoPopupShowCancelled = false;
      this.vehicleTodoAttachedCount = 0;
      this.vehicleTodoPendingChanges = { toLink: [], toUnlink: [], updates: [] };
      this.vehicleTodoPendingSelection = null;
      this.vehicleTodoPopupInitialAttachedCount = 0;
      this.vehicleTodoPopupSelectionInitialized = false;

      this.clearTabsFields();

      this.clearEntryTexts();

      this.isLatestJob = true;
      this.isLatestTyreJob = true;
    },

    clearEntryTexts() {
      this.popupEntryRequiredError = [];
      this.popupEntrySuccessMessages = [];
      this.popupEntryErrorMessages = [];
    },
    clearTabsFields() {
      this.docFields.docReferenceNoValue = '';
      this.docFields.docDocTypeValue = null;
      this.docFields.docDocDateValue = null;
      this.docFields.docDateReceivedValue = null;
      this.docFields.docDocCodeValue = null;
      this.docFields.docPicDocValue = null;
      this.docFields.docRemarkValue = '';
      this.docFields.docPicDocType = '';
      this.docFields.docPicDocName = '';

      this.clearErrorsDoc();
      this.docFieldsChangedCheck = structuredClone(this.docFields);

      this.taskFields.taskServiceValue = null;
      this.taskFields.taskServiceTaskValue = null;
      this.taskServiceType = '';
      this.taskFields.taskServicePersonValue = '';
      this.taskFields.taskDateTimeStartValue = null;
      this.taskFields.taskDateTimeEndValue = null;
      this.taskFields.taskRemarkValue = '';

      this.clearErrorsTask();
      this.taskFieldsChangedCheck = structuredClone(this.taskFields);

      this.partFields.partTypeValue = null;
      this.clearPartFieldsWithoutType();

      this.clearErrorsPart();
      this.partFieldsChangedCheck = structuredClone(this.partFields);

      this.clearTyresFields();

      this.docTableSelectedRows = [];
      this.taskTableSelectedRows = [];
      this.partTableSelectedRows = [];

      this.docSelectedItem = null;
      this.taskSelectedItem = null;
      this.partSelectedItem = null;
    },

    clearPartFieldsWithoutType() {
      this.cancelPartSerialNoCheck();
      this.partFields.partWarehouseItemValue = null;
      this.partFields.partWarehouseItemSavedValue = null;
      this.partFields.partWarehouseItemSearch = '';
      this.partFields.partSerialNoValue = null;
      this.partFields.partSerialNoInputValue = '';
      this.partFields.partPartNoValue = '';
      this.partFields.partSizeValue = '';
      this.partFields.partTakenFromValue = null;
      this.partFields.partTakenFromInputValue = '';
      this.partFields.partUnitPriceValue = '';
      this.partFields.partMFGDateValue = null;
      this.partFields.partDatePurchaseValue = null;
      this.partFields.partInvoiceNoValue = '';
      this.partFields.partModelValue = '';
      this.partFields.partReplaceValue = '';
      this.partFields.partMadeInValue = null;
      this.partFields.partQuantityValue = '1';
      this.partFields.partManufacturerValue = null;
      this.partFields.partPurchaseFromValue = null;
      this.partFields.partPONoValue = '';
      this.partFields.partInsuranceNoValue = '';
      this.partFields.partRemarkValue = '';
    },

    clearTyresFields() {
      this.tyreConfigLayoutSelectedItem = null;
      this.tyrePositionSelectedValue = null;
      this.tyreConfigTableSelectedRow = [];
    },

    clearErrorsGeneral() {
      this.jobNoValueError = '';
      this.jobDateValueError = '';
      this.jobInventoryValueError = null;
      this.dateTimeInValueError = '';
    },

    clearErrorsDoc() {
      this.docReferenceNoError = '';
      this.docDocTypeError = '';
      this.docDocDateError = '';
      this.docDateReceivedError = '';
      this.docDocCodeError = '';
    },

    clearErrorsTask() {
      this.taskServiceError = '';
      this.taskServiceTaskError = '';
    },

    clearErrorsPart() {
      this.partTypeError = '';
      this.partSerialNoError = '';
      this.partQuantityError = '';
    },

    clearErrorsTyreConfig() {
      this.tyreExistingValueError = '';
      this.tyreConfigOtherVehicleValueError = '';
      this.tyreConfigTyreTypeValueError = '';
      this.tyreConfigSerialNoValueError = '';
      this.tyreConfigErrorText = '';
    },
    //#endregion

    // #region populate
    async populatePopupEntryFields(jobNo) {
      this.clearPopupFields();
      this.$fullscreenLoading('Getting Workshop Job Details...');
      const job = await this.getWorkshopJobDetails(jobNo);
      const {
        JobNo,
        JobDate,
        Inventory,
        LastUpdateTime,
        LastUpdateUserID,
        CreateTime,
        CreateUserID,
        Trailer,
        RequestedBy,
        RepairType,
        InternalExternal,
        Workshop,
        ServicePerson,
        Mileage,
        Remark,
        DateTimeIn,
        TimeIn,
        DateTimeOut,
        TimeOut,
        Status,
        ApprovalStatus,
        LastApprovalDate,
        ApprovedAmount,
        PendingLevel,
        ApprovalHistory,
        InvAvailability,
        Documents,
        Tasks,
        Parts,
        InvAvailabilityRemark,
        IsLatest,
        IsLatestTyreJob,
      } = job || {};

      const DateIn = DateTimeIn ? new Date(DateTimeIn) : null;
      if (DateIn) {
        const TimeInHour = TimeIn ? parseInt(TimeIn?.slice(0, 2)) : '00';
        const TimeInMinute = TimeIn ? parseInt(TimeIn?.slice(2, 5)) : '00';
        DateIn.setHours(TimeInHour, TimeInMinute);
      }

      const DateOut = DateTimeOut ? new Date(DateTimeOut) : null;
      if (DateOut) {
        const TimeOutHour = TimeOut ? parseInt(TimeOut?.slice(0, 2)) : '00';
        const TimeOutMinute = TimeOut ? parseInt(TimeOut?.slice(2, 5)) : '00';
        DateOut.setHours(TimeOutHour, TimeOutMinute);
      }

      this.jobFetched = job;

      this.jobNoValue = cleanText(JobNo);
      this.jobDateValue = new Date(JobDate);
      this.jobInventoryValue =
        this.vehicleList.find((item) => cleanText(item['Inventory #']) == cleanText(Inventory)) ?? null;
      this.lastUpdatedInfo = `${LastUpdateUserID} ${new Date(LastUpdateTime).formatString('dd/MM/yyyy HH:mm')}`;
      this.createdInfo =
        CreateUserID && CreateTime ?
          `${CreateUserID} ${new Date(CreateTime).formatString('dd/MM/yyyy HH:mm')}`
        : this.lastUpdatedInfo;

      this.trailerNoValue =
        this.trailerList.find((item) => cleanText(item['Inventory #']) == cleanText(Trailer)) ?? null;
      this.requestedByValue = this.employeeList.find((item) => item['EmployeeID'] == RequestedBy);
      this.repairTypeValue = this.repairTypeOptions.find((item) => item.keyValue == RepairType) ?? null;
      this.internalExternalValue = this.intExtOptions.find((item) => item.keyValue == InternalExternal) ?? null;
      this.servicePersonValue = ServicePerson;
      this.workshopValue = this.supplierList.find((item) => item['Supplier Code'] == Workshop) ?? null;
      const mileage = parseFloat(Mileage);
      this.mileageValue = Number.isFinite(mileage) ? mileage.toString() : '0';
      this.remark1Value = Remark;
      this.dateTimeInValue = DateIn || DateIn == 'Invalid Date' ? DateIn : null;
      this.dateTimeOutValue = DateOut || DateOut == 'Invalid Date' ? DateOut : null;
      this.confirmJobValue = String(Status || '').trim() == '12001';
      this.approvalStatus = ApprovalStatus || '';
      this.lastApprovalDate = LastApprovalDate || null;
      this.approvedAmount = ApprovedAmount;
      this.pendingLevel = PendingLevel ?? null;
      this.approvalHistory = Array.isArray(ApprovalHistory) ? ApprovalHistory : [];
      this.jobAuditWarning = ApprovalStatus === 'Disabled' ? '' : job.AuditWarning || '';
      this.inventoryAvailabilityValue =
        this.availabilityOptions.find((item) => item.keyValue == InvAvailability) ?? this.availabilityOptions[0];
      this.inventoryUnavailableRemarkValue = InvAvailabilityRemark || '';

      if (Documents)
        this.docTableData = this.convertToArray(Documents).map((doc, index) => ({
          ...doc,
          'Date Received': this.combineDateTime(doc['Date Received'], doc['Time Received']),
          seqNo: index + 1, //seqNo used for table actions new/edit/delete
        }));
      if (Tasks)
        this.taskTableData = this.convertToArray(Tasks).map((task) => ({
          ...task,
          'Date Start': this.combineDateTime(task['Date Start'], task['Time Start']),
          'Date End': this.combineDateTime(task['Date End'], task['Time End']),
        }));

      await this.populateVehicleInfo(Inventory);
      const parts = this.convertToArray(Parts);
      this.tyreConfigUpdateList = this.tyreConfigUpdateList.map((tyre) => {
        if (cleanText(tyre.oldserialno)) return tyre;

        const part = parts.find(
          (item) =>
            cleanText(item['Serial No']) &&
            cleanText(item.TyreIndex) != null &&
            cleanText(item['Serial No']) == cleanText(tyre.serialno) &&
            cleanText(item.TyreIndex) == cleanText(tyre.tyreindex),
        );
        const oldSerialNo = cleanText(part?.Replace);
        return oldSerialNo ? { ...tyre, oldserialno: oldSerialNo } : tyre;
      });

      if (Parts)
        this.partTableData = parts.map((part, index) => {
          const { Type, MfgDate, 'Date Purchase': DatePurchase } = part;
          const {
            ProdTxnID,
            Description,
            'Expense Code': expenseCode,
            IsExpenses: isExpense,
          } = this.partTypeList.find((item) => item['Part Type'] == Type) || {};
          const update = this.tyreConfigUpdateList.find(
            (tyre) =>
              cleanText(tyre.serialno) == cleanText(part['Serial No']) &&
              cleanText(tyre.tyreindex) == cleanText(part.TyreIndex),
          );
          const isRotation = update?.type == 'ROTATE' || update?.type == 'OTHER';

          return {
            ...part,
            seqNo: index + 1,
            prodTxnID: ProdTxnID,
            AllocGUID: part.AllocGUID || '',
            BinLoc: part.BinLoc || '',
            expenseCode: expenseCode,
            isExpense: isRotation ? 'N' : isExpense,
            partType: Type,
            partTypeDesc: Description,
            MfgDate: MfgDate?.split('T')[0] || null,
            DatePurchase: DatePurchase?.split('T')[0] || null,
            tyreSeqNo: update ? update.seqno : null,
          };
        });

      this.isLatestJob = IsLatest == '1' || IsLatest == 1 || IsLatest === true;
      this.isLatestTyreJob = IsLatestTyreJob == '1' || IsLatestTyreJob == 1 || IsLatestTyreJob === true;

      this.$fullscreenLoading();
      await Promise.all([
        this.refreshStockLists(),
        this.refreshVehicleTodoAttachedCount(),
      ]);
    },

    async populateVehicleInfo(inventoryNo) {
      this.$fullscreenLoading('Getting Vehicle info...');
      const vehicle = await this.getVehicleInfo(inventoryNo);
      const { TyreJob, VehicleCategory, Type, MeterReading, Availability } = vehicle || {};
      this.selectedVehicleInfo = vehicle;
      if (this.menuMode == 'New') {
        this.inventoryAvailabilityValue =
          this.availabilityOptions.find((item) => item.keyValue == Availability) ?? this.availabilityOptions[0];
        this.inventoryUnavailableRemarkValue = '';
      }

      const tyreLayout = await this.getVehicleTyreLayout(VehicleCategory, Type);

      const tyreConfigList = this.tyreConfigToStructure(TyreJob, tyreLayout);

      this.tyreConfigTyresList = tyreConfigList.filter((item) => cleanText(item.jobno) != cleanText(this.jobNoValue));
      this.tyreConfigUpdateList = tyreConfigList.filter((item) => cleanText(item.jobno) == cleanText(this.jobNoValue));
      this.tyreConfigTableSelectedRow = [];

      this.tyreLayout = [
        ...tyreLayout,
        { index: '100', description: 'Spare1' },
        { index: '101', description: 'Spare2' },
        { index: '102', description: 'Spare3' },
        { index: '103', description: 'Spare4' },
      ];

      if (this.menuMode == 'New') {
        this.mileageValue = MeterReading ?? '0';
      }
      this.$fullscreenLoading();
    },

    async loadDocumentFiles(doc = null) {
      const rows = doc ? [doc] : this.docTableData.filter((item) => item.action !== 'D');
      const pending = rows.filter((item) => item.DocumentGUID && !item.FileLoaded);
      if (!pending.length) return;
      const result = await this.$apiCallV2('/WorkshopJob/GetWorkshopJobDetails', {
        recordid: this.jobNoValue, actionflag: 'DOCUMENTS', documentguid: doc?.DocumentGUID || '',
      });
      if (!Array.isArray(result?.Documents)) throw new Error('Unable to load existing attachments.');
      pending.forEach((item) => {
        const file = result.Documents.find((row) => String(row.DocumentGUID).toLowerCase() === String(item.DocumentGUID).toLowerCase());
        if (!file) throw new Error('Attachment changed or is unavailable. Reopen the job before saving.');
        this.$set(item, 'PicDocFile', file.PicDocFile);
        this.$set(item, 'PicDocType', file.PicDocType);
        this.$set(item, 'FileLoaded', true);
        const original = this.docTableData.find((row) => row.DocumentGUID === item.DocumentGUID);
        if (original && original !== item) Object.assign(original, { PicDocFile: file.PicDocFile, PicDocType: file.PicDocType, FileLoaded: true });
      });
    },
    populateDocumentFields(doc) {
      this.clearTabsFields();
      const {
        Code,
        Date: DocDate,
        'Date Received': DateReceived,
        'Pic/Doc': PicDoc,
        PicDocFile,
        PicDocType,
        'Ref No': RefNo,
        Remark,
        Type,
      } = doc || {};
      this.docFields.docReferenceNoValue = RefNo;
      this.docFields.docDocTypeValue = this.docTypeList.find((item) => item['Doc Type'] == Type);
      this.docFields.docDocDateValue = DocDate ? new Date(DocDate) : null;
      this.docFields.docDateReceivedValue = DateReceived ? new Date(DateReceived) : null;
      this.docFields.docDocCodeValue = this.docCodeList.find((item) => item['Doc Code'] == Code);
      this.docFields.docPicDocName = PicDoc;
      this.docFields.docPicDocType = PicDocType;
      this.docFields.docPicDocValue = PicDocFile;

      //att here
      this.docFields.docRemarkValue = Remark;

      if (!this.viewMode) this.docFieldsChangedCheck = structuredClone(this.docFields);
    },

    populateTaskFields(task) {
      this.clearTabsFields();
      const {
        'Date Start': DateStart,
        'Date End': DateEnd,
        Remark,
        'Service Code': ServiceCode,
        'Service Person': ServicePerson,
        'Task Code': TaskCode,
      } = task || {};

      this.taskFields.taskServiceValue = this.taskServicesList.find((item) => item.ServiceCode == ServiceCode) ?? null;
      this.taskServiceSelected(this.taskFields.taskServiceValue);
      this.taskFields.taskServiceTaskValue =
        this.taskServicesTaskList.find((item) => item.ServiceCode == TaskCode) ?? null;
      this.taskFields.taskServicePersonValue = ServicePerson;
      this.taskFields.taskDateTimeStartValue = DateStart ? new Date(DateStart) : null;
      this.taskFields.taskDateTimeEndValue = DateEnd ? new Date(DateEnd) : null;
      this.taskFields.taskRemarkValue = Remark;

      if (!this.viewMode) this.taskFieldsChangedCheck = structuredClone(this.taskFields);
    },

    populatePartFields(part) {
      this.clearTabsFields();
      const {
        Type,
        'Serial No': SerialNo,
        'Part No': PartNo,
        Manufacturer,
        'Made In': MadeIn,
        Size,
        'Cost (RM)': Cost,
        Replace,
        'Purchase From': PurchaseFrom,
        'Date Purchase': DatePurchase,
        'Insurance Claim No': InsuranceNo,
        'Invoice No': InvoiceNo,
        'Taken From': Warehouse,
        Remark,
        PONo,
        MfgDate,
        Model,
        Quantity,
      } = part || {};

      const quantityValue = this.parsePartAmount(Quantity) || 1;
      const unitPrice = this.parsePartAmount(Cost) / quantityValue;
      const isWarehouseLinked = this.partHasWarehouseLink(part);

      this.partFields.partWarehouseItemSavedValue = isWarehouseLinked ? part : null;
      this.partFields.partTypeValue = this.partTypeList.find((item) => cleanText(item['Part Type']) == cleanText(Type)) ?? null;
      this.partFields.partSerialNoValue =
        isWarehouseLinked ? this.serialNoList.find((item) => item['Serial No'] == SerialNo) ?? null : null;
      this.partFields.partSerialNoInputValue = SerialNo || '';
      this.partFields.partPartNoValue = PartNo;
      this.partFields.partSizeValue = Size;
      const warehouseItem = isWarehouseLinked ?
        this.warehouseItemListFiltered.find((item) => this.warehouseItemMatchesPart(item, part))
        : null;
      const selectedWarehouse = this.partWarehouseValue(warehouseItem || { Warehouse });
      this.partFields.partTakenFromValue = selectedWarehouse;
      this.partFields.partTakenFromInputValue = selectedWarehouse?.label ?? Warehouse ?? '';
      if (warehouseItem) {
        this.partFields.partWarehouseItemValue = warehouseItem;
        this.partFields.partWarehouseItemSearch = warehouseItem.label;
      }
      this.partFields.partUnitPriceValue = this.$currencyFormat(unitPrice);
      this.partFields.partMFGDateValue = MfgDate ? new Date(MfgDate) : null;
      this.partFields.partDatePurchaseValue = DatePurchase ? new Date(DatePurchase) : null;
      this.partFields.partInvoiceNoValue = InvoiceNo;
      this.partFields.partReplaceValue = Replace;
      this.partFields.partModelValue = Model;
      this.partFields.partMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.partFields.partQuantityValue = this.normalizePartQuantityValue(Quantity || '1') || '1';
      this.partFields.partManufacturerValue =
        this.manufacturerList.find((item) => item.MFGCode == Manufacturer) ?? null;
      this.partFields.partPurchaseFromValue =
        this.supplierList.find((item) => item['Supplier Code'] == PurchaseFrom) ?? null;
      this.partFields.partPONoValue = PONo;
      this.partFields.partInsuranceNoValue = InsuranceNo;
      this.partFields.partRemarkValue = Remark;

      if (!this.viewMode) this.partFieldsChangedCheck = structuredClone(this.partFields);
    },
    //#endregion

    //#region tyreConfig
    tyreGridClicked() {
      this.clearTyreConfigFieldsOnly();
      this.tyreUpdateMode = 'N';
      const { index: selectedIndex } = this.tyreConfigLayoutSelectedItem || {};
      if (!selectedIndex) {
        this.tyreConfigTableSelectedRow = [];
        this.tyrePositionSelectedValue = null;
        return;
      }
      this.tyrePositionSelectedValue = this.tyreConfigLayout.findLast((item) => item.index == selectedIndex) ?? null;

      this.tyreConfigTableSelectedRow = (() => {
        let returnItem = null;
        returnItem = this.tyreConfigUpdateListFiltered.find((item) => item.tyreindex == selectedIndex);
        if (!returnItem) {
          const findUpdate = this.tyreConfigUpdateList.find((item) => item.tyreindex == selectedIndex);
          if (findUpdate?.type == 'ROTATE') {
            const findPair = this.tyreConfigUpdateListFiltered.find((item) => item.seqno == findUpdate.seqno - 1);
            returnItem = findPair;
          }
        }
        return returnItem ? [{ ...returnItem, _rowid: `row${returnItem?.['#'] - 1}` }] : [];
      })();
    },
    tyreDropdownSelected(e) {
      const { index } = e || {};
      if (!index) {
        this.tyreConfigLayoutSelectedItem = null;
        this.tyreConfigTableSelectedRow = [];
        return;
      }
      this.tyreConfigLayoutSelectedItem = this.tyreConfigLayout?.find((item) => item.index == index) ?? null;

      this.tyreGridClicked();
    },
    tyreTableRowSelected(row, selected) {
      if (!selected) {
        this.tyreConfigLayoutSelectedItem = null;
        this.tyrePositionSelectedValue = null;
        return;
      }
      const { tyreindex } = row;
      const selectGrid = this.tyreConfigLayout?.find((item) => item.index == tyreindex);
      this.tyreConfigLayoutSelectedItem = selectGrid ?? null;
      this.tyrePositionSelectedValue = this.tyreConfigLayout.find((item) => item.index == tyreindex) ?? null;
    },

    async tyreConfigPopupOpen() {
      this.clearTyreConfigPopup();
      this.clearErrorsTyreConfig();
      const {
        'Old Serial No': oldserialno,
        action,
        otherjobno,
        'Replace Type': replace,
      } = this.tyreConfigTableSelectedRow[0] || {};
      const { serialno: currentOnTyre } = this.tyrePositionSelectedValue?.tyreInfo || {};
      this.tyreConfigOldSerialNoValue = oldserialno ?? currentOnTyre ?? '';
      if (!this.tyreConfigOldSerialNoValue) {
        this.tyreStatusValue = this.tyreStatusOptions[0];
      }

      if (this.tyreConfigTableSelectedRow.length > 0) {
        const { seqno } = this.tyreConfigTableSelectedRow[0];
        const tyreInfo = this.tyreConfigUpdateList.find((item) => item.seqno == seqno) || {};
        const partInfo = this.partTableData.find((item) => item.tyreSeqNo == seqno) || {};
        await this.populateTyreConfigFields(tyreInfo, partInfo);
        await this.validateTyreConfigPositionEdit(tyreInfo);
      }

      if (replace == 'OTHER') {
        await this.getTyreRotateOtherVehicleWorkshopJobs(this.tyreConfigOtherVehicleValue?.['Inventory #']);

        const findOtherSame = this.tyreConfigUpdateList.find(
          (item) =>
            cleanText(item.otherinventoryno) == cleanText(this.tyreConfigOtherVehicleValue?.['Inventory #']) &&
            item.action != 'D',
        );
        if (action != 'N' && findOtherSame) this.disableAttachJob = true;
        else this.disableAttachJob = false;

        const linkedJobNo = otherjobno || findOtherSame?.otherjobno;
        const findWorkshop = this.tyreRotateOtherVehicleWorkshopJobList.find(
          (item) => cleanText(item['Job #']) == cleanText(linkedJobNo),
        );
        if (findWorkshop) this.tyreRotateOtherVehicleWorkshopJobValue = findWorkshop;
        else if (linkedJobNo) {
          this.tyreRotateOtherVehicleWorkshopJobValue = {
            'Job #': linkedJobNo,
            label: linkedJobNo,
          };
        }
      }

      this.tyreConfigPopupShow = true;
    },

    async validateTyreConfigPositionEdit(tyreInfo) {
      const selected = this.tyreConfigTableSelectedRow[0] || {};
      this.tyreConfigPositionEditAllowed = false;
      this.tyreConfigPositionEditChecked = false;
      this.tyreConfigPositionEditMessage = '';

      if (!selected.seqno || selected.action == 'N' || this.viewMode) return;

      this.$fullscreenLoading('Checking Tyre Change...');
      const result = await this.$apiCallV2('/WorkshopJob/ValidateTyreChangeEdit', {
        jobno: this.jobNoValue,
        seqno: selected.seqno,
        inventoryno: this.jobInventoryValue?.['Inventory #'] ?? tyreInfo.inventoryno ?? '',
        tyreindex: selected.tyreindex ?? tyreInfo.tyreindex ?? '',
        oldserialno: selected['Old Serial No'] ?? tyreInfo.oldserialno ?? '',
        newserialno: selected['New Serial No'] ?? tyreInfo.serialno ?? '',
        datechanged: selected['Date Changed'] ?? tyreInfo.datechange ?? '',
      });
      this.$fullscreenLoading();

      const response = Array.isArray(result) ? result[0] || {} : result || {};
      const allowEdit = response.allowEdit ?? response.AllowEdit;
      this.tyreConfigPositionEditChecked = true;
      this.tyreConfigPositionEditAllowed = allowEdit === true || allowEdit == '1';
      if (!this.tyreConfigPositionEditAllowed)
        this.tyreConfigPositionEditMessage =
          response.reason ||
          response.Reason ||
          response.ErrMsg ||
          'Tyre change cannot be edited because future changes may exist for this position. Only Job Part Info is allowed for editing.';
    },

    tyreConfigRadioChanged() {
      this.tyreConfigOtherVehicleValue = null;
      this.tyreRotateOtherVehicleWorkshopJobs = null;
      this.tyreRotateOtherVehicleWorkshopJobValue = null;
      this.tyreRotateOtherVehicleWorkshopJobInputValue = '';
      const replaceType = this.tyreReplaceValue?.keyValue;
      this.tyreConfigJobPriceValue = '';

      this.clearTyreConfigFieldsOnly();
      if (replaceType == 'ROTATE' || replaceType == 'OTHER') {
        this.tyreConfigUnitPriceValue = '0';
        this.tyreConfigJobPriceValue = '0';
      }
      this.tyreConfigRotateLayoutChange(replaceType);

      this.clearErrorsTyreConfig();
    },

    tyreConfigRotateLayoutChange(replaceType) {
      if (replaceType == 'ROTATE') {
        const { 'New Serial No': newserialnoSelected, tyreindex: tyreindexTableSelected } =
          this.tyreConfigTableSelectedRow[0] || {};
        const tyreindexselected = tyreindexTableSelected ?? this.tyrePositionSelectedValue?.index;
        this.tyreConfigSelectedVehicleLayout = this.tyreConfigLayout.map((tyre) => {
          const { tyreInfo, index } = tyre;
          const { serialno } = tyreInfo || {};
          let returnState = tyre.state;
          if (index == tyreindexselected) {
            returnState = 'changes';
          }
          if (serialno && serialno == newserialnoSelected) {
            returnState = 'saved';
          }

          const findRevert = this.tyreConfigUpdateList.findLast(
            (item) => item.tyreindex == tyreindexselected && item.revert,
          );

          let returnTyreInfo = tyre.tyreInfo;
          if (findRevert) {
            returnTyreInfo = findRevert;
          }

          return { ...tyre, state: returnState, label: returnTyreInfo?.label, tyreInfo: returnTyreInfo };
        });

        this.tyreConfigSelectedVehicleLayout = this.tyreConfigSelectedVehicleLayout.map((item) => {
          if (item.state != 'saved' || this.viewMode || !this.tyreChangeAllowed) return { ...item, disabled: true };
          return item;
        });

        this.tyreConfigSelectedVehicleNo = this.jobInventoryValue?.['Inventory #'] ?? '';
      } else {
        this.tyreConfigSelectedVehicleLayout = null;
        this.tyreConfigSelectedVehicleNo = '';
      }

      if (replaceType == 'WHSE') {
        this.tyreConfigWarehouseSelected();
      }
    },

    async tyreConfigOtherVehicleSelected() {
      this.tyreConfigSelectedVehichleTyreConfig = [];
      this.tyreConfigSelectedVehichleTyreConfigFull = [];
      if (!this.tyreConfigOtherVehicleValue) {
        this.tyreConfigSelectedVehicleLayout = null;
        return;
      }

      this.tyreRotateOtherVehicleWorkshopJobValue = null;
      this.tyreRotateOtherVehicleWorkshopJobInputValue = '';

      const { 'Inventory #': InventoryNo } = this.tyreConfigOtherVehicleValue || {};

      await this.getTyreRotateOtherVehicleWorkshopJobs(InventoryNo);

      const findUpdate = this.tyreConfigUpdateList.find(
        (item) => cleanText(item.otherinventoryno) == cleanText(InventoryNo),
      );

      if (findUpdate) {
        const findJob = this.tyreRotateOtherVehicleWorkshopJobList.find(
          (item) => cleanText(item['Job #']) == cleanText(findUpdate.otherjobno),
        );
        if (findJob) this.tyreRotateOtherVehicleWorkshopJobValue = findJob;
        else if (findUpdate.otherjobno) {
          this.tyreRotateOtherVehicleWorkshopJobValue = {
            'Job #': findUpdate.otherjobno,
            label: findUpdate.otherjobno,
          };
        }
      }
      const findUpdateIfNew = this.tyreConfigUpdateList.find(
        (item) => cleanText(item.otherinventoryno) == cleanText(InventoryNo) && item.action != 'D',
      );
      if (findUpdateIfNew) {
        this.disableAttachJob = true;
      } else {
        this.disableAttachJob = false;
      }

      await this.getTyreConfigOtherVehicle();
    },

    async getTyreConfigOtherVehicle() {
      const { 'Inventory #': InventoryNo } = this.tyreConfigOtherVehicleValue || {};

      this.$fullscreenLoading('Getting Other Vehicle Tyre Layout...');
      const vehicle = await this.getVehicleInfo(InventoryNo);
      if (!vehicle) {
        this.tyreConfigSelectedVehicleLayout = null;
        this.tyreConfigSelectedVehicleNo = '';
        this.$fullscreenLoading();
        return;
      }

      const { TyreJob, VehicleCategory, Type } = vehicle;
      const tyreLayout = await this.getVehicleTyreLayout(VehicleCategory, Type);

      let tyreConfig = this.tyreConfigToStructure(TyreJob, tyreLayout);

      this.tyreConfigSelectedVehichleTyreConfigFull = tyreConfig;

      tyreConfig.map((tyre) => {
        const findUpdate = this.tyreConfigUpdateList.find(
          (item) =>
            cleanText(item.serialno) == cleanText(tyre.oldserialno) &&
            cleanText(item.oldserialno) == cleanText(tyre.serialno) &&
            cleanText(item.type) == 'OTHER',
        );
        if (findUpdate) {
          tyreConfig = tyreConfig.filter((item) => item.seqno != tyre.seqno);

          const findPrev = tyreConfig.findLast(
            (item) =>
              cleanText(item.serialno) == cleanText(findUpdate.serialno) &&
              cleanText(item.tyreindex) == cleanText(tyre.tyreindex) &&
              item.seqno != tyre.seqno,
          );
          tyreConfig = tyreConfig.map((item) => {
            if (findPrev && item.seqno == findPrev.seqno) {
              return {
                ...item,
                inuse: '1',
              };
            }
            return item;
          });
        }
      });

      this.tyreConfigUpdateOtherVehicle.map((item) => {
        if (item.revert && cleanText(item.inventoryno) == cleanText(InventoryNo)) {
          tyreConfig = tyreConfig.map((tyre) => {
            if (tyre.seqno == item.seqno) {
              return {
                ...tyre,
                inuse: item.inuse,
              };
            }
            return tyre;
          });
        }
      });

      const { 'New Serial No': newserialnoSelected } = this.tyreConfigTableSelectedRow?.[0] || {};

      this.tyreConfigSelectedVehichleTyreConfig = tyreConfig;

      let returnLayout = [
        ...tyreLayout,
        { index: '100', description: 'Spare1' },
        { index: '101', description: 'Spare2' },
        { index: '102', description: 'Spare3' },
        { index: '103', description: 'Spare4' },
      ].map((layout) => {
        const found = tyreConfig.find(
          (item) => layout.index == item.tyreindex && (item.inuse == '1' || item.inuse == '2'),
        );
        const editsFound = this.tyreConfigUpdateList.find(
          (item) =>
            cleanText(item.serialno) == cleanText(found?.serialno) &&
            cleanText(item.serialno) != cleanText(this.tyreConfigTableSelectedRow?.[0]?.['New Serial No']) &&
            item.inuse == '1' &&
            item.action != 'D',
        );

        const changesInLatest =
          found &&
          this.tyreRotateOtherVehicleWorkshopJobValue &&
          cleanText(this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #']) == cleanText(found.jobno);

        const state = (() => {
          if (editsFound || changesInLatest) return 'changes';
          if (found) return 'saved';
          return 'normal';
        })();

        return {
          ...layout,
          state: state,
          tyreInfo: found,
          label: found ? `${layout['description']} - ${found.serialno}` : '',
        };
      });

      returnLayout = returnLayout.map((item) => {
        const findTaken = this.tyreConfigUpdateList?.find(
          (update) =>
            cleanText(update.serialno) == cleanText(item.tyreInfo?.serialno) &&
            cleanText(update.serialno) != cleanText(newserialnoSelected) &&
            update.action != 'D',
        );
        if (findTaken) {
          return {
            ...item,
            state: 'changes',
          };
        }
        return item;
      });

      if (this.tyreConfigRotateOtherDisabled || this.tyreConfigRotateOtherWorkshopJobIsNotLatest) {
        returnLayout = returnLayout.map((item) => {
          if (newserialnoSelected && cleanText(item.tyreInfo?.serialno) == cleanText(newserialnoSelected))
            return {
              ...item,
              state: 'saved',
            };
          return {
            ...item,
            state: 'normal',
          };
        });
      }

      this.tyreConfigSelectedVehicleLayout = returnLayout.map((item) => {
        const { state, tyreInfo } = item || {};
        return { ...item, disabled: state == 'changes' || !tyreInfo || this.tyreConfigRotateOtherDisabled };
      });
      this.tyreConfigSelectedVehicleNo = InventoryNo ?? '';

      this.$fullscreenLoading();
    },

    tyreConfigRotateClicked() {
      if (!this.tyreConfigRotateSelectedTyre) {
        this.clearTyreConfigFields();
        this.tyreConfigSelectedVehicleDropdownValue = null;
        return;
      }
      const { index } = this.tyreConfigRotateSelectedTyre;
      this.tyreConfigSelectedVehicleDropdownValue =
        this.tyreConfigSelectedVehicleDropdownList.find((item) => item.index == index) || null;
      const tyreInfo = this.tyreConfigSelectedVehicleDropdownValue?.tyreInfo || {};
      const { seqno } = tyreInfo;
      const partInfo = this.partTableData.find((item) => item.tyreSeqNo == seqno) || {};
      this.populateTyreConfigFields(tyreInfo, partInfo, false);
    },

    tyreConfigSelectedVehicleDropdownSelected(e) {
      this.tyreConfigRotateSelectedTyre = e ?? null;
      this.tyreConfigRotateClicked();
    },

    tyreConfigWarehouseSelected(newItem, oldItem) {
      if (!this.tyreExistingValue || (newItem && oldItem && this.warehouseItemMatchesPart(newItem, oldItem))) return;
      const {
        'Serial No': SerialNo,
        Warehouse,
        Model,
        'Part Type': PartType,
        ID: ProdTxnID,
        MadeIn,
        Manufacturer,
        ProdDate,
        PurcDate,
        UnitPrice,
        Supplier: SppCode,
        PONo,
        'Part No': PartNo,
      } = this.tyreExistingValue || {};

      let findTyreType = this.tyreTypeList.find((item) => item.ProdTxnID == ProdTxnID);
      if (!findTyreType) findTyreType = this.tyreTypeList.find((item) => item['Part Type'] == PartType) || {};
      const { Size } = findTyreType;

      this.tyreConfigTyreTypeValue = findTyreType;
      this.tyreConfigSerialNoValue = SerialNo ?? '';
      this.tyreConfigPartNoValue = PartNo ?? '';
      this.tyreConfigSizeValue = Size?.toString() ?? '';
      const findWarehouse = this.warehouseList.find((item) => item['Warehouse Code'] == Warehouse) ?? null;
      if (findWarehouse) this.tyreConfigTakenFromValue = findWarehouse;
      else this.tyreConfigTakenFromInputValue = Warehouse ?? '';
      this.tyreConfigUnitPriceValue = UnitPrice?.toString() ?? '';
      this.tyreConfigModelValue = Model ?? '';
      this.tyreConfigMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.tyreConfigManufacturerValue = this.manufacturerList.find((item) => item.MFGCode == Manufacturer) ?? null;
      this.tyreConfigSupplierValue = this.supplierList.find((item) => item['Supplier Code'] == SppCode) ?? null;
      this.tyreConfigPONoValue = PONo ?? '';
      this.tyreConfigMfgDateValue = ProdDate ? new Date(ProdDate) : null;
      this.tyreConfigDatePurchaseValue = PurcDate ? new Date(PurcDate) : null;

      this.tyreConfigJobPriceValue = UnitPrice?.toString() ?? '';
    },

    clearTyreConfigPopup() {
      this.tyreStatusValue = null;
      this.tyreStoreInValue = null;
      this.tyreReplaceValue = null;
      this.tyreExistingValue = null;
      this.tyreConfigOtherVehicleValue = null;
      this.tyreRotateOtherVehicleWorkshopJobs = null;

      this.tyreConfigSelectedVehicleLayout = null;
      this.tyreConfigSelectedVehicleNo = '';

      this.tyreRotateOtherVehicleWorkshopJobInputValue = '';
      this.tyreConfigPositionEditAllowed = false;
      this.tyreConfigPositionEditChecked = false;
      this.tyreConfigPositionEditMessage = '';

      this.clearTyreConfigFields();
      this.clearTyreConfigPartFields();
    },

    clearTyreConfigFields() {
      this.tyreConfigTyreTypeDisabled = true;

      this.tyreRotateOtherVehicleWorkshopJobValue = null;
      this.tyreRotateOtherVehicleWorkshopJobInputValue = '';
      this.disableAttachJob = false;

      this.clearTyreConfigFieldsOnly();
    },

    clearTyreConfigFieldsOnly() {
      this.cancelTyreConfigSerialNoCheck();
      this.tyreConfigRotateSelectedTyre = null;
      this.tyreConfigSelectedVehicleDropdownValue = null;

      this.tyreConfigTyreTypeValue = null;
      this.tyreConfigSerialNoValue = '';
      this.tyreConfigPartNoValue = '';
      this.tyreConfigSizeValue = '';
      this.tyreConfigModelValue = '';
      this.tyreConfigTakenFromValue = null;
      this.tyreConfigTakenFromInputValue = '';
      this.tyreConfigUnitPriceValue = '';
      this.tyreConfigModelValue = '';
      this.tyreConfigMadeInValue = null;
      this.tyreConfigManufacturerValue = null;
      this.tyreConfigSupplierValue = null;
      this.tyreConfigPONoValue = '';
      this.tyreConfigMfgDateValue = null;
      this.tyreConfigDatePurchaseValue = null;
    },

    clearTyreConfigPartFields() {
      this.tyreConfigOldSerialNoValue = '';
      this.tyreConfigJobPriceValue = '';
      this.tyreConfigInvoiceNoValue = null;
      this.tyreConfigInsuranceNoValue = null;
      this.tyreConfigRemarksValue = '';
    },

    async tyreConfigDeleteClicked() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Tyre Change?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;

      let updateTyreUpdateList = this.tyreConfigUpdateList;
      let updateTyreConfigList = this.tyreConfigTyresList;
      const {
        'Replace Type': type,
        seqno: selectedSeqNo,
        action,
        'Old Serial No': oldserialno,
        'New Serial No': newserialno,
        rotatedwith,
        otherjobno,
      } = this.tyreConfigTableSelectedRow?.[0] || {};
      const seqno = parseInt(selectedSeqNo);
      if (action == 'N') {
        updateTyreUpdateList = updateTyreUpdateList.filter((item) => item.seqno != seqno);
        if (oldserialno) {
          updateTyreUpdateList = updateTyreUpdateList.filter(
            (item) => !(item.serialno == oldserialno && item.prevupdate == true),
          );
        }
        //ROTATE to empty position - disabled
        // else if (type == 'ROTATE' && !oldserialno) {
        //   updateTyreUpdateList = updateTyreUpdateList.filter(
        //     (item) => !(item.serialno == newserialno && item.prevupdate == true),
        //   );
        // }
        this.partTableData = this.partTableData.filter((item) => item.tyreSeqNo != seqno);
      } else {
        updateTyreUpdateList = updateTyreUpdateList.map((item) => {
          if (item.seqno == seqno)
            return {
              ...item,
              action: 'D',
            };
          return item;
        });

        const findPrevious = this.tyreConfigTyresList.findLast((item) => item.serialno == oldserialno);

        if (findPrevious) {
          const findFromUpdateList = this.tyreConfigTyresList?.findLast(
            (u) => u.tyreindex == findPrevious.tyreindex && u.inuse == '5',
          );

          updateTyreUpdateList.push({
            ...findPrevious,
            inuse: '1',
            action: 'E',
            revert: true,
            revertgrn: Boolean(findFromUpdateList),
          });
          updateTyreConfigList = updateTyreConfigList.map((item) => {
            if (item.seqno == findPrevious.seqno)
              return {
                ...item,
                inuse: '1',
                action: 'E',
              };
            return item;
          });
        }

        this.partTableData = this.partTableData.map((item) => {
          if (item.tyreSeqNo == seqno)
            return {
              ...item,
              action: 'D',
            };
          return item;
        });
      }

      if (type == 'ROTATE' && oldserialno) {
        if (action == 'N') {
          updateTyreUpdateList = updateTyreUpdateList.filter((item) => item.seqno != seqno + 1);
          this.partTableData = this.partTableData.filter((item) => item.tyreSeqNo != seqno + 1);
          updateTyreUpdateList = updateTyreUpdateList.filter(
            (item) => !(item.serialno == newserialno && item.prevupdate == true),
          );
        } else {
          updateTyreUpdateList = updateTyreUpdateList.map((item) => {
            if (item.seqno == seqno + 1)
              return {
                ...item,
                action: 'D',
              };
            return item;
          });

          const findPrevious = this.tyreConfigTyresList.findLast((item) => item.serialno == newserialno);
          if (findPrevious) {
            updateTyreUpdateList = [
              ...updateTyreUpdateList,
              { ...findPrevious, inuse: '1', action: 'E', revert: true },
            ];
            updateTyreConfigList = updateTyreConfigList.map((item) => {
              if (item.seqno == findPrevious.seqno)
                return {
                  ...item,
                  inuse: '1',
                  action: 'E',
                  revert: true,
                };
              return item;
            });
          }
          this.partTableData = this.partTableData.map((item) => {
            if (item.tyreSeqNo == seqno + 1)
              return {
                ...item,
                action: 'D',
              };
            return item;
          });
        }
      }

      if (type == 'OTHER') {
        if (action == 'N') {
          this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.filter(
            (tyre) => !(tyre.serialno == oldserialno && tyre.oldserialno == newserialno),
          );
          this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.filter(
            (tyre) => !(tyre.serialno == newserialno && tyre.action == 'E'),
          );
        } else {
          this.$fullscreenLoading('Deleting...');
          const vehicle = await this.getVehicleInfo(rotatedwith);
          const { TyreJob, VehicleCategory, Type } = vehicle || {};
          const tyreLayout = await this.getVehicleTyreLayout(VehicleCategory, Type);
          const tyreConfigList = this.tyreConfigToStructure(TyreJob, tyreLayout);

          const findUpdate = tyreConfigList.findLast(
            (item) => item.serialno == oldserialno && item.oldserialno == newserialno && type == 'OTHER',
          );

          this.tyreConfigUpdateOtherVehicle.push({ ...findUpdate, action: 'D' });

          const findPrev = tyreConfigList.findLast(
            (item) => item.serialno == findUpdate.oldserialno && item.tyreindex == findUpdate.tyreindex,
          );
          this.tyreConfigUpdateOtherVehicle.push({ ...findPrev, action: 'E', inuse: '1', revert: true });

          const otherJob = await this.getWorkshopJobDetails(otherjobno);
          const { Parts } = otherJob || {};
          const parts = this.convertToArray(Parts);
          const findPart = parts.find(
            (part) => part['Serial No'] == findUpdate.serialno && part.Replace == findUpdate.oldserialno,
          );
          if (findPart) {
            this.partUpdateOtherVehicle.push({
              ...findPart,
              partinventoryno: rotatedwith,
              jobno: findPart.JobNo,
              action: 'D',
            });
          }

          this.$fullscreenLoading();
        }
      }

      this.tyreConfigUpdateList = [];
      this.tyreConfigTyresList = [];
      this.$nextTick(() => {
        this.tyreConfigUpdateList = updateTyreUpdateList;
        this.tyreConfigTyresList = updateTyreConfigList;
      });

      this.tyreConfigLayoutSelectedItem = null;
      this.tyreConfigTableSelectedRow = [];
      this.tyrePositionSelectedValue = null;
    },

    cancelTyreConfigSerialNoCheck() {
      clearTimeout(this.tyreConfigSerialNoCheckTimer);
      this.tyreConfigSerialNoCheckTimer = null;
      this.tyreConfigSerialNoCheckRequestId++;
      this.tyreConfigSerialNoChecking = false;
      this.tyreConfigSerialNoCheckResult = null;
    },

    tyreConfigSerialNoChanged(value) {
      this.cancelTyreConfigSerialNoCheck();
      this.tyreConfigSerialNoValueError = '';
      const selectedRow = this.tyreConfigTableSelectedRow?.[0];
      const originalSerialNo = selectedRow?.['New Serial No'] ?? '';
      if (!value || this.viewMode || this.tyreReplaceValue?.keyValue != 'NEW' || value == originalSerialNo) return;
      const requestId = this.tyreConfigSerialNoCheckRequestId;
      this.tyreConfigSerialNoCheckTimer = setTimeout(async () => {
        this.tyreConfigSerialNoCheckTimer = null;
        if (!this.showPopup || !this.tyreConfigPopupShow || this.tyreReplaceValue?.keyValue != 'NEW' || this.tyreConfigSerialNoValue != value || this.tyreConfigTableSelectedRow?.[0] !== selectedRow) return;
        this.tyreConfigSerialNoChecking = true;
        try {
          let invalid = this.tyreConfigUpdateList.some((item) => item.serialno == value && item.serialno != originalSerialNo);
          if (!invalid) {
            const result = await this.$apiCallV2('/WorkshopJob/CheckTyreSerialNoValid', { serialno: value });
            invalid = result?.[0]?.Results != 0;
          }
          if (requestId === this.tyreConfigSerialNoCheckRequestId && this.tyreConfigSerialNoValue == value && this.tyreConfigTableSelectedRow?.[0] === selectedRow && this.tyreReplaceValue?.keyValue == 'NEW') {
            this.tyreConfigSerialNoValueError = invalid ? 'Serial No already exists' : '';
            this.tyreConfigSerialNoCheckResult = !invalid;
          }
        } catch (_error) {
          if (requestId === this.tyreConfigSerialNoCheckRequestId && this.tyreConfigSerialNoValue == value && this.tyreConfigTableSelectedRow?.[0] === selectedRow && this.tyreReplaceValue?.keyValue == 'NEW') {
            this.tyreConfigSerialNoValueError = 'Unable to check Serial No. Please try again.';
            this.tyreConfigSerialNoCheckResult = false;
          }
        } finally {
          if (requestId === this.tyreConfigSerialNoCheckRequestId) this.tyreConfigSerialNoChecking = false;
        }
      }, 350);
    },

    async tyreConfigPopupSave() {
      this.cancelTyreConfigSerialNoCheck();
      if (this.tyreConfigPartInfoOnlyEdit) {
        this.saveTyreConfigJobPartInfoOnly();
        return;
      }

      let error = false;
      if (!this.tyreStatusValue) {
        this.tyreStatusValueError = 'Current Tyre Status is required!';
        error = true;
      }
      if (this.tyreStatusValue.keyValue == '5' && !this.tyreStoreInValue) {
        this.tyreStoreInValueError = 'Warehouse is required!';
        error = true;
      }

      if (!this.tyreReplaceValue) {
        this.tyreReplaceValueError = 'Replace With is requried!';
        error = true;
      } else {
        const { keyValue: replace } = this.tyreReplaceValue || {};

        if (!this.tyreExistingValue && replace == 'WHSE') {
          this.tyreExistingValueError = 'Tyre is required!';
          error = true;
        }
        if (!this.tyreConfigSelectedVehicleDropdownValue && replace == 'ROTATE') {
          this.tyreConfigErrorText = 'Selected Tyre is required!';
          error = true;
        }

        if (replace == 'OTHER') {
          if (!this.tyreConfigOtherVehicleValue) {
            this.tyreConfigOtherVehicleValueError = 'Other Vehicle is required!';
            error = true;
          }

          if (!this.tyreConfigSelectedVehicleDropdownValue) {
            this.tyreConfigErrorText = 'Selected Tyre is required!';
            error = true;
          }

          if (!this.tyreRotateOtherVehicleWorkshopJobInputValue || !this.tyreRotateOtherVehicleWorkshopJobValue) {
            this.tyreRotateOtherVehicleWorkshopJobValueError = 'Workshop Job No. is required!';
            error = true;
          }
        }

        if (replace == 'NEW') {
          if (!this.tyreConfigTyreTypeValue) {
            this.tyreConfigTyreTypeValueError = 'Tyre Type is requried!';
            error = true;
          }

          if (!this.tyreConfigSerialNoValue) {
            this.tyreConfigSerialNoValueError = 'Serial No is required!';
            error = true;
          } else if (this.tyreConfigSerialNoValue) {
            const newserialno = this.tyreConfigTableSelectedRow?.[0]?.['New Serial No'] ?? '';
            const noChange = newserialno == this.tyreConfigSerialNoValue;

            error = await (async () => {
              if (newserialno && noChange) {
                return false;
              }

              const findExist = this.tyreConfigUpdateList.find(
                (item) => item.serialno == this.tyreConfigSerialNoValue && item.serialno != newserialno,
              );
              if (findExist) return true;

              this.$fullscreenLoading('Checking Serial No...');
              const checkDB = await this.$apiCallV2('/WorkshopJob/CheckTyreSerialNoValid', {
                serialno: this.tyreConfigSerialNoValue,
              });
              this.$fullscreenLoading();
              if (checkDB[0].Results != 0) return true;

              return false;
            })();

            if (error) {
              this.tyreConfigSerialNoValueError = 'Serial No already exists';
              this.tyreConfigSerialNoCheckResult = false;
            }
          }
        }

        if (!error) {
          if (!this.tyreConfigTyreTypeValue) {
            this.tyreConfigTyreTypeValueError = 'Tyre Type is requried!';
            error = true;
          }

          if (!this.tyreConfigSerialNoValue) {
            this.tyreConfigSerialNoValueError = 'Serial No is required!';
            error = true;
          }
        }
      }

      if (error) return;

      let updateTyres = this.tyreConfigUpdateList || [];
      let updateParts = this.partTableData || [];

      const {
        seqno: existSeqno,
        'Old Serial No': oldserialnoTableSelected,
        'New Serial No': newserialnoTableSelected,
        'Date Changed': datechangedSelected,
        tyreindex: tyreIndexTableSelected,
      } = this.tyreConfigTableSelectedRow[0] || {};

      //clean up for previous entry
      if (existSeqno) {
        const findUpdateItem = this.tyreConfigUpdateList.find((item) => item.seqno == existSeqno);
        const { type, serialno, oldserialno } = findUpdateItem || {};
        if (this.tyreReplaceValue.keyValue != type) {
          if (type == 'ROTATE') {
            const findPair =
              updateTyres.find(
                (item) => item.serialno == oldserialno && item.oldserialno == serialno && item.type == 'ROTATE',
              ) || {};
            if (findPair.tyreguid) {
              updateTyres = updateTyres.map((item) => {
                if (item.seqno == findPair.seqno) {
                  return {
                    ...item,
                    action: 'D',
                  };
                }
              });

              const findLast = this.tyreConfigTyresList.findLast(
                (item) => item.serialno == findPair.oldserialno && item.inuse != '1',
              );
              updateTyres.push({ ...findLast, action: 'E', inuse: '1' });
            } else {
              updateTyres = updateTyres.filter(
                (item) => item.seqno != findPair.seqno && !(item.serialno == serialno && item.prevupdate),
              );
            }

            const findPart = updateParts.find((item) => findPair.seqno == item.tyreSeqNo);

            if (findPart) {
              if (findPart.action == 'N') {
                updateParts = updateParts.filter((item) => item.tyreSeqNo != findPart.tyreSeqNo);
              } else
                updateParts = updateParts.map((item) => {
                  if (item.tyreSeqNo == findPart.tyreSeqNo) {
                    return {
                      ...item,
                      action: 'D',
                    };
                  }
                  return item;
                });
            }
          }
        }
      }

      // end cleanup

      let selectedTyrePosition = this.tyrePositionSelectedValue || {};
      if (tyreIndexTableSelected) {
        selectedTyrePosition = this.tyreConfigLayout.find((item) => item.index == tyreIndexTableSelected);
      }
      const { tyreInfo, index: tyreIndex, description: positionDescription } = selectedTyrePosition || {};
      const { action: selectedTyreAction, tyretype: selectedPartType } = tyreInfo || {};
      const action = this.tyreConfigTableSelectedRow[0] && this.tyreConfigTableSelectedRow[0].action != 'N' ? 'E' : 'N';

      const { keyValue: inUse } = this.tyreStatusValue;

      //Tyre to add
      const { keyValue: replaceTypeKey } = this.tyreReplaceValue;
      const isRotation = replaceTypeKey == 'ROTATE' || replaceTypeKey == 'OTHER';
      const { align, tyrePosition, rowNo: rowno, columnNo: columnno } = this.tyrePositionInfo(tyreIndex);
      const { 'Warehouse Code': storeInWarehouse } = this.tyreStoreInValue || {};
      const { 'Inventory #': inventoryNo } = this.jobInventoryValue;
      const tyreVehicleMaxSeqNo = Math.max(0, ...this.tyreConfigTyresList.map((item) => Number(item.seqno) || 0)) + 1;
      const tyreUpdatemaxSeqno = Math.max(0, ...this.tyreConfigUpdateList.map((item) => Number(item.seqno) || 0)) + 1;
      const tyreMaxSeqNo = Math.max(tyreUpdatemaxSeqno, tyreVehicleMaxSeqNo);

      const { 'Part Type': partType, ProdTxnID } = this.tyreConfigTyreTypeValue || {};

      const tyreConfigToAdd = {
        type: replaceTypeKey,
        serialno: this.tyreConfigSerialNoValue ?? '',
        oldserialno: this.tyreConfigOldSerialNoValue ?? '',
        tyreposition: positionDescription || tyrePosition,
        storein: inUse == '5' ? storeInWarehouse : '',
        inventoryno: inventoryNo,
        tyresize: this.tyreConfigSizeValue ?? '',
        tyreindex: tyreIndex,
        repair: 'Y',
        inuse: '1',
        datechange: datechangedSelected ?? this.jobDateDate,
        align,
        rowno,
        columnno,
        tyretype: partType ?? '',
        prodtxnid: ProdTxnID ?? '',
        action: selectedTyreAction == 'N' ? 'N' : action,

        partno: this.tyreConfigPartNoValue ?? '',
        mfgdate: this.tyreConfigMfgDateValue?.formatString('yyyy-MM-dd') ?? null,
        datepurchase: this.tyreConfigDatePurchaseValue?.formatString('yyyy-MM-ddTHH:mm:ss') ?? null,
        noofretread: this.tyreExistingValue?.NoOfRetread || '0',
        whsecode: this.tyreConfigTakenFromValue?.['Warehouse Code'] ?? this.tyreConfigTakenFromInputValue ?? '',
        model: this.tyreConfigModelValue ?? '',
        madein: this.tyreConfigMadeInValue?.['Country Code'] ?? '',
        manufacturer: this.tyreConfigManufacturerValue?.MFGCode ?? '',
        unitprice: isRotation ? '0' : this.tyreConfigJobPriceValue ?? '0',
        sppcode: this.tyreConfigSupplierValue?.['Supplier Code'] ?? '',

        otherjobno:
          replaceTypeKey == 'OTHER' ?
            this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #'] || this.tyreRotateOtherVehicleWorkshopJobInputValue
          : '',
        otherinventoryno: replaceTypeKey == 'OTHER' ? this.tyreConfigOtherVehicleValue['Inventory #'] : '',

        jobno: this.jobNoValue,
      };

      if (!this.tyreConfigRotateOtherDisabled && !this.tyreConfigRotateOtherWorkshopJobIsNotLatest) {
        if (existSeqno) {
          updateTyres = updateTyres?.map((item) => {
            if (item?.seqno == existSeqno) {
              return {
                ...item,
                ...tyreConfigToAdd,
                action: action == 'N' ? item.action : 'E',
              };
            }
            return item;
          });
        } else updateTyres.push({ ...tyreConfigToAdd, seqno: tyreMaxSeqNo });

        //Update previous tyre
        if (tyreInfo) {
          updateTyres = updateTyres.filter((item) => !(item.serialno == oldserialnoTableSelected && item.prevupdate));
          const {
            align: alignPrevTyre,
            tyrePosition: tyrePositionPrevTyre,
            rowNo: rownoPrevTyre,
            columnNo: columnnoPrevTyre,
          } = this.tyrePositionInfo(tyreInfo.tyreindex);

          const findFromUpdateList = this.tyreConfigTyresList?.findLast(
            (u) => u.tyreindex == tyreInfo.tyreindex && u.inuse == '5',
          );

          const { ProdTxnID: TyreTypeProdTxnID, tyreposition } = tyreInfo || {};

          let tyreTypeFind = null;
          if (TyreTypeProdTxnID) {
            tyreTypeFind = this.tyreTypeList.find((item) => item.ProdTxnID == TyreTypeProdTxnID);
          } else {
            tyreTypeFind = this.tyreTypeList.find((item) => item['Product Code'] == selectedPartType);
          }

          updateTyres.push({
            ...tyreInfo,
            align: alignPrevTyre,
            tyreposition: positionDescription || tyreposition || tyrePositionPrevTyre,
            rowno: rownoPrevTyre,
            columnno: columnnoPrevTyre,
            inuse: inUse,
            action: 'E',
            tyretype: tyreTypeFind?.['Product Code'] ?? partType,
            prodtxnid: tyreTypeFind?.ProdTxnID ?? ProdTxnID,
            prevupdate: true,
            revertgrn: Boolean(findFromUpdateList),
            storein: inUse == '5' ? storeInWarehouse : '',
          });
        }
      }

      // DISABLED - this was to enable taking tyres from the same vehicle to empty slots
      // A bit too buggy to enable for. Will explore when needed
      // The bugs are related to 'Old Serial No' not being present when you try to edit.
      // Some of the code that relies on old serial no will break, so need special cases for it

      // else if (replaceTypeKey == 'ROTATE' && !tyreInfo) {
      //   if (action == 'N') {
      //     updateTyres = updateTyres.filter(
      //       (item) => item.serialno != this.tyreConfigTableSelectedRow?.[0]?.['New Serial No'],
      //     );
      //   } else if (action == 'E') {
      //     if (
      //       this.tyreConfigSelectedVehicleDropdownValue.tyreInfo?.serialno !=
      //       this.tyreConfigTableSelectedRow?.[0]?.['New Serial No']
      //     ) {
      //       const findPrev = this.tyreConfigTyresList.findLast(
      //         (item) => item.serialno == this.tyreConfigTableSelectedRow?.[0]?.['New Serial No'],
      //       );
      //       updateTyres.push({
      //         ...findPrev,
      //         inuse: '1',
      //         action: 'E',
      //       });
      //     }
      //   }

      //   updateTyres.push({
      //     ...this.tyreConfigSelectedVehicleDropdownValue?.tyreInfo,
      //     inuse: inUse,
      //     action: 'E',
      //     prevupdate: true,
      //   });
      // }

      const partMaxSeqNo = Math.max(0, ...updateParts.map((item) => Number(item.seqNo) || 0)) + 1;

      const partToAdd = {
        Type: this.tyreConfigTyreTypeValue?.['Part Type'] ?? '',
        'Serial No': this.tyreConfigSerialNoValue ?? '',
        'Part No': this.tyreConfigPartNoValue ?? '',
        Manufacturer: this.tyreConfigManufacturerValue?.MFGCode ?? '',
        'Made In': this.tyreConfigMadeInValue?.['Country Code'] ?? '',
        Size: this.tyreConfigSizeValue ?? '',
        'Cost (RM)': isRotation ? '0' : this.tyreConfigJobPriceValue ?? '0',
        Replace: this.tyreConfigOldSerialNoValue ?? '',
        'Purchase From': this.tyreConfigSupplierValue?.['Supplier Code'] ?? '',
        'Date Purchase':
          this.tyreConfigDatePurchaseValue ?
            new Date(this.tyreConfigDatePurchaseValue.formatString('yyyy-MM-ddTHH:mm:ss'))
          : null,
        'Insurance Claim No': this.tyreConfigInsuranceNoValue ?? '',
        'Invoice No': this.tyreConfigInvoiceNoValue ?? '',
        'Taken From': this.tyreConfigTakenFromValue?.['Warehouse Code'] ?? this.tyreConfigTakenFromInputValue ?? '',
        Remark: this.tyreConfigRemarksValue,
        PONo: this.tyreConfigPONoValue ?? '',
        UserID: '',
        'Date Updated': '',
        MfgDate: this.tyreConfigMfgDateValue ? new Date(this.tyreConfigMfgDateValue).formatString('yyyy-MM-dd') : null,
        Model: this.tyreConfigModelValue ?? '',
        Quantity: '1',

        partTypeDesc: this.tyreConfigTyreTypeValue?.Description ?? '',

        action: action,
        expenseCode: this.tyreConfigTyreTypeValue?.['Expense Code'] || 'EMS',
        isExpense: isRotation ? 'N' : this.tyreConfigTyreTypeValue?.isExpense ?? this.tyreConfigTyreTypeValue?.IsExpenses,

        prodTxnID: this.tyreConfigTyreTypeValue?.ProdTxnID,
        AllocGUID: this.tyreExistingValue?.AllocGUID ?? '',
        BinLoc: this.tyreExistingValue?.BinLoc ?? '',

        TyreIndex: tyreIndex,
      };

      if (existSeqno) {
        updateParts = updateParts.map((item) => {
          if (item.tyreSeqNo == existSeqno) {
            return {
              ...item,
              ...partToAdd,
            };
          }
          return item;
        });
      } else updateParts.push({ ...partToAdd, tyreSeqNo: tyreMaxSeqNo, seqNo: partMaxSeqNo });

      //tyreRotate
      if (replaceTypeKey == 'ROTATE' && tyreInfo) {
        const { tyreInfo: tyreInfoRotatedPair, description: tyreRotatePositionDescription } =
          this.tyreConfigSelectedVehicleDropdownValue || {};
        const {
          tyreposition,
          tyreindex,
          tyresize,
          prodtxnid,
          partno,
          mfgdate,
          datepurchase,
          noofretread,
          whsecode,
          tyretype,
          model,
          madein,
          manufacturer,
          supplier,
        } = tyreInfoRotatedPair || {};
        const {
          align: alignRotate,
          tyrePosition: tyrePositionRotate,
          rowNo: rowNoRotate,
          columnNo: columnNoRotate,
        } = this.tyrePositionInfo(tyreindex);
        const tyreTypeFind = this.tyreTypeList.find((item) => item['Product Code'] == tyretype);

        const tyreConfigRotated = {
          type: 'ROTATE',
          serialno: this.tyreConfigOldSerialNoValue ?? '',
          oldserialno: this.tyreConfigSerialNoValue ?? '',
          tyreposition: tyreRotatePositionDescription || tyrePositionRotate || tyreposition,
          storein: '',
          inventoryno: inventoryNo,
          tyresize: tyresize ?? this.tyreConfigSizeValue ?? '',
          tyreindex: tyreindex,
          repair: 'Y',
          inuse: '1',
          datechange: datechangedSelected ?? this.jobDateDate,
          align: alignRotate,
          rowno: rowNoRotate,
          columnno: columnNoRotate,
          tyretype: tyretype || partType,
          prodtxnid: prodtxnid || (tyreTypeFind ? tyreTypeFind.ProdTxnID : ProdTxnID),
          action: selectedTyreAction == 'N' ? 'N' : action,

          partno: partno ?? '',
          mfgdate: mfgdate ? new Date(mfgdate) : null,
          datepurchase: datepurchase ? new Date(datepurchase) : null,
          noofretread: noofretread || '0',
          whsecode: whsecode ?? '',
          model: model ?? '',
          madein: madein ?? '',
          manufacturer: manufacturer ?? '',
          unitprice: '0',
          sppcode: supplier ?? '',

          jobno: this.jobNoValue,
        };

        const seqNoRotate = parseInt(existSeqno ?? tyreMaxSeqNo) + 1;

        if (!this.tyreConfigRotateOtherDisabled && !this.tyreConfigRotateOtherWorkshopJobIsNotLatest) {
          if (this.tyreConfigUpdateList.find((item) => item.seqno == seqNoRotate)) {
            updateTyres = updateTyres.map((item) => {
              if (item.seqno == seqNoRotate) {
                return { ...item, ...tyreConfigRotated, seqno: seqNoRotate };
              }
              return item;
            });
          } else {
            updateTyres.push({ ...tyreConfigRotated, seqno: seqNoRotate });
          }

          if (action == 'N') {
            //prev tyre clear
            updateTyres = updateTyres.filter((item) => !(item.serialno == newserialnoTableSelected && item.prevupdate));

            const {
              align: alignPrevTyre,
              tyrePosition: tyrePositionPrevTyre,
              rowNo: rownoPrevTyre,
              columnNo: columnnoPrevTyre,
            } = this.tyrePositionInfo(tyreInfoRotatedPair.tyreindex);

            //prev Tyre add
            updateTyres.push({
              ...tyreInfoRotatedPair,
              tyretype: tyreInfoRotatedPair.tyretype || partType,
              align: alignPrevTyre,
              tyreposition: tyreRotatePositionDescription || tyrePositionPrevTyre,
              rowno: rownoPrevTyre,
              columnno: columnnoPrevTyre,
              action: 'E',
              inuse: '0',
              prevupdate: true,
            });
          } else if (action == 'E') {
            if (newserialnoTableSelected != this.tyreConfigSerialNoValue) {
              const findPrev = this.tyreConfigTyresList.findLast((item) => item.serialno == newserialnoTableSelected);
              if (findPrev) {
                const {
                  align: alignPrevTyre,
                  tyrePosition: tyrePositionPrevTyre,
                  rowNo: rownoPrevTyre,
                  columnNo: columnnoPrevTyre,
                } = this.tyrePositionInfo(findPrev.tyreindex);

                updateTyres.push({
                  ...findPrev,
                  align: alignPrevTyre,
                  tyreposition: tyreRotatePositionDescription || tyrePositionPrevTyre,
                  rowno: rownoPrevTyre,
                  columnno: columnnoPrevTyre,
                  action: 'E',
                  inuse: '1',
                  prevupdate: true,
                  revert: true,
                });
              }

              const {
                align: alignPrevTyre,
                tyrePosition: tyrePositionPrevTyre,
                rowNo: rownoPrevTyre,
                columnNo: columnnoPrevTyre,
              } = this.tyrePositionInfo(tyreInfoRotatedPair.tyreindex);

              //prev Tyre add
              updateTyres.push({
                ...tyreInfoRotatedPair,
                tyretype: tyreInfoRotatedPair.tyretype || partType,
                align: alignPrevTyre,
                tyreposition: tyrePositionPrevTyre,
                rowno: rownoPrevTyre,
                columnno: columnnoPrevTyre,
                action: 'E',
                inuse: '0',
                prevupdate: true,
              });
            }
          }
        }

        const partRotateToAdd = {
          Type: this.tyreConfigTyreTypeValue?.['Part Type'] ?? '',
          'Serial No': this.tyreConfigOldSerialNoValue ?? '',
          'Part No': partno ?? '',
          Manufacturer: manufacturer ?? '',
          'Made In': madein ?? '',
          Size: tyresize ?? this.tyreConfigSizeValue ?? '',
          'Cost (RM)': '0',
          Replace: this.tyreConfigSerialNoValue ?? '',
          'Purchase From': supplier ?? '',
          'Date Purchase': datepurchase ? new Date(datepurchase).formatString('yyyy-MM-ddTHH:mm:ss') : null,
          'Insurance Claim No': this.tyreConfigInsuranceNoValue ?? '',
          'Invoice No': this.tyreConfigInvoiceNoValue ?? '',
          'Taken From': whsecode ?? '',
          Remark: this.tyreConfigRemarksValue,
          PONo: this.tyreConfigPONoValue ?? '',
          UserID: '',
          'Date Updated': '',
          MfgDate: mfgdate ? new Date(mfgdate).formatString('yyyy-MM-dd') : null,
          Model: model ?? '',
          Quantity: '1',

          partTypeDesc: this.tyreConfigTyreTypeValue?.Description ?? '',

          action: action,
          expenseCode: this.tyreConfigTyreTypeValue?.['Expense Code'] || 'EMS',
          isExpense: 'N',

          prodTxnID: this.tyreConfigTyreTypeValue?.ProdTxnID,
          TyreIndex: tyreindex,
        };

        if (existSeqno) {
          updateParts = updateParts.map((item) => {
            if (item.tyreSeqNo == seqNoRotate) {
              return {
                ...item,
                ...partRotateToAdd,
              };
            }
            return item;
          });
        } else updateParts.push({ ...partRotateToAdd, tyreSeqNo: seqNoRotate, seqNo: partMaxSeqNo + 1 });
      }

      if (replaceTypeKey == 'OTHER') {
        const { tyreInfo: tyreInfoRotatedPair } = this.tyreConfigSelectedVehicleDropdownValue || {};
        const {
          tyreposition,
          tyreindex,
          tyresize,
          prodtxnid,
          partno,
          mfgdate,
          datepurchase,
          noofretread,
          whsecode,
          tyretype,
          model,
          madein,
          manufacturer,
          supplier,
          inventoryno,
        } = tyreInfoRotatedPair || {};
        const {
          align: alignRotate,
          tyrePosition: tyrePositionRotate,
          rowNo: rowNoRotate,
          columnNo: columnNoRotate,
        } = this.tyrePositionInfo(tyreindex);
        const tyreConfigToAdd = {
          type: 'OTHER',
          serialno: this.tyreConfigOldSerialNoValue ?? '',
          oldserialno: this.tyreConfigSerialNoValue ?? '',
          tyreposition: tyrePositionRotate || tyreposition,
          storein: '',
          inventoryno: inventoryno,
          tyresize: tyresize ?? this.tyreConfigSizeValue ?? '',
          tyreindex: tyreindex,
          repair: 'Y',
          inuse: '1',
          datechange: datechangedSelected ?? this.jobDateDate,
          align: alignRotate,
          rowno: rowNoRotate,
          columnno: columnNoRotate,
          tyretype: tyretype || partType || '',
          prodtxnid: prodtxnid ?? ProdTxnID ?? '',
          action: selectedTyreAction == 'N' ? 'N' : action,

          partno: partno ?? '',
          mfgdate: mfgdate ? new Date(mfgdate).formatString('yyyy-MM-dd') : null,
          datepurchase: datepurchase ? new Date(datepurchase).formatString('yyyy-MM-ddTHH:mm:ss') : null,
          noofretread: noofretread || '0',
          whsecode: whsecode ?? '',
          model: model ?? '',
          madein: madein ?? '',
          manufacturer: manufacturer ?? '',
          unitprice: '0',
          sppcode: supplier ?? '',

          jobno: this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #'] ?? '',
        };

        const targetJobNo = this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #'] ?? '';
        const findMaxSeqNoOther = Math.max(
          ...this.tyreConfigUpdateOtherVehicle
            .filter((item) => item.jobno == targetJobNo)
            .map((item) => item.seqno ?? 0),
        );
        const maxSeqNoFromExisting = Math.max(
          ...this.tyreConfigSelectedVehichleTyreConfigFull.map((x) => x.seqno ?? 0),
        );

        const tyreMaxSeqNoOther = Math.max(findMaxSeqNoOther + 1, maxSeqNoFromExisting + 1);

        if (action == 'N') {
          const findRotated = this.tyreConfigUpdateOtherVehicle.find(
            (item) => item.serialno == this.tyreConfigOldSerialNoValue && item.type == 'OTHER',
          );
          if (findRotated) {
            this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.map((item) => {
              if (item.seqno == findRotated.seqno && item.inventoryno == findRotated.inventoryno) {
                return {
                  ...item,
                  ...tyreConfigToAdd,
                };
              }
              return item;
            });
            this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.filter(
              (item) =>
                !(
                  item.serialno == findRotated.oldserialno &&
                  item.inventoryno == findRotated.inventoryno &&
                  item.prevupdate
                ),
            );
          } else {
            this.tyreConfigUpdateOtherVehicle.push({ ...tyreConfigToAdd, seqno: tyreMaxSeqNoOther });
          }

          this.tyreConfigUpdateOtherVehicle.push({
            ...tyreInfoRotatedPair,
            tyretype: tyreInfoRotatedPair.tyretype || partType,
            action: 'E',
            inuse: '0',
            seqno: tyreMaxSeqNoOther,
            prevupdate: true,
          });
        }

        //DISABLED edit existing configs
        // else {
        //   const tyreConfigList = this.tyreConfigSelectedVehichleTyreConfigFull;

        //   const findExistUpdateOther = this.tyreConfigUpdateOtherVehicle.findLast(
        //     (item) =>
        //       item.serialno == oldserialnoTableSelected &&
        //       item.oldserialno == newserialnoTableSelected &&
        //       item.type == 'OTHER' &&
        //       item.action == 'E',
        //   );

        //   if (findExistUpdateOther) {
        //     this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.map((item) => {
        //       if (item.tyreguid == findExistUpdateOther.tyreguid) {
        //         return {
        //           ...item,
        //           ...tyreConfigRotated,
        //         };
        //       }
        //       return item;
        //     });

        //     this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.filter(
        //       (item) => !(item.serialno == newserialnoTableSelected && item.prevupdate),
        //     );

        //     const findPrev = tyreConfigList.findLast(
        //       (item) => item.serialno == this.tyreConfigSerialNoValue && item.tyreindex == tyreConfigRotated.tyreindex,
        //     );
        //     this.tyreConfigUpdateOtherVehicle.push({
        //       ...findPrev,
        //       inuse: '1',
        //       action: 'E',
        //       prevupdate: true,
        //     });
        //   } else {
        //     const findUpdateOther = tyreConfigList.findLast(
        //       (item) =>
        //         item.serialno == oldserialnoTableSelected &&
        //         item.oldserialno == newserialnoTableSelected &&
        //         item.type == 'OTHER',
        //     );

        //     this.tyreConfigUpdateOtherVehicle.push({
        //       ...findUpdateOther,
        //       ...tyreConfigRotated,
        //       action: 'E',
        //     });

        //     if (newserialnoTableSelected != this.tyreConfigSerialNoValue) {
        //       const findPrev = tyreConfigList.findLast(
        //         (item) => item.serialno == newserialnoTableSelected && item.tyreindex == findUpdateOther.tyreindex,
        //       );

        //       this.tyreConfigUpdateOtherVehicle.push({
        //         ...findPrev,
        //         inuse: '1',
        //         action: 'E',
        //         revert: true,
        //       });

        //       const revertSelected = tyreConfigList.findLast(
        //         (item) =>
        //           item.serialno == this.tyreConfigSerialNoValue && item.tyreindex == tyreConfigRotated.tyreindex,
        //       );

        //       this.tyreConfigUpdateOtherVehicle.push({
        //         ...revertSelected,
        //         inuse: '0',
        //         action: 'E',
        //         prevupdate: true,
        //       });
        //     }
        //   }
        // }

        const partRotateToAdd = {
          Type: this.tyreConfigTyreTypeValue?.['Part Type'] ?? '',
          'Serial No': this.tyreConfigOldSerialNoValue ?? '',
          'Part No': partno ?? '',
          Manufacturer: manufacturer ?? '',
          'Made In': madein ?? '',
          Size: tyresize ?? this.tyreConfigSizeValue ?? '',
          'Cost (RM)': '0',
          Replace: this.tyreConfigSerialNoValue ?? '',
          'Purchase From': supplier ?? '',
          'Date Purchase': datepurchase ? new Date(datepurchase).formatString('yyyy-MM-ddTHH:mm:ss') : null,
          'Insurance Claim No': this.tyreConfigInsuranceNoValue ?? '',
          'Invoice No': this.tyreConfigInvoiceNoValue ?? '',
          'Taken From': whsecode ?? '',
          Remark: this.tyreConfigRemarksValue,
          PONo: this.tyreConfigPONoValue ?? '',
          UserID: '',
          'Date Updated': '',
          MfgDate: mfgdate ? new Date(mfgdate).formatString('yyyy-MM-dd') : null,
          Model: model ?? '',
          Quantity: '1',

          partTypeDesc: this.tyreConfigTyreTypeValue?.Description ?? '',

          action: action,
          expenseCode: this.tyreConfigTyreTypeValue?.['Expense Code'] || 'EMS',
          isExpense: 'N',

          prodTxnID: this.tyreConfigTyreTypeValue?.ProdTxnID,
          TyreIndex: tyreindex,

          partinventoryno: inventoryno,
          jobno:
            this.tyreRotateOtherVehicleWorkshopJobValue?.['Job #'] || this.tyreRotateOtherVehicleWorkshopJobInputValue,
        };

        if (action == 'N') {
          const findExist = this.partUpdateOtherVehicle.find(
            (item) => item['Serial No'] == oldserialnoTableSelected && item.Replace == newserialnoTableSelected,
          );
          if (findExist) {
            this.partUpdateOtherVehicle = this.partUpdateOtherVehicle.map((item) => {
              if (item.tyreSeqNo == findExist.tyreSeqNo) {
                return {
                  ...item,
                  ...partRotateToAdd,
                };
              }
              return item;
            });
          } else
            this.partUpdateOtherVehicle.push({
              ...partRotateToAdd,
              action: 'N',
              tyreSeqNo: tyreMaxSeqNoOther,
            });
        }

        const otherJobNo =
          this.tyreRotateOtherVehicleWorkshopJobValue ?
            this.tyreRotateOtherVehicleWorkshopJobValue['Job #']
          : this.tyreRotateOtherVehicleWorkshopJobInputValue;

        if (!this.tyreConfigRotateOtherDisabled && !this.tyreConfigRotateOtherWorkshopJobIsNotLatest) {
          if (action == 'N') {
            updateTyres = updateTyres.map((item) => {
              if (item.otherinventoryno == tyreConfigToAdd.inventoryno && item.type == 'OTHER') {
                return {
                  ...item,
                  otherjobno: otherJobNo,
                };
              }
              return item;
            });
            this.tyreConfigUpdateOtherVehicle = this.tyreConfigUpdateOtherVehicle.map((item) => {
              if (item.inventoryno == tyreConfigToAdd.inventoryno && item.type == 'OTHER' && item.action == 'N') {
                return {
                  ...item,
                  jobno: otherJobNo,
                };
              }
              return item;
            });

            this.partUpdateOtherVehicle = this.partUpdateOtherVehicle.map((item) => {
              if (item.partinventoryno == tyreConfigToAdd.inventoryno && item.action == 'N') {
                return {
                  ...item,
                  jobno: otherJobNo,
                };
              }
              return item;
            });
          }
        }
      }

      this.tyreConfigUpdateList = [];
      this.updateParts = [];
      this.$nextTick(() => {
        this.tyreConfigUpdateList = this.reorderTableNo(updateTyres);
        this.partTableData = this.reorderTableNo(updateParts);
      });

      this.tyreConfigLayoutSelectedItem = null;
      this.tyrePositionSelectedValue = null;
      this.tyreConfigTableSelectedRow = [];

      this.tyreConfigPopupShow = false;
    },

    saveTyreConfigJobPartInfoOnly() {
      const { seqno } = this.tyreConfigTableSelectedRow[0] || {};
      if (!seqno) return;
      const rotationTyre = this.tyreConfigUpdateList.find((item) => item.seqno == seqno);
      const isRotation = rotationTyre?.type == 'ROTATE' || rotationTyre?.type == 'OTHER';

      const updateParts = this.partTableData.map((part) => {
        if (part.tyreSeqNo != seqno) return part;

        const quantity = this.parsePartAmount(part.Quantity) || 1;
        const unitPrice = isRotation ? 0 : this.parsePartAmount(this.tyreConfigJobPriceValue);
        return {
          ...part,
          'Cost (RM)': unitPrice * quantity,
          'Insurance Claim No': this.tyreConfigInsuranceNoValue ?? '',
          'Invoice No': this.tyreConfigInvoiceNoValue ?? '',
          Remark: this.tyreConfigRemarksValue,
          isExpense: isRotation ? 'N' : part.isExpense,
          action: part.action ?? 'E',
        };
      });

      const updateTyres = this.tyreConfigUpdateList.map((tyre) => {
        if (tyre.seqno != seqno) return tyre;

        return {
          ...tyre,
          unitprice: isRotation ? '0' : this.tyreConfigJobPriceValue ?? '0',
        };
      });

      this.tyreConfigUpdateList = [];
      this.partTableData = [];
      this.$nextTick(() => {
        this.tyreConfigUpdateList = this.reorderTableNo(updateTyres);
        this.partTableData = this.reorderTableNo(updateParts);
      });

      this.tyreConfigLayoutSelectedItem = null;
      this.tyrePositionSelectedValue = null;
      this.tyreConfigTableSelectedRow = [];
      this.tyreConfigPopupShow = false;
    },
    //#endregion

    //#region menu actions
    newDocClick() {
      this.clearTabsFields();
      this.docFields.docDateReceivedValue = new Date();
      this.docFieldsChangedCheck = structuredClone(this.docFields);
      this.docSelectedItem = {};
      this.tabFieldsAction = 'N';
    },

    openTaskAdd() {
      if (!this.jobInventoryValue || this.viewMode || this.taskSelectedItem) return;
      this.taskAddGroupAction = 'N';
      this.taskAddOriginalRows = [];
      this.taskAddServiceValue = null;
      this.taskAddServiceError = '';
      this.taskAddServiceType = '';
      this.taskAddRows = [];
      this.taskAddSelectedRows = [];
      this.taskAddRowId = 0;
      this.taskAddEditorOpen = false;
      this.taskTableSelectedRows = [];
      this.taskAddShow = true;
    },
    openTaskGroupEdit() {
      if (!this.jobInventoryValue || this.viewMode || this.taskSelectedItem) return;
      const selectedTask = this.taskTableSelectedRows[0];
      if (!selectedTask) {
        this.$showAlert('error', 'No Service Selected', 'Select a task in the service group to edit its tasks.', [{ text: 'OK' }]);
        return;
      }

      const serviceCode = cleanText(selectedTask['Service Code']);
      const groupRows = this.taskTableData.filter((task) =>
        task.action != 'D' && cleanText(task['Service Code']) == serviceCode)
        .sort((a, b) => Number(a.seqNo) - Number(b.seqNo));
      if (!groupRows.length) return;

      const service = this.taskServicesList.find((item) => cleanText(item.ServiceCode) == serviceCode) || {
        ServiceCode: selectedTask['Service Code'],
        Description: selectedTask['Service Description'],
        label: `${selectedTask['Service Code']} - ${selectedTask['Service Description'] || ''}`,
      };
      this.taskAddGroupAction = 'E';
      this.taskAddOriginalRows = groupRows.map((task) => ({ ...task }));
      this.taskAddServiceValue = service;
      this.taskAddServiceError = '';
      this.taskAddServiceType = this.getTaskServiceType(service);
      this.taskAddRows = groupRows.map((task, index) => ({
        ...task,
        _rowid: `task-add-existing-${task.seqNo ?? index + 1}`,
      }));
      const editRow = this.taskAddRows.find((task) => task.seqNo == selectedTask.seqNo);
      this.taskAddSelectedRows = editRow ? [editRow] : [];
      this.taskAddRowId = 0;
      this.startTaskEdit(editRow);
      this.taskAddShow = true;
    },
    taskAddServiceAsTaskRow(service = this.taskAddServiceValue) {
      return {
        _rowid: `task-add-${++this.taskAddRowId}`,
        'Service Code': service.ServiceCode,
        'Service Description': service.Description,
        'Task Code': service.ServiceCode,
        'Task Description': service.Description,
        'Service Person': '',
        'Date Start': null,
        'Date End': null,
        Remark: '',
        action: 'N',
      };
    },
    getTaskServiceType(service) {
      return service?.Type == null ? '' : String(service.Type).trim();
    },
    taskServiceSelected(service) {
      this.taskServiceType = this.getTaskServiceType(service);
      this.taskServiceTaskError = '';
    },
    taskAddServiceSelected(service) {
      if (this.taskAddGroupAction != 'E') {
        this.taskAddRows = [];
        this.taskAddOriginalRows = [];
        this.taskAddSelectedRows = [];
        this.taskAddEditorOpen = false;
        this.taskAddRowId = 0;
      }
      this.taskAddServiceError = '';
      this.taskAddServiceType = this.getTaskServiceType(service);
    },
    startTaskAdd() {
      this.taskAddSelectedRows = [];
      this.taskAddEditorAction = 'N';
      this.taskAddEditorRowId = null;
      this.taskAddTaskValue = null;
      this.taskAddTaskError = '';
      this.taskAddPersonValue = '';
      this.taskAddDateStartValue = null;
      this.taskAddDateEndValue = null;
      this.taskAddRemarkValue = '';
      this.taskAddEditorOpen = true;
    },
    startTaskEdit(row = this.taskAddSelectedRows[0]) {
      if (!row) return;
      this.taskAddEditorAction = 'E';
      this.taskAddEditorRowId = row._rowid;
      this.taskAddTaskValue = this.taskServicesTaskList.find((task) => task.ServiceCode == row['Task Code']) || {
        ServiceCode: row['Task Code'], Description: row['Task Description'],
        label: `${row['Task Code']} - ${row['Task Description'] || ''}`,
      };
      this.taskAddTaskError = '';
      this.taskAddPersonValue = row['Service Person'];
      this.taskAddDateStartValue = row['Date Start'];
      this.taskAddDateEndValue = row['Date End'];
      this.taskAddRemarkValue = row.Remark;
      this.taskAddEditorOpen = true;
    },
    taskAddTableRowClicked(_buttonText, row) {
      if (this.taskAddEditorOpen) {
        this.$showAlert('error', 'Task Is Being Edited', 'Save or cancel the current task before editing another task.', [{ text: 'OK' }]);
        return;
      }
      this.startTaskEdit(row);
    },
    saveTaskAdd() {
      if (!this.taskAddTaskValue) {
        this.taskAddTaskError = 'Task is required!';
        return;
      }
      const taskCode = this.taskAddTaskValue.ServiceCode;
      if (this.taskAddRows.some((row) => row['Task Code'] == taskCode && row._rowid != this.taskAddEditorRowId)) {
        this.$showAlert('error', 'Task Already Exists', 'The selected task is already in this service.', [{ text: 'OK' }]);
        return;
      }
      const currentRow = this.taskAddRows.find((task) => task._rowid == this.taskAddEditorRowId);
      const originalRow = this.taskAddGroupAction == 'E' && currentRow?.seqNo != null ?
        this.taskAddOriginalRows.find((task) => String(task.seqNo) == String(currentRow.seqNo)) : null;
      const row = {
        ...(currentRow || {}),
        _rowid: this.taskAddEditorRowId || `task-add-${++this.taskAddRowId}`,
        'Service Code': this.taskAddServiceValue.ServiceCode,
        'Service Description': this.taskAddServiceValue.Description,
        'Task Code': taskCode,
        'Task Description': this.taskAddTaskValue.Description,
        'Service Person': this.taskAddPersonValue,
        'Date Start': this.taskAddDateStartValue,
        'Date End': this.taskAddDateEndValue,
        Remark: this.taskAddRemarkValue,
        action: originalRow ? (originalRow.action == 'N' ? 'N' : 'E') : 'N',
      };
      const rows = this.taskAddEditorAction == 'E' ?
        this.taskAddRows.map((task) => task._rowid == row._rowid ? row : task) : [...this.taskAddRows, row];
      this.taskAddRows = [];
      this.taskAddSelectedRows = [];
      this.$nextTick(() => {
        this.taskAddRows = rows;
        this.taskAddEditorOpen = false;
      });
    },
    deleteTaskAdd() {
      const row = this.taskAddSelectedRows[0];
      if (!row) return;
      const rows = this.taskAddRows.filter((task) => task._rowid != row._rowid);
      this.taskAddRows = [];
      this.taskAddSelectedRows = [];
      this.$nextTick(() => { this.taskAddRows = rows; });
    },
    saveTaskAddGroup() {
      if (!this.taskAddServiceValue?.ServiceCode) {
        this.taskAddServiceError = 'Service Code is required!';
        return;
      }
      this.taskAddServiceType = this.getTaskServiceType(this.taskAddServiceValue);
      if (!this.taskAddRows.length && this.taskAddServiceType == '2') {
        this.taskAddRows = [this.taskAddServiceAsTaskRow()];
      }
      if (!this.taskAddRows.length) {
        this.$showAlert('error', 'No Tasks Added', 'Add at least one Task. A Service and Task code can be added directly.', [{ text: 'OK' }]);
        return;
      }
      const editingGroup = this.taskAddGroupAction == 'E';
      const groupServiceCode = String(cleanText(this.taskAddServiceValue.ServiceCode)).toUpperCase();
      const originalRows = editingGroup ? this.taskAddOriginalRows : [];
      const originalSeqNos = new Set(originalRows.map((task) => String(task.seqNo)));
      const originalServiceCode = String(cleanText(originalRows[0]?.['Service Code']) || '').toUpperCase();
      const serviceChanged = editingGroup && groupServiceCode != originalServiceCode;
      const originalRowsBySeqNo = new Map(originalRows.map((task) => [String(task.seqNo), task]));
      const pairKey = (task) =>
        `${String(task['Service Code']).trim().toUpperCase()}|${String(task['Task Code']).trim().toUpperCase()}`;
      const existing = new Set(this.taskTableDataFiltered
        .filter((task) => !editingGroup || !originalSeqNos.has(String(task.seqNo)))
        .map(pairKey));
      let skipped = 0;
      let added = 0;
      let duplicate = false;
      let seqNo = this.taskTableData.reduce((max, task) => Math.max(max, Number(task.seqNo) || 0), 0);
      const rows = [];
      this.taskAddRows.forEach((task) => {
        const originalRow = editingGroup && task.seqNo != null ? originalRowsBySeqNo.get(String(task.seqNo)) : null;
        const serviceTask = {
          ...task,
          'Service Code': this.taskAddServiceValue.ServiceCode,
          'Service Description': this.taskAddServiceValue.Description,
        };
        const key = pairKey(serviceTask);
        if (existing.has(key)) {
          if (editingGroup) duplicate = true;
          else skipped++;
          return;
        }
        existing.add(key);
        if (!originalRow) added++;
        rows.push({
          ...serviceTask,
          seqNo: originalRow ? originalRow.seqNo : ++seqNo,
          action: originalRow ?
            (originalRow.action == 'N' ? 'N' : serviceChanged || task.action == 'E' ? 'E' : originalRow.action)
          : 'N',
        });
      });
      if (duplicate) {
        this.$showAlert('error', 'Task Already Exists', 'The selected service already has one of these tasks.', [{ text: 'OK' }]);
        return;
      }

      let table;
      if (editingGroup) {
        const rowsBySeqNo = new Map(rows
          .filter((task) => originalSeqNos.has(String(task.seqNo)))
          .map((task) => [String(task.seqNo), task]));
        table = [];
        this.taskTableData.forEach((task) => {
          const seqKey = String(task.seqNo);
          const isEditedGroupRow =
            String(cleanText(task['Service Code']) || '').toUpperCase() == originalServiceCode &&
            task.action != 'D' && originalSeqNos.has(seqKey);
          if (!isEditedGroupRow) {
            table.push(task);
            return;
          }
          const updatedTask = rowsBySeqNo.get(seqKey);
          if (updatedTask) {
            table.push(updatedTask);
            rowsBySeqNo.delete(seqKey);
          } else if (task.action != 'N') {
            table.push({ ...task, action: 'D' });
          }
        });
        table.push(...rows.filter((task) => !originalSeqNos.has(String(task.seqNo))));
      } else {
        table = [...this.taskTableData, ...rows];
      }
      this.taskTableData = [];
      this.$nextTick(() => {
        this.taskTableData = table;
        this.taskTableSelectedRows = [];
        this.taskAddShow = false;
        this.taskAddGroupAction = 'N';
        this.taskAddOriginalRows = [];
      });
      if (skipped) {
        this.$showAlert(
          'success',
          editingGroup ? 'Service Updated' : 'Tasks Added',
          `${added} task(s) added; ${skipped} duplicate(s) skipped.`,
          [{ text: 'OK' }],
        );
      }
    },
    async openMaintenanceImport() {
      if (!this.jobInventoryValue || this.viewMode || this.taskSelectedItem) return;
      this.maintenanceDueRows = [];
      this.maintenanceImportShow = true;
      this.maintenanceImportLoading = true;
      const vehicleNo = this.jobInventoryValue['Inventory #'];
      const requestId = ++this.maintenanceImportRequestId;
      try {
        const rows = await this.$apiCallV2('/EquipmentReport/GetMaintanceList', { vehicleno: vehicleNo });
        if (requestId == this.maintenanceImportRequestId && this.maintenanceImportShow && this.jobInventoryValue?.['Inventory #'] == vehicleNo) {
          this.maintenanceDueRows = rows;
        }
      } catch (error) {
        if (requestId == this.maintenanceImportRequestId && this.maintenanceImportShow) {
          this.$showAlert('error', 'Maintenance Due Dates Unavailable', 'Unable to load maintenance due dates.');
        }
      } finally {
        if (requestId == this.maintenanceImportRequestId) this.maintenanceImportLoading = false;
      }
    },
    importVehicleMaintenance(_buttonText, selectedGroup) {
      if (!this.jobInventoryValue || this.viewMode || this.maintenanceImportLoading || !selectedGroup?.key) return;
      const group = this.vehicleMaintenanceGroups.find((item) => item.key == selectedGroup.key);
      if (!group) return;
      const pairKey = (serviceCode, taskCode) => JSON.stringify([
        String(serviceCode || '').trim().toUpperCase(), String(taskCode || '').trim().toUpperCase(),
      ]);
      const existing = new Set(this.taskTableDataFiltered.map((task) =>
        pairKey(task['Service Code'], task['Task Code'])));
      const imported = [];
      let skipped = 0;
      let seqNo = this.taskTableData.reduce((max, task) => Math.max(max, Number(task.seqNo) || 0), 0);
      group.tasks.forEach((task) => {
        const serviceCode = cleanText(task.ServiceCode);
        const taskCode = cleanText(task['Service Task']);
        const pair = pairKey(serviceCode, taskCode);
        if (existing.has(pair)) {
          skipped++;
          return;
        }
        existing.add(pair);
        imported.push({
          '#': '',
          'Service Code': serviceCode,
          'Service Description': task['Code Description'],
          'Task Code': taskCode,
          'Task Description': task['Task Description'],
          'Service Person': '',
          'Date Start': null,
          'Date End': null,
          Remark: task.Remark || '',
          action: 'N',
          seqNo: ++seqNo,
        });
      });
      const table = [...this.taskTableData, ...imported];
      this.taskTableData = [];
      this.$nextTick(() => {
        this.taskTableData = table;
        this.maintenanceImportShow = false;
      });
      if (skipped) {
        this.$showAlert('success', 'Service Imported', `${imported.length} task(s) added; ${skipped} duplicate(s) skipped.`, [{ text: 'OK' }]);
      }
    },
    newPartClick() {
      this.clearTabsFields();
      this.partFieldsChangedCheck = structuredClone(this.partFields);
      this.partSelectedItem = {};
      this.tabFieldsAction = 'N';
    },

    docFileDownload() {
      // if it already has prefix like "data:...;base64,", use it directly
      const base64Data = this.docFields.docPicDocValue;
      let url = base64Data;
      if (!base64Data.startsWith('data:')) {
        // fallback generic MIME type if unknown
        url = 'data:application/octet-stream;base64,' + base64Data;
      }

      const link = document.createElement('a');
      link.href = url;
      link.download = this.docFields.docPicDocName; // no extension if you don’t know it
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async docFileChanged(file) {
      if (file.length == 0) {
        this.docFields.docPicDocName = '';
        this.docFields.docPicDocValue = null;
        this.docFields.docPicDocType = '';
      } else {
        this.$fullscreenLoading('Adding File');
        this.docFields.docPicDocName = file[0].name;
        this.docFields.docPicDocType = file[0].name.substring(file[0].name.indexOf('.'));
        const tempAtt = await file[0].toBase64();
        this.docFields.docPicDocValue = tempAtt.substring(tempAtt.indexOf(',') + 1);
        this.$fullscreenLoading();
      }
    },

    async docSaveClick() {
      this.clearErrorsDoc();
      const { '#': tableNo, seqNo } = this.docSelectedItem || {};
      const {
        docDocCodeValue,
        docDocDateValue,
        docDateReceivedValue,
        docReferenceNoValue,
        docRemarkValue,
        docDocTypeValue,
        docPicDocName,
        docPicDocValue,
        docPicDocType,
      } = this.docFields;

      const { 'Ref No': selectedRefNo } = this.docSelectedItem || {};

      //#region  error checking
      let error = false;

      if (
        !docPicDocValue &&
        !docReferenceNoValue &&
        !docDocDateValue &&
        !docRemarkValue &&
        !docDocTypeValue &&
        !docDocCodeValue
      ) {
        this.$showAlert('error', 'Empty Document', 'Add an attachment or enter document information before saving.', [
          { text: 'OK' },
        ]);
        return;
      }

      if (docReferenceNoValue && docReferenceNoValue != this.docFieldsChangedCheck.docReferenceNoValue) {
        this.$fullscreenLoading('Checking Reference No...');
        const check = await this.$apiCallV2('/WorkshopJob/CheckRefNo', { refno: docReferenceNoValue });
        const { Results } = check[0] || {};
        if (Results != 0) {
          this.docReferenceNoError = 'Reference No already exists!';
          error = true;
        }
        this.$fullscreenLoading();
      } else if (
        docReferenceNoValue &&
        selectedRefNo != docReferenceNoValue &&
        this.docTableData.find((doc) => doc['Ref No'] == docReferenceNoValue)
      ) {
        this.docReferenceNoError = 'Reference No is duplicated from list!';
        error = true;
      }

      if (error) return;
      //#endregion

      const docItem = {
        ...this.docSelectedItem,
        '#': tableNo ?? '',
        'Ref No': docReferenceNoValue,
        Type: docDocTypeValue?.['Doc Type'] ?? this.docSelectedItem.Type ?? '',
        Code: docDocCodeValue?.['Doc Code'] ?? this.docSelectedItem.Code ?? '',
        Date: docDocDateValue,
        'Date Received': docDateReceivedValue,
        'Pic/Doc': docPicDocName,
        PicDocFile: docPicDocValue,
        PicDocType: docPicDocType,
        Remark: docRemarkValue,
        JobNo: '',
        action: this.docSelectedItem.action ?? this.tabFieldsAction,
        seqNo: seqNo,
      };

      let setTableData = this.docTableData;
      if (this.tabFieldsAction == 'N') {
        setTableData = [...this.docTableData, { ...docItem, seqNo: this.docTableData.length + 1 }];
      } else if (this.tabFieldsAction == 'E') {
        setTableData = this.docTableData.map((doc) => {
          if (doc.seqNo == seqNo) return docItem;
          return doc;
        });
      }
      //use filtered data for operations but set to the actual table data
      this.docTableData = [];
      this.$nextTick(() => {
        this.docTableData = setTableData;
      });
      this.clearTabsFields();
    },

    taskSaveClick() {
      const { '#': tableNo, seqNo } = this.taskSelectedItem;

      const {
        taskServiceValue,
        taskServiceTaskValue,
        taskServicePersonValue,
        taskDateTimeStartValue,
        taskDateTimeEndValue,
        taskRemarkValue,
      } = this.taskFields;

      //#region error checking
      let error = false;
      if (!taskServiceValue) {
        this.taskServiceError = 'Service Code is required!';
        error = true;
      }
      if (taskServiceValue && !this.taskServiceType) this.taskServiceSelected(taskServiceValue);
      if (!taskServiceTaskValue && !this.taskServiceIsTask) {
        this.taskServiceTaskError = 'Task is required!';
        error = true;
      }
      if (error) return;
      //#endregion

      const selectedTask = taskServiceTaskValue || taskServiceValue;
      const taskItem = {
        ...this.taskSelectedItem,
        '#': tableNo ?? '',
        'Service Code': taskServiceValue.ServiceCode,
        'Service Description': taskServiceValue.Description,
        'Task Code': selectedTask.ServiceCode,
        'Task Description': selectedTask.Description,
        'Service Person': taskServicePersonValue,
        'Date Start': taskDateTimeStartValue,
        'Date End': taskDateTimeEndValue,
        Remark: taskRemarkValue,
        action: this.taskSelectedItem.action ?? this.tabFieldsAction,
        seqNo: seqNo,
      };

      let setTableData = this.taskTableData;
      if (this.tabFieldsAction == 'N') {
        setTableData = [...this.taskTableData, { ...taskItem, seqNo: this.taskTableData.length + 1 }];
      } else if (this.tabFieldsAction == 'E') {
        setTableData = this.taskTableData.map((task) => {
          if (task.seqNo == seqNo) return taskItem;
          return task;
        });
      }
      //use filtered data for operations but set to the actual table data
      this.taskTableData = [];
      this.$nextTick(() => {
        this.taskTableData = setTableData;
      });
      this.clearTabsFields();
    },

    partQuantityFocusChanged(focus) {
      if (focus) return;
      const quantityValue = this.normalizePartQuantityValue(this.partFields.partQuantityValue);
      if (quantityValue) this.partFields.partQuantityValue = quantityValue;
      this.validatePartQuantityMaximum();
    },

    validatePartQuantityMaximum(quantityValue = null) {
      const selectedWarehouseItem = this.partFields.partWarehouseItemValue;
      const quantityBalance = this.partQuantityMaximum;
      if (!selectedWarehouseItem || quantityBalance === null) return true;

      const value =
        quantityValue === null || quantityValue === undefined ?
          this.parsePartAmount(String(this.partFields.partQuantityValue || '1').trim() || '1')
        : quantityValue;
      if (value > quantityBalance) {
        this.partQuantityError = `Quantity cannot exceed available stock (${quantityBalance})!`;
        return false;
      }
      return true;
    },

    async partSaveClick() {
      if (this.partSerialNoSaving) return;
      this.cancelPartSerialNoCheck();
      const { '#': tableNo, seqNo, tyreSeqNo } = this.partSelectedItem || {};
      const rotationTyre = this.tyreConfigUpdateList.find((item) => item.seqno == tyreSeqNo);
      const isRotation = rotationTyre?.type == 'ROTATE' || rotationTyre?.type == 'OTHER';
      const {
        partTypeValue,
        partSerialNoValue,
        partSerialNoInputValue,
        partPartNoValue,
        partSizeValue,
        partWarehouseItemValue,
        partWarehouseItemSavedValue,
        partTakenFromValue,
        partTakenFromInputValue,
        partUnitPriceValue,
        partMFGDateValue,
        partDatePurchaseValue,
        partInvoiceNoValue,
        partReplaceValue,
        partModelValue,
        partMadeInValue,
        partQuantityValue,
        partManufacturerValue,
        partPurchaseFromValue,
        partPONoValue,
        partInsuranceNoValue,
        partRemarkValue,
      } = this.partFields;

      //#region error checking
      this.partTypeError = '';
      this.partSerialNoError = '';
      this.partQuantityError = '';
      let error = false;
      if (!partTypeValue) {
        this.partTypeError = 'Type is required!';
        error = true;
      }
      if (!partSerialNoValue && this.serialNoRequired && !partSerialNoInputValue) {
        this.partSerialNoError = 'Serial No is required!';
        error = true;
      }
      const rawQuantity = partQuantityValue == null ? '' : partQuantityValue.toString().trim();
      const quantityValue = this.parsePartAmount(rawQuantity || '1');
      if (quantityValue <= 0) {
        this.partQuantityError = 'Quantity must be greater than 0!';
        error = true;
      }
      if (!this.validatePartQuantityMaximum(quantityValue)) error = true;
      if (error) return;
      const serialNo = String(partSerialNoInputValue ?? '').trim();
      const selectedPart = this.partSelectedItem;
      const requestId = this.partSerialNoCheckRequestId;
      if (serialNo && !this.partWarehouseItemSelected && serialNo != String(selectedPart?.['Serial No'] ?? '').trim()) {
        this.partSerialNoSaving = true;
        this.partSerialNoChecking = true;
        try {
          const message = await this.checkPartSerialNo(serialNo);
          if (requestId !== this.partSerialNoCheckRequestId || String(this.partFields.partSerialNoInputValue ?? '').trim() != serialNo || this.partSelectedItem !== selectedPart) return;
          this.partSerialNoCheckResult = !message;
          if (message) {
            this.partSerialNoError = message;
            return;
          }
        } catch (_error) {
          if (requestId === this.partSerialNoCheckRequestId) {
            this.partSerialNoError = 'Unable to check Serial No. Please try again.';
            this.partSerialNoCheckResult = false;
          }
          return;
        } finally {
          this.partSerialNoSaving = false;
          if (requestId === this.partSerialNoCheckRequestId) this.partSerialNoChecking = false;
        }
      }
      //#endregion

      const quantity = quantityValue.toString();
      const unitPriceValue = this.parsePartAmount(partUnitPriceValue);
      const savedWarehouseItem = partWarehouseItemSavedValue;
      const existingPart = this.partSelectedItem || {};
      const isNewPart = this.tabFieldsAction == 'N' || existingPart.action == 'N';
      const warehouseGitNo = isNewPart ? '' : existingPart.GitNo || '';
      const warehousePLGuid = isNewPart ? '' : existingPart.PLGuid || '';
      const warehousePLNo = isNewPart ? '' : existingPart.PLNo || '';
      const warehouseGRNGuid = isNewPart ? '' : existingPart.GRNGuid || savedWarehouseItem?.GRNGuid || '';

      const partItem = {
        ...this.partSelectedItem,
        '#': tableNo ?? '',
        Type: partTypeValue['Part Type'],
        'Serial No': partSerialNoValue ? partSerialNoValue['Serial No'] : partSerialNoInputValue,
        'Part No': partPartNoValue,
        Manufacturer: partManufacturerValue?.MFGCode ?? '',
        'Made In': partMadeInValue?.['Country Code'] ?? '',
        Size: partSizeValue,
        'Cost (RM)': unitPriceValue * quantityValue,
        Replace: partReplaceValue,
        'Purchase From': partPurchaseFromValue?.['Supplier Code'] ?? '',
        'Date Purchase': partDatePurchaseValue,
        'Insurance Claim No': partInsuranceNoValue,
        'Invoice No': partInvoiceNoValue,
        'Taken From': partTakenFromValue?.Warehouse ?? partTakenFromValue?.['Warehouse Code'] ?? partTakenFromInputValue,
        Remark: partRemarkValue,
        PONo: partPONoValue,
        UserID: '',
        'Date Updated': '',
        MfgDate: partMFGDateValue,
        Model: partModelValue,
        Quantity: quantity,

        partTypeDesc: partTypeValue.Description,

        action: this.partSelectedItem.action ?? this.tabFieldsAction,
        seqNo: seqNo,

        expenseCode: partTypeValue['Expense Code'] || 'EMS',
        isExpense: isRotation ? 'N' : (partTypeValue?.isExpense ?? partTypeValue?.IsExpenses),

        prodTxnID: partWarehouseItemValue?.ProdTxnID || partSerialNoValue?.ProdTxnID || partTypeValue.ProdTxnID || '',
        AllocGUID: partWarehouseItemValue?.AllocGUID || partTakenFromValue?.AllocGUID || partSerialNoValue?.AllocGUID || '',
        BinLoc: partWarehouseItemValue?.BinLoc || partTakenFromValue?.BinLoc || '',
        GitNo: warehouseGitNo,
        PLGuid: warehousePLGuid,
        PLNo: warehousePLNo,
        GRNGuid: warehouseGRNGuid,
      };

      let setTableData = this.partTableData;
      if (this.tabFieldsAction == 'N') {
        setTableData = [...this.partTableData, { ...partItem, seqNo: this.partTableData.length + 1 }];
      } else if (this.tabFieldsAction == 'E') {
        setTableData = this.partTableData.map((part) => {
          if (part.seqNo == seqNo) return partItem;
          return part;
        });
      }

      this.partTableData = [];
      this.$nextTick(() => {
        this.partTableData = setTableData;
      });
      this.clearTabsFields();

      if (tyreSeqNo) {
        const updateTyreConfig = this.tyreConfigUpdateList.map((item) => {
          if (tyreSeqNo == item.seqno)
            return {
              ...item,
              manufacturer: partManufacturerValue?.MFGCode ?? '',
              madein: partMadeInValue?.['Country Code'] ?? '',
              model: partModelValue,
              tyresize: partSizeValue,
              sppcode: partPurchaseFromValue?.['Supplier Code'] ?? '',
              partno: partPartNoValue,
              unitprice: unitPriceValue,
            };
          return item;
        });

        this.tyreConfigUpdateList = [];
        this.$nextTick(() => {
          this.tyreConfigUpdateList = this.reorderTableNo(updateTyreConfig);
        });
      }
    },

    async docDeleteClick() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Document?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;
      const { seqNo, action } = this.docTableSelectedRows[0] || {};
      const update =
        action == 'N' ?
          this.docTableData.filter((item) => item.seqNo != seqNo)
        : this.docTableData.map((item) => {
            if (item.seqNo == seqNo) {
              return { ...item, action: 'D' };
            }
            return item;
          });
      this.docTableData = [];
      this.$nextTick(() => {
        this.docTableData = this.reorderTableNo(update);
      });
    },
    async taskDeleteClick() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Task?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;
      const { seqNo, action } = this.taskTableSelectedRows[0] || {};
      const update =
        action == 'N' ?
          this.taskTableData.filter((item) => item.seqNo != seqNo)
        : this.taskTableData.map((item) => {
            if (item.seqNo == seqNo) {
              return { ...item, action: 'D' };
            }
            return item;
          });
      this.taskTableData = [];
      this.$nextTick(() => {
        this.taskTableData = this.reorderTableNo(update);
      });
    },
    async partDeleteClick() {
      const discard = await this.$showAlert('warning', 'Are you sure you want to delete this Part?', '', [
        { text: 'Delete', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);
      if (discard == 'Cancel') return false;
      const { seqNo, action, TyreIndex: tyreindex, 'Serial No': serialno } = this.partTableSelectedRows[0] || {};
      const update =
        action == 'N' ?
          this.partTableData.filter((item) => item.seqNo != seqNo)
        : this.partTableData.map((item) => {
            if (item.seqNo == seqNo) {
              return { ...item, action: 'D' };
            }
            return item;
          });
      this.partTableData = [];
      this.$nextTick(() => {
        this.partTableData = this.reorderTableNo(update);
        if (tyreindex) {
          this.tyreConfigDeleteClicked(null, tyreindex, serialno);
          this.$nextTick(() => {
            this.tyreConfigPopupSave();
          });
        }
      });
    },

    popupEntryRequiredClicked: function (item) {
      this.$refs[item].setFocus();
    },

    tabFieldsCancel() {
      this.docSelectedItem = null;
      this.taskSelectedItem = null;
      this.partSelectedItem = null;
      this.clearTabsFields();
    },

    async resubmitApprovalClicked() {
      if (!this.viewMode || !this.canSendApproval || this.resubmitApproval) return;
      this.resubmitApproval = true;
      this.clearEntryTexts();
      try {
        const result = await this.$apiCallV2('/DocumentApproval/DocApprovalWorkOrder', { actionflag: 'SUBMIT', paymentno: this.jobNoValue });
        const response = result?.[0];
        if (String(response?.Results) !== '0' || !response.ApprovalSummary) {
          this.popupEntryErrorMessages.push(response?.ErrMsg || 'Unable to submit this job for approval.');
          return;
        }
        const summary = response.ApprovalSummary;
        this.approvalStatus = summary.ApprovalStatus;
        this.pendingLevel = summary.PendingLevel;
        this.approvalHistory = summary.ApprovalHistory || [];
        this.lastApprovalDate = summary.LastApprovalDate;
        this.approvedAmount = summary.ApprovedAmount;
        const job = this.workshopJobsTableData.find((row) => row['Job #'] === this.jobNoValue);
        if (job) this.$set(job, 'Approval Status', summary.ApprovalStatus);
        this.popupEntrySuccessMessages.push(response.ErrMsg);
      } catch (error) {
        this.popupEntryErrorMessages.push(error.message || 'Unable to submit this job for approval.');
      } finally {
        this.resubmitApproval = false;
      }
    },

    async popupEntrySaveClicked() {
      if (this.resubmitApproval) return;
      if (this.fieldsChanged) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }

      this.clearEntryTexts();
      const error = [];
      if (!this.jobInventoryValue) {
        this.jobInventoryValueError = 'Inventory is required!';
        error.push({ key: 'jobInventory', value: 'Inventory is required!' });
      }
      if (!this.jobDateValue) {
        this.jobDateValueError = 'Job Date is requried!';
        error.push({ key: 'jobDate', value: 'Job Date is required!' });
      }
      if (!this.repairTypeValue) {
        this.repairTypeValueError = 'Replaced With is required!';
        error.push({ key: 'repairType', value: 'Replaced With is required!' });
      }
      if (!this.internalExternalValue) {
        this.internalExternalValueError = 'Internal/External is required!';
        error.push({ key: 'intext', value: 'Internal/External is required!' });
      }
      if (!this.mileageValue || !Number.isFinite(Number(this.mileageValue))) {
        this.mileageValueError = 'Mileage is required!';
        error.push({ key: 'mileage', value: 'Mileage is required!' });
      }
      if (
        this.availabilityEnabled &&
        (this.inventoryAvailabilityValue ? this.inventoryAvailabilityValue.keyValue == '0' : false) &&
        !this.inventoryUnavailableRemarkValue
      ) {
        this.inventoryUnavailableRemarkValueError = 'Inventory Unavailable Remark is required!';
        error.push({ key: 'inventoryUnavailableRemark', value: 'Inventory Unavailable Remark is required!' });
      }
      this.popupEntryRequiredError = error;
      if (error.length > 0) return;

      this.$fullscreenLoading('Saving Workshop Job...');
      try {
        await this.loadDocumentFiles();
      } catch (error) {
        this.popupEntryErrorMessages.push(error.message || 'Unable to load existing attachments. Job was not saved.');
        this.$fullscreenLoading();
        return;
      }

      const successMessages = [];

      const requestBody = {
        action: this.menuMode == 'New' ? 'N' : 'E',

        //general info
        jobno: this.jobNoValue,
        inventoryno: this.jobInventoryValue['Inventory #'],
        jobdate: this.jobDateValue.formatString('yyyy-MM-ddTHH:mm:ss'),

        //job details
        trailer: this.trailerNoValue?.['Inventory #'] ?? '',
        requestby: this.requestedByValue?.['EmployeeID'] ?? '',
        servicetype: this.repairTypeValue?.keyValue ?? '',
        intext: this.internalExternalValue?.keyValue ?? '',
        serviceperson: this.servicePersonValue,
        workshop: this.workshopValue?.['Supplier Code'] ?? '',
        meterreading:
          this.isLatestJob && this.mileageValue > this.selectedVehicleInfo.MeterReading ?
            this.mileageValue
          : this.selectedVehicleInfo.MeterReading,
        remark1: this.remark1Value,
        datein: this.dateTimeInValue?.formatString('yyyy-MM-dd') ?? null,
        timein: this.dateTimeInValue?.formatString('HHmm') ?? '',
        dateout: this.dateTimeOutValue?.formatString('yyyy-MM-dd') ?? null,
        timeout: this.dateTimeOutValue?.formatString('HHmm') ?? '',
        status: this.confirmJobValue ? '12001' : '', // Confirmation does not submit the job.
        availability: this.inventoryAvailabilityValue?.keyValue ?? '',
        invavailabilityremark:
          (this.inventoryAvailabilityValue ? this.inventoryAvailabilityValue.keyValue == '0' : false) ?
            this.inventoryUnavailableRemarkValue
          : '',

        //things not in form
        indicator: 'N', //must be N if saving or editing, if C it's a Cancelled entry

        jobdatedate: this.jobDateDate,
        jobdatetime: this.jobDateTime,

        documents: this.docTableData
          .filter((task) => task.action != 'D')
          .map((item) => {
            const {
              Code,
              Date: DocDate,
              'Date Received': DateReceived,
              'Pic/Doc': PicDoc,
              PicDocFile,
              PicDocType,
              'Ref No': RefNo,
              Remark,
              Type,
              action,
            } = item;
            return {
              code: Code,
              date: DocDate,
              datereceived: DateReceived?.formatString('yyyy-MM-dd') ?? null,
              timereceived: DateReceived?.formatString('HHmm') ?? null,
              picdoc: PicDoc,
              picdocfile: PicDocFile ?? null,
              picdoctype: PicDocType ?? '',
              refno: RefNo,
              remark: Remark,
              type: Type,
              action: this.menuMode == 'New' ? 'N' : action,
            };
          }),
        //documents are deleted and inserted again because there are previous items where seqNo is not used
        //so making identifying unique items for Edit and Delete not possible

        tasks: this.taskTableData
          .filter((task) => task.action != 'D')
          .map((item, index) => {
            const {
              'Service Code': ServiceCode,
              'Task Code': TaskCode,
              'Service Person': ServicePerson,
              'Date Start': DateStart,
              'Date End': DateEnd,
              Remark,
              action,
               InvSeq,
               TaskGuid,
            } = item;
            return {
              servicecode: ServiceCode,
              seqno: index + 1, //reset this seqNo, because deleted item might affect the order,
              task: TaskCode,
              serviceperson: ServicePerson,
              datestart: DateStart?.formatString('yyyy-MM-dd') ?? null,
              timestart: DateStart?.formatString('HHmm') ?? null,
              dateend: DateEnd?.formatString('yyyy-MM-dd') ?? null,
              timeend: DateEnd?.formatString('HHmm') ?? null,
              remark: Remark,
               action: this.menuMode == 'New' ? 'N' : action,
               taskguid: TaskGuid || null,

              invseq: InvSeq ?? null, //For ServiceTask not EMSTask
            };
          }),
        parts: this.partTableData
          .filter((part) => part.action)
          .map((item) => {
            const {
              'Cost (RM)': Cost,
              'Date Purchase': DatePurchase,
              'Insurance Claim No': InsuranceNo,
              'Invoice No': InvoiceNo,
              'Made In': MadeIn,
              Manufacturer,
              MfgDate,
              Model,
              PONo,
              'Part No': PartNo,
              PartID,
              'Purchase From': PurchaseFrom,
              Quantity,
              Remark,
              Replace,
              'Serial No': SerialNo,
              Size,
              'Taken From': TakenFrom,
              Type,

              partTypeDesc,

              action,
              seqNo,

              expenseCode,
              isExpense,

              prodTxnID,
              AllocGUID,
              GitNo,
              GRNGuid,
              BinLoc,
              PLNo,
              PLGuid,

              TyreIndex,
              noOfRetread,
              partinventoryno,
            } = item;

            return {
              partid: PartID ?? '',
              docrefno: InvoiceNo ?? '',
              serialno: SerialNo ?? '',
              partno: PartNo ?? '',
              parttype: Type ?? '',
              model: Model ?? '',
              madein: MadeIn ?? '',
              manufacturer: Manufacturer ?? '',
              purchasefrom: PurchaseFrom ?? '',
              datepurchase: this.toUTCDateTime(DatePurchase) ?? null,
              mfgdate: this.toUTCDateTime(MfgDate) ?? null,
              size: Size ?? '',
              quantity: Quantity ?? '',
              cost: Cost ?? '0',
              insclaimno: InsuranceNo ?? '',
              reusefrom: Replace ?? '',
              remark: Remark ?? '',
              pono: PONo ?? '',

              action: this.menuMode == 'New' ? 'N' : action,
              seqno: seqNo ?? '',

              takenfrom: TakenFrom ?? '',

              tyreindex: TyreIndex ?? '',
              noofretread: noOfRetread ?? '',

              parttypedesc: partTypeDesc ?? '',
              expensecode: expenseCode ?? '',
              isexpense: isExpense ?? '',

              prodtxnid: prodTxnID ?? '',
              allocguid: AllocGUID ?? '',
              gitno: GitNo ?? '',
              grnguid: GRNGuid ?? '',
              binloc: BinLoc,
              plno: PLNo ?? '',
              plguid: PLGuid ?? '',

              partinventoryno: partinventoryno ?? this.jobInventoryValue['Inventory #'],
            };
          }),

        tyres: [
          ...this.tyreConfigUpdateList,
          ...this.tyreConfigUpdateOtherVehicle.filter((item) => item.prevupdate || item.revert),
        ]
          .filter((item) => item.action && item.action != 'O')
          .map((item) => {
            const {
              serialno,
              oldserialno,
              actualSerial,
              tyreposition,
              storein,
              inventoryno,
              seqno,
              tyresize,
              tyreindex,
              repair,
              inuse,
              datechange,
              align,
              rowno,
              columnno,
              whsecode,
              prodtxnid,
              action,
              actualtyreguid,
              tyreguid,

              type,
              mfgdate,
              datepurchase,
              noofretread,
              model,
              madein,
              manufacturer,
              unitprice,
              sppcode,
              allocguid,

              jobno,

              tyretype,
              partno,

              startmileage,

              revertgrn,
            } = item;

            return {
              newserial: serialno ?? '',
              currentserial: oldserialno ?? '',
              actualserial: actualSerial ?? '',
              tyreposition: tyreposition ?? '',
              storein: storein ?? '',
              inventoryno: inventoryno ?? '',
              seqno: seqno ?? '',
              tyresize: tyresize ?? '',
              tyreindex: tyreindex ?? '',
              repair: repair ?? '',
              inuse: inuse ?? '',
              datechange: datechange ? (this.toUTCDateTime(datechange) ?? null) : this.toUTCDateTime(this.jobDateDate),
              align: align ?? '',
              rowno: rowno ?? '',
              columnno: columnno ?? '',
              whsecode: whsecode ?? '',
              prodtxnid: prodtxnid ?? '',
              action: action ?? '',
              actualtyreguid: actualtyreguid ?? '',
              currenttyreguid: tyreguid ?? '',
              type: type ?? '',
              mfgdate: this.toUTCDateTime(mfgdate) ?? null,
              datepurchase: this.toUTCDateTime(datepurchase) ?? null,
              noofretread: noofretread ?? '',
              model: model ?? '',
              madein: madein ?? '',
              manufacturer: manufacturer ?? '',
              unitprice: unitprice ?? '',
              sppcode: sppcode ?? '',
              allocguid: allocguid ?? '',
              tyretype: tyretype ?? '',
              partno: partno ?? '',

              jobno: jobno ?? '',

              startmileage: startmileage ?? this.mileageValue ?? '0',
              endmileage: this.mileageValue ?? '0',

              revertgrn: revertgrn ?? false,
            };
          }),

        //extra needed
        inventorycode: this.jobInventoryValue['Inventory Code'],
        islatestjob: this.isLatestJob ? 'Y' : 'N',
      };

      const result = await this.$apiCallV2('/WorkshopJob/SaveEMSJob', requestBody);
      const auditWarnings = new Set();

      const { Results, ErrMsg, EMSJOB } = result?.[0] || {};
      if (Results == '1') {
        this.popupEntryErrorMessages.push(ErrMsg);
        this.$fullscreenLoading();
        return;
      }
      successMessages.push(ErrMsg);
      if (result?.[0]?.AuditWarning) auditWarnings.add(result[0].AuditWarning);

      const otherNewInv = [
        ...new Set(
          this.tyreConfigUpdateOtherVehicle
            .filter((item) => item.action == 'N' && !item.jobno && !(item.revert || item.prevupdate))
            .map((item) => item.inventoryno)
            .filter(Boolean), // removes null/undefined/empty
        ),
      ].map((invno) => {
        const findParts = this.partUpdateOtherVehicle.filter(
          (item) => item.partinventoryno == invno && item.action == 'N' && !item.jobno,
        );

        const findTyres = this.tyreConfigUpdateOtherVehicle.filter(
          (item) => item.inventoryno == invno && item.action == 'N' && !item.jobno,
        );

        return {
          action: 'N',

          //general info
          jobno: '',
          inventoryno: invno,
          jobdate: this.jobDateValue.formatString('yyyy-MM-ddTHH:mm:ss'),

          //job details
          trailer: '',
          requestby: this.requestedByValue?.['EmployeeID'] ?? '',
          servicetype: this.repairTypeValue?.keyValue ?? '',
          intext: this.internalExternalValue?.keyValue ?? '',
          serviceperson: this.servicePersonValue,
          workshop: this.workshopValue?.['Supplier Code'] ?? '',
          meterreading: '',
          remark1: '',
          datein: this.dateTimeInValue?.formatString('yyyy-MM-dd') ?? null,
          timein: this.dateTimeInValue?.formatString('HHmm') ?? '',
          dateout: this.dateTimeOutValue?.formatString('yyyy-MM-dd') ?? null,
          timeout: this.dateTimeOutValue?.formatString('HHmm') ?? '',
          status: this.confirmJobValue ? '12001' : '', // Confirmed job; submit for approval on save

          availability: '1',
          invavailabilityremark: '',

          //things not in form
          indicator: 'N', //must be N if saving or editing, if C it's a Cancelled entry

          jobdatedate: this.jobDateDate,
          jobdatetime: this.jobDateTime,

          islatsetjob: 'Y',

          parts: findParts.map((item) => {
            const {
              'Cost (RM)': Cost,
              'Date Purchase': DatePurchase,
              'Insurance Claim No': InsuranceNo,
              'Invoice No': InvoiceNo,
              'Made In': MadeIn,
              Manufacturer,
              MfgDate,
              Model,
              PONo,
              'Part No': PartNo,
              PartID,
              'Purchase From': PurchaseFrom,
              Quantity,
              Remark,
              Replace,
              'Serial No': SerialNo,
              Size,
              'Taken From': TakenFrom,
              Type,

              partTypeDesc,

              action,
              seqNo,

              expenseCode,
              isExpense,

              prodTxnID,
              AllocGUID,
              GitNo,
              GRNGuid,
              BinLoc,
              PLNo,
              PLGuid,

              TyreIndex,
              noOfRetread,
              partinventoryno,
            } = item;

            return {
              partid: PartID ?? '',
              docrefno: InvoiceNo ?? '',
              serialno: SerialNo ?? '',
              partno: PartNo ?? '',
              parttype: Type ?? '',
              model: Model ?? '',
              madein: MadeIn ?? '',
              manufacturer: Manufacturer ?? '',
              purchasefrom: PurchaseFrom ?? '',
              datepurchase: this.toUTCDateTime(DatePurchase) ?? null,
              mfgdate: this.toUTCDateTime(MfgDate) ?? null,
              size: Size ?? '',
              quantity: Quantity ?? '',
              cost: Cost ?? '0',
              insclaimno: InsuranceNo ?? '',
              reusefrom: Replace ?? '',
              remark: Remark ?? '',
              pono: PONo ?? '',

              action: action,
              seqno: seqNo ?? '',

              takenfrom: TakenFrom ?? '',

              tyreindex: TyreIndex ?? '',
              noofretread: noOfRetread ?? '',

              parttypedesc: partTypeDesc ?? '',
              expensecode: expenseCode ?? '',
              isexpense: isExpense ?? '',

              prodtxnid: prodTxnID ?? '',
              allocguid: AllocGUID ?? '',
              gitno: GitNo ?? '',
              grnguid: GRNGuid ?? '',
              binloc: BinLoc,
              plno: PLNo ?? '',
              plguid: PLGuid ?? '',

              partinventoryno: partinventoryno ?? this.jobInventoryValue['Inventory #'],
            };
          }),

          tyres: findTyres.map((item) => {
            const {
              serialno,
              oldserialno,
              actualSerial,
              tyreposition,
              storein,
              inventoryno,
              seqno,
              tyresize,
              tyreindex,
              repair,
              inuse,
              datechange,
              align,
              rowno,
              columnno,
              whsecode,
              prodtxnid,
              action,
              actualtyreguid,
              tyreguid,

              type,
              mfgdate,
              datepurchase,
              noofretread,
              model,
              madein,
              manufacturer,
              unitprice,
              sppcode,
              allocguid,

              jobno,

              tyretype,
              partno,

              startmileage,

              revertgrn,
            } = item;
            return {
              newserial: serialno ?? '',
              currentserial: oldserialno ?? '',
              actualserial: actualSerial ?? '',
              tyreposition: tyreposition ?? '',
              storein: storein ?? '',
              inventoryno: inventoryno ?? '',
              seqno: seqno ?? '',
              tyresize: tyresize ?? '',
              tyreindex: tyreindex ?? '',
              repair: repair ?? '',
              inuse: inuse ?? '',
              datechange: datechange ? (this.toUTCDateTime(datechange) ?? null) : this.toUTCDateTime(this.jobDateDate),
              align: align ?? '',
              rowno: rowno ?? '',
              columnno: columnno ?? '',
              whsecode: whsecode ?? '',
              prodtxnid: prodtxnid ?? '',
              action: action ?? '',
              actualtyreguid: actualtyreguid ?? '',
              currenttyreguid: tyreguid ?? '',
              type: type ?? '',
              mfgdate: this.toUTCDateTime(mfgdate) ?? null,
              datepurchase: this.toUTCDateTime(datepurchase) ?? null,
              noofretread: noofretread ?? '',
              model: model ?? '',
              madein: madein ?? '',
              manufacturer: manufacturer ?? '',
              unitprice: unitprice ?? '',
              sppcode: sppcode ?? '',
              allocguid: allocguid ?? '',
              tyretype: tyretype ?? '',
              partno: partno ?? '',

              jobno: jobno ?? '',

              startmileage: startmileage ?? '0',

              revertgrn: revertgrn ?? false,
            };
          }),
        };
      });

      const attachToJobNo = await Promise.all(
        [
          ...new Set(
            this.tyreConfigUpdateOtherVehicle
              .filter(
                (item) =>
                  item.jobno && item.jobno != this.jobNoValue && item.action != 'O' && !item.prevupdate && !item.revert,
              )
              .map((item) => item.jobno)
              .filter(Boolean),
          ),
        ].map(async (otherjobno) => {
          const getDetails = await this.getWorkshopJobDetails(otherjobno);
          const {
            JobNo,
            JobDate,
            Inventory,
            Trailer,
            RequestedBy,
            RepairType,
            InternalExternal,
            Workshop,
            ServicePerson,
            Mileage,
            Remark,
            DateTimeIn,
            TimeIn,
            DateTimeOut,
            TimeOut,
            Status,
            InvAvailability,
            InvAvailabilityRemark,
            Indicator,
          } = getDetails || {};

          const findParts = this.partUpdateOtherVehicle.filter(
            (item) => item.partinventoryno == Inventory && item.jobno == JobNo,
          );

          const findTyres = this.tyreConfigUpdateOtherVehicle.filter(
            (item) => item.inventoryno == Inventory && item.jobno == JobNo,
          );

          return {
            action: 'E',

            //general info
            jobno: JobNo,
            inventoryno: Inventory,
            jobdate: JobDate,

            //job details
            trailer: Trailer,
            requestby: RequestedBy,
            servicetype: RepairType,
            intext: InternalExternal,
            serviceperson: ServicePerson,
            workshop: Workshop,
            meterreading: Mileage,
            remark1: Remark,
            datein: DateTimeIn ?? null,
            timein: TimeIn ?? '',
            dateout: DateTimeOut ?? null,
            timeout: TimeOut ?? '',
            status: Status, //12001 is the hard coded value in 4G, not sure why

            availability: InvAvailability,
            invavailabilityremark: InvAvailabilityRemark || '',

            //things not in form
              indicator: Indicator, //must be N if saving or editing, if C it's a Cancelled entry

            jobdatedate: new Date(JobDate).formatString('yyyy-MM-dd'),
            jobdatetime: new Date(JobDate).formatString('HHmm'),

            parts: findParts.map((item) => {
              const {
                'Cost (RM)': Cost,
                'Date Purchase': DatePurchase,
                'Insurance Claim No': InsuranceNo,
                'Invoice No': InvoiceNo,
                'Made In': MadeIn,
                Manufacturer,
                MfgDate,
                Model,
                PONo,
                'Part No': PartNo,
                PartID,
                'Purchase From': PurchaseFrom,
                Quantity,
                Remark,
                Replace,
                'Serial No': SerialNo,
                Size,
                'Taken From': TakenFrom,
                Type,

                partTypeDesc,

                action,
                seqNo,

                expenseCode,
                isExpense,

                prodTxnID,
                AllocGUID,
                GitNo,
                GRNGuid,
                BinLoc,
                PLNo,
                PLGuid,

                TyreIndex,
                noOfRetread,
                partinventoryno,
              } = item;

              return {
                partid: PartID ?? '',
                docrefno: InvoiceNo ?? '',
                serialno: SerialNo ?? '',
                partno: PartNo ?? '',
                parttype: Type ?? '',
                model: Model ?? '',
                madein: MadeIn ?? '',
                manufacturer: Manufacturer ?? '',
                purchasefrom: PurchaseFrom ?? '',
                datepurchase: this.toUTCDateTime(DatePurchase) ?? null,
                mfgdate: this.toUTCDateTime(MfgDate) ?? null,
                size: Size ?? '',
                quantity: Quantity ?? '',
                cost: Cost ?? '0',
                insclaimno: InsuranceNo ?? '',
                reusefrom: Replace ?? '',
                remark: Remark ?? '',
                pono: PONo ?? '',

                action: action,
                seqno: seqNo ?? '',

                takenfrom: TakenFrom ?? '',

                tyreindex: TyreIndex ?? '',
                noofretread: noOfRetread ?? '',

                parttypedesc: partTypeDesc ?? '',
                expensecode: expenseCode ?? '',
                isexpense: isExpense ?? '',

                prodtxnid: prodTxnID ?? '',
                allocguid: AllocGUID ?? '',
                gitno: GitNo ?? '',
                grnguid: GRNGuid ?? '',
                binloc: BinLoc,
                plno: PLNo ?? '',
                plguid: PLGuid ?? '',

                partinventoryno: partinventoryno ?? this.jobInventoryValue['Inventory #'],
              };
            }),

            tyres: findTyres.map((item) => {
              const {
                serialno,
                oldserialno,
                actualSerial,
                tyreposition,
                storein,
                inventoryno,
                seqno,
                tyresize,
                tyreindex,
                repair,
                inuse,
                datechange,
                align,
                rowno,
                columnno,
                whsecode,
                prodtxnid,
                action,
                actualtyreguid,
                tyreguid,

                type,
                mfgdate,
                datepurchase,
                noofretread,
                model,
                madein,
                manufacturer,
                unitprice,
                sppcode,
                allocguid,

                jobno,
                partno,

                tyretype,

                startmileage,

                revertgrn,
              } = item;
              return {
                newserial: serialno ?? '',
                currentserial: oldserialno ?? '',
                actualserial: actualSerial ?? '',
                tyreposition: tyreposition ?? '',
                storein: storein ?? '',
                inventoryno: inventoryno ?? '',
                seqno: seqno ?? '',
                tyresize: tyresize ?? '',
                tyreindex: tyreindex ?? '',
                repair: repair ?? '',
                inuse: inuse ?? '',
                datechange:
                  datechange ? (this.toUTCDateTime(datechange) ?? null) : this.toUTCDateTime(this.jobDateDate),
                align: align ?? '',
                rowno: rowno ?? '',
                columnno: columnno ?? '',
                whsecode: whsecode ?? '',
                prodtxnid: prodtxnid ?? '',
                action: action ?? '',
                actualtyreguid: actualtyreguid ?? '',
                currenttyreguid: tyreguid ?? '',
                type: type ?? '',
                mfgdate: this.toUTCDateTime(mfgdate) ?? null,
                datepurchase: this.toUTCDateTime(datepurchase) ?? null,
                noofretread: noofretread ?? '',
                model: model ?? '',
                madein: madein ?? '',
                manufacturer: manufacturer ?? '',
                unitprice: unitprice ?? '',
                sppcode: sppcode ?? '',
                allocguid: allocguid ?? '',
                tyretype: tyretype ?? '',

                jobno: jobno ?? '',
                partno: partno ?? '',

                startmileage: startmileage ?? '0',

                revertgrn: revertgrn ?? false,
              };
            }),
          };
        }),
      );

      const updateOtherInv = [...otherNewInv, ...attachToJobNo];

      for (const inv of updateOtherInv) {
        const resultOther = await this.$apiCallV2('/WorkshopJob/SaveEMSJob', inv);

        const { Results, ErrMsg, EMSJOB } = resultOther?.[0] || {};
        if (resultOther?.[0]?.AuditWarning) auditWarnings.add(resultOther[0].AuditWarning);

        if (Results == '1') {
          this.popupEntryErrorMessages.push(ErrMsg);
        }
        const currentUrl = new URL(window.location.href);
        const newParams = new URLSearchParams();
        newParams.set('JobNo', EMSJOB);

        successMessages.push(
          ErrMsg.replace(
            EMSJOB,
            `<a href="${currentUrl.origin}${currentUrl.pathname}?JobNo=${EMSJOB}}" target="_blank">${EMSJOB}</a>`,
          ),
        );
      }

      const vehicleTodoLinksSaved = await this.savePendingVehicleTodoLinks(EMSJOB || this.jobNoValue);
      if (!vehicleTodoLinksSaved) {
        this.jobNoValue = cleanText(EMSJOB || this.jobNoValue);
        this.menuMode = 'Edit';
        this.viewMode = false;
        this.$fullscreenLoading();
        return;
      }

      await this.populatePopupEntryFields(EMSJOB);
      if (this.approvalSetup?.Enabled === true && this.approvalStatus !== 'Disabled')
        this.jobAuditWarning = [...new Set([this.jobAuditWarning, ...auditWarnings].filter(Boolean))].join('\n');
      this.viewMode = true;
      this.menuMode = 'View';
      this.popupEntrySuccessMessages.push(...successMessages);

      await this.getWorkshopJobs();

      const selectedIndex = this.workshopJobsTableData.findIndex((item) => item['Job #'] == EMSJOB);
      this.workshopJobsTableSelectedRows = [
        { ...this.workshopJobsTableData.find((item) => item['Job #'] == EMSJOB), _rowid: `row${selectedIndex}` },
      ];

      this.$fullscreenLoading();
    },

    openVehicleTodoPopup() {
      if (!this.jobInventoryValue || (this.menuMode != 'New' && !this.jobNoValue)) return;
      this.vehicleTodoPopupVehicleNo = cleanText(this.jobInventoryValue['Inventory #']);
      this.vehicleTodoPopupJobNo = cleanText(this.jobNoValue);
      this.vehicleTodoPopupViewOnly = this.viewMode;
      if (this.vehicleTodoPendingChanges.toLink.some((row) => cleanText(row.previousJobNo))) this.vehicleTodoPopupShowAttached = true;
      this.vehicleTodoPopupInitialAttachedCount = this.vehicleTodoAttachedCount;
      this.vehicleTodoPopupSelectionInitialized = false;
      this.vehicleTodoPopupShow = true;
    },

    closeVehicleTodoPopup() {
      this.vehicleTodoPopupShow = false;
    },

    vehicleTodoSelectionChanged(rows) {
      if (this.vehicleTodoPopupViewOnly) return;
      const selectedRows = Array.isArray(rows) ? rows : [];
      if (!this.vehicleTodoPopupSelectionInitialized) {
        this.vehicleTodoPopupInitialAttachedCount = selectedRows.length;
        this.vehicleTodoPopupSelectionInitialized = true;
      }
      this.vehicleTodoAttachedCount = selectedRows.length;
    },

    vehicleTodoShowAttachedChanged(value) {
      this.vehicleTodoPopupShowAttached = value;
    },

    async refreshVehicleTodoAttachedCount() {
      this.vehicleTodoAttachedCount = 0;
      const inventoryNo = cleanText(this.jobInventoryValue?.['Inventory #']);
      const jobNo = cleanText(this.jobNoValue);
      if (!inventoryNo || !jobNo) return;

      try {
        const result = await this.$apiCallV2('/Vehicle/GetVehicleTodoList', {
          status: 'All',
          vehiclenos: [inventoryNo],
        });
        const rows = Array.isArray(result) ? result : [];
        this.vehicleTodoAttachedCount = rows.filter((row) => {
          const linkedJobNo = cleanText(row['_jobNo'] || row['Job No.'] || row['Job No'] || row.JobNo || row.jobno);
          return linkedJobNo == jobNo;
        }).length;
      } catch (_error) {
        this.vehicleTodoAttachedCount = 0;
      }
    },

    async saveVehicleTodoPopup() {
      if (this.vehicleTodoLinkSaving || this.vehicleTodoPopupSaving) return;
      this.vehicleTodoPopupSaving = true;
      const taskList = this.$refs.vehicleTodoLinkList;
      const changes = taskList?.getDeferredLinkChanges?.() || { toLink: [], toUnlink: [] };
      const updates = taskList?.getDeferredTodoChanges?.() || [];
      const selectedRows = Array.isArray(taskList?.selectedRows) ? taskList.selectedRows : [];
      try {
        const currentJobNo = cleanText(this.vehicleTodoPopupJobNo);
        const reassignedRows = (changes.toLink || []).filter((row) => {
          const linkedJobNo = cleanText(row['_jobNo'] || row['Job No.'] || row['Job No'] || row.JobNo || row.jobno);
          return linkedJobNo && linkedJobNo != currentJobNo;
        });
        if (reassignedRows.length > 0) {
          const confirm = await this.$showAlert(
            'warning',
            'Reassign Vehicle Tasks',
            `${reassignedRows.length} selected task${reassignedRows.length == 1 ? '' : 's'} already ${reassignedRows.length == 1 ? 'belongs' : 'belong'} to another Workshop Job. Reassign ${reassignedRows.length == 1 ? 'it' : 'them'} to this job?`,
            [{ text: 'Reassign' }, { text: 'Cancel' }],
          );
          if (confirm == 'Cancel') return;
        }

        this.vehicleTodoPendingChanges = {
          toLink: (changes.toLink || []).map((row) => ({
            todoid: row['Todo ID'],
            inventoryno: row['Vehicle No.'],
            previousJobNo: row['_jobNo'] || row['Job No.'] || row['Job No'] || row.JobNo || row.jobno || '',
          })),
          toUnlink: (changes.toUnlink || []).map((row) => ({
            todoid: row['Todo ID'],
            inventoryno: row['Vehicle No.'],
          })),
          updates: updates.map((row) => ({ ...row })),
        };
        if (reassignedRows.length > 0) this.vehicleTodoPopupShowAttached = true;
        this.vehicleTodoPendingSelection = selectedRows.map((row) => String(row['Todo ID']));
        this.vehicleTodoAttachedCount = selectedRows.length;
        this.closeVehicleTodoPopup();
      } finally {
        this.vehicleTodoPopupSaving = false;
      }
    },

    cancelVehicleTodoPopup() {
      if (this.vehicleTodoLinkSaving) return;
      this.vehicleTodoAttachedCount = this.vehicleTodoPopupInitialAttachedCount;
      this.closeVehicleTodoPopup();
    },

    async savePendingVehicleTodoLinks(jobNo) {
      const pending = this.vehicleTodoPendingChanges || { toLink: [], toUnlink: [], updates: [] };
      const updates = pending.updates || [];
      if (pending.toLink.length == 0 && pending.toUnlink.length == 0 && updates.length == 0) return true;

      const targetJobNo = cleanText(jobNo);
      if (!targetJobNo) {
        this.popupEntryErrorMessages.push('Vehicle Task Links Failed: Workshop Job number was not returned.');
        return false;
      }

      const todos = [
        ...updates.map((row) => ({
          ...row,
          ...(String(row.action || '').toUpperCase() == 'N' ? { jobno: targetJobNo } : {}),
        })),
        ...pending.toLink.map((row) => ({
          action: 'L',
          todoid: row.todoid,
          inventoryno: row.inventoryno,
          jobno: targetJobNo,
        })),
        ...pending.toUnlink.map((row) => ({
          action: 'L',
          todoid: row.todoid,
          inventoryno: row.inventoryno,
          jobno: '',
        })),
      ];

      this.vehicleTodoLinkSaving = true;
      try {
        const result = await this.$apiCallV2('/Vehicle/SaveVehicleTodo', { todos: todos });
        const failures = (Array.isArray(result) ? result : []).filter(
          (item) => item.Results == '1' || item.Result == 'Failed',
        );
        if (failures.length > 0) {
          this.popupEntryErrorMessages.push(
            `Vehicle Task Changes Failed: ${failures.map((item) => item.ErrMsg || 'Update failed').join('; ')}`,
          );
          return false;
        }
        this.vehicleTodoPendingChanges = { toLink: [], toUnlink: [], updates: [] };
        this.vehicleTodoPendingSelection = null;
        return true;
      } catch (error) {
        this.popupEntryErrorMessages.push(`Vehicle Task Changes Failed: ${error?.message || 'Unable to save changes.'}`);
        return false;
      } finally {
        this.vehicleTodoLinkSaving = false;
      }
    },

    async populateTyreConfigFields(tyreInfo, partInfo, updateStatusValue = true) {
      const {
        oldserialno,
        tyreindex,
        serialno,
        partno,
        partprice,
        prodtxnid,
        tyresize,
        tyretype,
        datepurchase,
        action,
        otherinventoryno,
      } = tyreInfo;
      const { 'Replace Type': type } = this.tyreConfigTableSelectedRow?.[0] || {};
      const isRotation =
        type == 'ROTATE' || type == 'OTHER' || this.tyreReplaceValue?.keyValue == 'ROTATE' || this.tyreReplaceValue?.keyValue == 'OTHER';
      const {
        'Taken From': TakenFrom,
        MfgDate,
        Model,
        'Made In': MadeIn,
        'Part No': PartNo,
        Manufacturer,
        'Purchase From': Supplier,
        PONo,
        DatePurchase,
        'Cost (RM)': JobPrice,
        'Invoice No': InvoiceNo,
        'Insurance Claim No': InsuranceNo,
        Remark,
        prodTxnID,
      } = partInfo;

      const prevTyre = oldserialno ? this.tyreConfigLayout.findLast((item) => item.index == tyreindex) : null;

      const { whsecode, seqno } = prevTyre?.tyreInfo || {};

      const { inuse: trueInuse } = this.tyreConfigTyresList.findLast((item) => item.seqno == seqno) || {};

      if (updateStatusValue)
        if (type == 'OTHER') {
          this.tyreStatusValue = this.tyreStatusOptions.find((item) => item.label == 'Rotate');
        } else if (prevTyre) {
          if (type == 'ROTATE') this.tyreStatusValue = this.tyreStatusOptions.find((item) => item.label == 'Rotate');
          else this.tyreStatusValue = this.tyreStatusOptions.find((item) => item.keyValue == trueInuse ?? '0');
        } else {
          this.tyreStatusValue = this.tyreStatusOptions[0];
        }
      this.tyreStoreInValue = this.warehouseList.find((item) => item['Warehouse Code'] == whsecode);

      if (type && updateStatusValue) {
        this.tyreReplaceValue = this.tyreReplaceRadio.find((item) => item.keyValue == type);
        this.tyreConfigRotateSelectedTyre = null;
        if (type == 'ROTATE' || type == 'OTHER') {
          let inventoryToLoad = null;
          let serialToSelect = serialno;

          if (action == 'N') {
            if (otherinventoryno) {
              inventoryToLoad = tyreInfo.otherinventoryno;
            } else {
              await this.tyreConfigRadioChanged();
              serialToSelect = this.tyreConfigTableSelectedRow[0]?.['New Serial No'] ?? serialno;
            }
          } else if (otherinventoryno) {
            inventoryToLoad = otherinventoryno;
          } else {
            await this.tyreConfigRadioChanged();
            serialToSelect = this.tyreConfigTableSelectedRow[0]?.['New Serial No'] ?? serialno;
          }

          if (inventoryToLoad) {
            this.tyreConfigOtherVehicleValue = this.vehicleList.find((item) => item['Inventory #'] == inventoryToLoad);
            await this.getTyreConfigOtherVehicle();
          }

          this.tyreConfigRotateSelectedTyre = this.tyreConfigSelectedVehicleLayout?.findLast(
            (item) => item.tyreInfo?.serialno == serialToSelect,
          );
          this.tyreConfigSelectedVehicleDropdownValue = this.tyreConfigSelectedVehicleDropdownList?.findLast(
            (item) => item.tyreInfo?.serialno == serialToSelect,
          );
        } else {
          this.tyreConfigRotateLayoutChange(type);
        }
      }

      this.tyreConfigSerialNoValue = serialno ?? '';

      this.tyreExistingValue = this.tyreNewList.find((item) =>
        cleanText(item['Serial No']) == cleanText(serialno) && this.warehouseItemMatchesPart(item, partInfo),
      ) || null;

      let findTyreType = this.tyreTypeList.find((item) => item.ProdTxnID == prodTxnID);
      if (!findTyreType) findTyreType = this.tyreTypeList.find((item) => item.ProdTxnID == prodtxnid);
      if (!findTyreType) findTyreType = this.tyreTypeList.find((item) => item['Product Code'] == tyretype);

      if (!findTyreType) {
        this.tyreConfigTyreTypeDisabled = false;
      } else {
        this.tyreConfigTyreTypeDisabled = true;
        this.tyreConfigTyreTypeValue = findTyreType || null;
      }

      this.tyreConfigPartNoValue = partno ?? PartNo ?? '';
      this.tyreConfigSizeValue = tyresize || (findTyreType?.Size ?? '');
      this.tyreConfigTakenFromValue = this.warehouseList.find((item) => item['Warehouse Code'] == TakenFrom) || {};
      this.tyreConfigUnitPriceValue = this.$currencyFormat(isRotation ? '0' : partprice ?? JobPrice ?? '0');
      this.tyreConfigMfgDateValue = MfgDate ? new Date(MfgDate) : null;
      this.tyreConfigModelValue = Model ?? '';
      this.tyreConfigMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) || {};
      this.tyreConfigManufacturerValue = this.manufacturerList.find((item) => item.MFGCode == Manufacturer) || {};
      this.tyreConfigSupplierValue = this.supplierList.find((item) => item['Supplier Code'] == Supplier) || {};
      this.tyreConfigPONoValue = PONo ?? '';
      if (datepurchase) this.tyreConfigDatePurchaseValue = new Date(datepurchase);
      else if (DatePurchase) this.tyreConfigDatePurchaseValue = new Date(DatePurchase);
      else this.tyreConfigDatePurchaseValue = null;

      this.tyreConfigJobPriceValue = this.$currencyFormat(isRotation ? '0' : JobPrice ?? partprice ?? '0');
      this.tyreConfigInvoiceNoValue = InvoiceNo ?? '';
      this.tyreConfigInsuranceNoValue = InsuranceNo ?? '';
      this.tyreConfigRemarksValue = Remark ?? '';
    },

    tyreConfigUnitPriceOnInput(newValue, _oldValue) {
      if (this.tyreReplaceValue?.keyValue == 'NEW') {
        this.tyreConfigJobPriceValue = newValue;
      }
    },

    //#endregion

    //#region misc

    async inventoryNoSelected(newInv, oldInv) {
      const { 'Inventory #': invNo } = newInv || {};
      const { 'Inventory #': invNoOld } = oldInv || {};
      if (invNo == invNoOld) return;
      if (oldInv) {
        const discard = await this.cancelEdit(
          'Changing the Inventory will reset all fields!<br>Do you wish to discard the changes?',
        );
        if (!discard) {
          this.jobInventoryValue = oldInv;
          return;
        }
      }
      if (invNo) {
        const { dateTimeInValue, dateTimeInDefaultOverridden } = this;
        this.clearPopupFieldsWithoutJobNo();
        this.dateTimeInValue = dateTimeInValue;
        this.dateTimeInDefaultOverridden = dateTimeInDefaultOverridden;
        this.jobInventoryValue = newInv;
        await this.populateVehicleInfo(invNo);
        const vehicleWithDriver = await this.getVehicleDriver(invNo);
        const { CurrDriver } = vehicleWithDriver?.[0] || {};
        this.requestedByValue = this.employeeList.find((item) => item.EmployeeID == CurrDriver) ?? null;
        await this.refreshVehicleTodoAttachedCount();
      }
    },

    parsePartAmount(value) {
      const normalized = typeof value == 'string' ? value.replace(/,/g, '').trim() : value;
      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : 0;
    },

    partHasWarehouseLink(part) {
      if (part?.HasGRNDetail !== null && part?.HasGRNDetail !== undefined && part?.HasGRNDetail !== '') {
        return part.HasGRNDetail == 1 || part.HasGRNDetail == '1';
      }

      const warehouseLinkValue = part?.GRNGuid;
      return Boolean(String(warehouseLinkValue ?? '').trim());
    },

    normalizePartQuantityValue(value) {
      const quantityValue = String(value ?? '').trim();
      return quantityValue ? this.parsePartAmount(quantityValue).toString() : '';
    },

    warehouseItemEffectiveQuantityBalance(item) {
      const rawQuantityBalance = item?.warehouseItemRawQuantityBalance ?? item?.['Quantity Balance'];
      let quantityBalance = this.parsePartAmount(rawQuantityBalance);
      const savedPart = this.partFields.partWarehouseItemSavedValue;
      const isMergedWarehouseItem = Array.isArray(item?.warehouseItemRows) && item.warehouseItemRows.length > 1;
      if (savedPart && this.warehouseItemMatchesPart(item, savedPart) && !isMergedWarehouseItem) {
        quantityBalance += this.parsePartAmount(savedPart.Quantity);
      }
      return quantityBalance;
    },

    warehouseItemMatchesPart(item, part) {
      if (!item || !part) return false;

      const normalizeIdentifier = (value) => String(value ?? '').trim().toUpperCase();
      const itemAllocation = normalizeIdentifier(item.AllocGUID);
      const partAllocation = normalizeIdentifier(part.AllocGUID);
      const itemGitNo = normalizeIdentifier(item.GitNo);
      const partGitNo = normalizeIdentifier(part.GitNo);
      const itemPLGuid = normalizeIdentifier(item.PLGuid);
      const partPLGuid = normalizeIdentifier(part.PLGuid);
      const itemPLNo = normalizeIdentifier(item.PLNo);
      const partPLNo = normalizeIdentifier(part.PLNo);
      const itemGRNGuid = normalizeIdentifier(item.GRNGuid);
      const partGRNGuid = normalizeIdentifier(part.GRNGuid);
      if (itemAllocation && partAllocation) return itemAllocation == partAllocation;
      if (itemPLGuid && partPLGuid) {
        return !itemGitNo || !partGitNo || itemGitNo == partGitNo ? itemPLGuid == partPLGuid : false;
      }
      if (itemPLNo && partPLNo) return itemPLNo == partPLNo;
      if (itemGRNGuid && partGRNGuid) return itemGRNGuid == partGRNGuid;

      const itemWarehouse = normalizeIdentifier(item.Warehouse);
      const partWarehouse = normalizeIdentifier(part.Warehouse || part['Taken From']);
      if (!itemWarehouse || itemWarehouse != partWarehouse) return false;

      const partSerialNo = normalizeIdentifier(part['Serial No']);
      const itemSerialNo = normalizeIdentifier(item['Serial No']);
      if (partSerialNo) return itemSerialNo == partSerialNo;

      const partNo = normalizeIdentifier(part['Part No']);
      return Boolean(partNo) && itemSerialNo == '' && normalizeIdentifier(item['Part No']) == partNo;
    },

    warehouseItemPrimaryLabel(item) {
      const warehouse = String(item?.Warehouse || item?.['Warehouse Code'] || '').trim();
      const serialNo = String(item?.['Serial No'] || '').trim();
      if (serialNo && warehouse) return `${serialNo} (${warehouse})`;
      if (serialNo) return serialNo;
      return warehouse ? `(${warehouse})` : '';
    },

    mergeWarehouseItemRows(items) {
      const mergedItems = [];
      items.forEach((item) => {
        const allocation = String(item?.AllocGUID ?? '')
          .trim()
          .toUpperCase();
        if (!allocation) {
          mergedItems.push(item);
          return;
        }

        const existingIndex = mergedItems.findIndex(
          (existing) =>
            String(existing?.AllocGUID ?? '')
              .trim()
              .toUpperCase() == allocation,
        );
        if (existingIndex == -1) {
          mergedItems.push({ ...item, warehouseItemRows: [item] });
          return;
        }

        const existing = mergedItems[existingIndex];
        const mergedItem = { ...existing };
        Object.keys(item).forEach((key) => {
          const existingValue = mergedItem[key];
          const itemValue = item[key];
          if (existingValue === null || existingValue === undefined || String(existingValue).trim() === '') {
            mergedItem[key] = itemValue;
          }
        });
        mergedItem.warehouseItemRawQuantityBalance =
          this.parsePartAmount(existing.warehouseItemRawQuantityBalance) +
          this.parsePartAmount(item.warehouseItemRawQuantityBalance);
        mergedItem['Quantity Balance'] = mergedItem.warehouseItemRawQuantityBalance;
        mergedItem.warehouseItemRows = [...(existing.warehouseItemRows || [existing]), item];
        mergedItem.label = this.warehouseItemPrimaryLabel(mergedItem);
        mergedItems[existingIndex] = mergedItem;
      });
      return mergedItems;
    },

    warehouseItemFromSavedPart(part) {
      const quantity = this.parsePartAmount(part.Quantity) || 1;
      const cost = this.parsePartAmount(part['Cost (RM)']);
      const warehouse = part['Taken From'] || part.Warehouse || '';
      const serialNo = part['Serial No'] || '';
      const manufactureDate = part.MfgDate || '';
      const purchaseDate = part['Date Purchase'] || '';

      return {
        Warehouse: warehouse,
        'Serial No': serialNo,
        'Part Type': part.Type || part['Part Type'] || '',
        'Part No': part['Part No'] || '',
        'Quantity Balance': 0,
        warehouseItemRawQuantityBalance: 0,
        HasGRNDetail: 1,
        'Unit Price': this.$currencyFormat(quantity ? cost / quantity : 0),
        ProdTxnID: part.prodTxnID || part.ProdTxnID || '',
        Supplier: part['Purchase From'] || '',
        'Date In': purchaseDate,
        'Date Purchase': purchaseDate ? new Date(purchaseDate).formatString('yyyy-MM-dd') : '',
        'Model No': part.Model || '',
        Model: part.Model || '',
        MadeIn: part['Made In'] || '',
        Manufacturer: part.Manufacturer || '',
        ProdDate: manufactureDate,
        'Manufacture Date': manufactureDate,
        InvoiceNo: part['Invoice No'] || '',
        PONo: part.PONo || '',
        BinLoc: part.BinLoc || '',
        AllocGUID: part.AllocGUID || '',
        GitNo: part.GitNo || '',
        PLGuid: part.PLGuid || '',
        PLNo: part.PLNo || '',
        GRNGuid: part.GRNGuid || '',
        label: this.warehouseItemPrimaryLabel({ Warehouse: warehouse, 'Serial No': serialNo }),
      };
    },

    partWarehouseValue(item) {
      const warehouseCode = String(item?.Warehouse || item?.['Warehouse Code'] || '').trim();
      if (!warehouseCode) return null;

      const warehouse = this.warehouseList.find(
        (warehouseItem) => String(warehouseItem['Warehouse Code'] ?? '').trim() == warehouseCode,
      );
      const selectedWarehouse = warehouse ? { ...warehouse } : { 'Warehouse Code': warehouseCode, label: warehouseCode };
      selectedWarehouse.Warehouse = warehouseCode;
      if (item?.AllocGUID) selectedWarehouse.AllocGUID = item.AllocGUID;
      if (item?.BinLoc) selectedWarehouse.BinLoc = item.BinLoc;
      return selectedWarehouse;
    },

    cancelPartSerialNoCheck() {
      clearTimeout(this.partSerialNoCheckTimer);
      this.partSerialNoCheckTimer = null;
      this.partSerialNoCheckRequestId++;
      this.partSerialNoChecking = false;
      this.partSerialNoCheckResult = null;
    },

    async checkPartSerialNo(value) {
      const selectedPart = this.partSelectedItem;
      if (this.partWarehouseItemSelected || value == String(selectedPart?.['Serial No'] ?? '').trim()) return '';
      const duplicate = this.partTableDataFiltered.some((item) =>
        item !== selectedPart && !(item.seqNo != null && selectedPart?.seqNo != null && item.seqNo == selectedPart.seqNo) &&
        String(item['Serial No'] ?? '').trim() == value,
      );
      if (duplicate) return 'Serial No already exists';
      const result = await this.$apiCallV2('/WorkshopJob/CheckTyreSerialNoValid', { serialno: value });
      return result?.[0]?.Results == 0 ? '' : 'Serial No already exists';
    },

    partSerialNoInputChanged(value) {
      this.cancelPartSerialNoCheck();
      this.partSerialNoError = '';
      const selectedSerialNo = String(this.partFields.partSerialNoValue?.['Serial No'] ?? '').trim();
      if (selectedSerialNo != String(value ?? '').trim()) {
        this.partFields.partSerialNoValue = null;
      }
      this.trySelectWarehouseItemFromManualFields();
      const serialNo = String(value ?? '').trim();
      const selectedPart = this.partSelectedItem;
      if (!serialNo || this.viewMode || this.partWarehouseItemSelected || serialNo == String(selectedPart?.['Serial No'] ?? '').trim()) return;
      const requestId = this.partSerialNoCheckRequestId;
      this.partSerialNoCheckTimer = setTimeout(async () => {
        this.partSerialNoCheckTimer = null;
        if (!this.showPopup || this.partWarehouseItemSelected || String(this.partFields.partSerialNoInputValue ?? '').trim() != serialNo || this.partSelectedItem !== selectedPart) return;
        this.partSerialNoChecking = true;
        try {
          const message = await this.checkPartSerialNo(serialNo);
          if (requestId === this.partSerialNoCheckRequestId && String(this.partFields.partSerialNoInputValue ?? '').trim() == serialNo && this.partSelectedItem === selectedPart) {
            this.partSerialNoError = message;
            this.partSerialNoCheckResult = !message;
          }
        } catch (_error) {
          if (requestId === this.partSerialNoCheckRequestId && String(this.partFields.partSerialNoInputValue ?? '').trim() == serialNo && this.partSelectedItem === selectedPart) {
            this.partSerialNoError = 'Unable to check Serial No. Please try again.';
            this.partSerialNoCheckResult = false;
          }
        } finally {
          if (requestId === this.partSerialNoCheckRequestId) this.partSerialNoChecking = false;
        }
      }, 350);
    },

    partWarehouseSelected() {
      this.cancelPartSerialNoCheck();
      this.partSerialNoError = '';
      this.trySelectWarehouseItemFromManualFields();
      if (!this.partWarehouseItemSelected) this.partSerialNoInputChanged(this.partFields.partSerialNoInputValue);
    },

    trySelectWarehouseItemFromManualFields() {
      if (this.partWarehouseItemSelected || !this.partFields.partTypeValue) return;

      const warehouse = String(
        this.partFields.partTakenFromValue?.Warehouse ||
          this.partFields.partTakenFromValue?.['Warehouse Code'] ||
          this.partFields.partTakenFromInputValue ||
          '',
      ).trim();
      const serialNo = String(this.partFields.partSerialNoInputValue || '').trim();
      if (!warehouse || !serialNo) return;

      const matches = this.warehouseItemListFiltered.filter(
        (item) => String(item.Warehouse ?? '').trim() == warehouse && String(item['Serial No'] ?? '').trim() == serialNo,
      );
      if (matches.length == 1) this.warehouseItemSelected(matches[0]);
    },

    partTypeSelected(newItem, oldItem) {
      if (newItem && oldItem && cleanText(newItem['Part Type']) == cleanText(oldItem['Part Type'])) return;
      this.clearPartFieldsWithoutType();
      const { partTypeValue } = this.partFields || {};
      const { Size, 'Unit Price': UnitPrice } = partTypeValue || {};
      this.partFields.partSizeValue = Size ?? '';
      this.partFields.partUnitPriceValue = this.$currencyFormat(UnitPrice ?? '0');

    },

    serialNoSelected() {
      const { partSerialNoInputValue } = this.partFields || {};
      const findSerial = this.serialNoListFiltered.find((item) => item['Serial No'] == partSerialNoInputValue) ?? null;
      if (!findSerial) {
        return;
      }
      this.partFields.partSerialNoValue = findSerial;

      this.partFields.partTakenFromValue = null;
      this.partFields.partTakenFromInputValue = '';
      this.partFields.partModelValue = '';
      this.partFields.partMadeInValue = null;
      this.partFields.partMFGDateValue = null;
      this.partFields.partDatePurchaseValue = null;
      this.partFields.partPurchaseFromValue = null;
      this.partFields.partPartNoValue = '';
      this.partFields.partUnitPriceValue = '';
      this.partFields.partQuantityValue = '';

      const { Warehouse, Model, MadeIn, ProdDate, PurcDate, PalletNo, UnitPrice, Supplier, Manufacturer } = findSerial;

      this.partFields.partTakenFromInputValue = Warehouse;
      this.partFields.partTakenFromValue = this.takenFromListFiltered.find(
        (item) => item.Warehouse == Warehouse && item['Serial No'] == partSerialNoInputValue,
      );
      this.partFields.partModelValue = Model;
      this.partFields.partMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.partFields.partMFGDateValue = ProdDate ? new Date(ProdDate) : null;
      this.partFields.partDatePurchaseValue = PurcDate ? new Date(PurcDate) : null;
      this.partFields.partPartNoValue = PalletNo;
      this.partFields.partUnitPriceValue = this.$currencyFormat(UnitPrice ?? '0');
      this.partFields.partQuantityValue = '1';
      this.partFields.partManufacturerValue =
        this.manufacturerList.find((item) => item.MFGCode == Manufacturer) ?? null;
      this.partFields.partPurchaseFromValue =
        this.supplierList.find((item) => item['Supplier Code'] == Supplier) ?? null;
    },

    warehouseItemSelected(item, oldItem) {
      this.cancelPartSerialNoCheck();
      this.partSerialNoError = '';
      if (item && oldItem && this.warehouseItemMatchesPart(item, oldItem)) return;
      if (!item) {
        this.partFields.partWarehouseItemValue = null;
        return;
      }

      const serialItem = this.serialNoList.find((serial) => serial['Serial No'] == item['Serial No']) || {};
      const manufacturer = item.Manufacturer || serialItem.Manufacturer;
      const madeIn = item.MadeIn || serialItem.MadeIn;
      const model = item['Model No'] || serialItem.Model || '';
      const partNo = item['Part No'] || serialItem.PalletNo || '';
      const unitPrice = this.parsePartAmount(item['Unit Price'] ?? serialItem.UnitPrice ?? '0');
      const supplier = item.Supplier || serialItem.Supplier;
      const manufactureDate = item.ProdDate || serialItem.ProdDate;
      const purchaseDate = item['Date In'] || serialItem.PurcDate;

      this.partFields.partWarehouseItemValue = item;
      this.partFields.partWarehouseItemSearch = item.label || '';
      this.partFields.partSerialNoValue = serialItem['Serial No'] ? serialItem : null;
      this.partFields.partSerialNoInputValue = item['Serial No'] || '';
      const selectedWarehouse = this.partWarehouseValue(item);
      this.partFields.partTakenFromValue = selectedWarehouse;
      this.partFields.partTakenFromInputValue = selectedWarehouse?.label ?? item.Warehouse ?? '';
      this.partFields.partPartNoValue = partNo;
      this.partFields.partModelValue = model;
      this.partFields.partMadeInValue = this.countryList.find((country) => country['Country Code'] == madeIn) ?? null;
      this.partFields.partMFGDateValue = manufactureDate ? new Date(manufactureDate) : null;
      this.partFields.partDatePurchaseValue = purchaseDate ? new Date(purchaseDate) : null;
      this.partFields.partUnitPriceValue = this.$currencyFormat(unitPrice);
      this.partFields.partManufacturerValue =
        this.manufacturerList.find((manufacturerItem) => manufacturerItem.MFGCode == manufacturer) ?? null;
      this.partFields.partPurchaseFromValue =
        this.supplierList.find((supplierItem) => supplierItem['Supplier Code'] == supplier) ?? null;
      this.partFields.partPONoValue = item.PONo || '';
      this.partFields.partInvoiceNoValue = item.InvoiceNo || '';

      const quantityBalance = this.partQuantityMaximum;
      const currentQuantity = this.parsePartAmount(this.partFields.partQuantityValue);
      if (quantityBalance !== null && quantityBalance > 0 && (!currentQuantity || currentQuantity > quantityBalance)) {
        this.partFields.partQuantityValue = quantityBalance >= 1 ? '1' : quantityBalance.toString();
      }
      const normalizedQuantity = this.normalizePartQuantityValue(this.partFields.partQuantityValue);
      if (normalizedQuantity) this.partFields.partQuantityValue = normalizedQuantity;
    },

    takenFromSelected() {
      const { partTakenFromValue, partTakenFromInputValue } = this.partFields || {};

      const findWarehouse = this.takenFromListFiltered.find((item) => item.Warehouse == partTakenFromInputValue);
      if (!findWarehouse && !partTakenFromValue) return;

      this.partFields.partSerialNoValue = null;
      this.partFields.partSerialNoInputValue = '';
      this.partFields.partModelValue = '';
      this.partFields.partMadeInValue = null;
      this.partFields.partMFGDateValue = null;
      this.partFields.partDatePurchaseValue = null;
      this.partFields.partPurchaseFromValue = null;
      this.partFields.partPartNoValue = '';
      this.partFields.partUnitPriceValue = '';
      this.partFields.partQuantityValue = '';
      this.partFields.partPONoValue = '';
      this.partFields.partInvoiceNoValue = '';

      const {
        'Serial No': SerialNo,
        Model,
        MadeIn,
        ProdDate,
        'Date In': PurchDate,
        Supplier,
        'Unit Price': UnitPrice,
        'Part No': PartNo,
        PONo,
        InvoiceNo,
        PickQty,
      } = partTakenFromValue || {};

      this.partFields.partSerialNoInputValue = SerialNo;
      this.partFields.partModelValue = Model;
      this.partFields.partMadeInValue = this.countryList.find((item) => item['Country Code'] == MadeIn) ?? null;
      this.partFields.partMFGDateValue = ProdDate ? new Date(ProdDate) : null;
      this.partFields.partDatePurchaseValue = PurchDate ? new Date(PurchDate) : null;
      this.partFields.partPurchaseFromValue =
        this.supplierList.find((item) => item['Supplier Code'] == Supplier) ?? null;
      this.partFields.partPartNoValue = PartNo;
      this.partFields.partUnitPriceValue = this.$currencyFormat(UnitPrice ?? '0');
      this.partFields.partQuantityValue = this.normalizePartQuantityValue(PickQty ?? '1') || '1';
      this.partFields.partPONoValue = PONo;
      this.partFields.partInvoiceNoValue = InvoiceNo;
    },

    async tyreStatusSelected(_new, oldValue) {
      if (!oldValue) return;
      const discard = await this.$showAlert(
        'warning',
        'Changing this will discard your current changes!',
        'Do you wish to discard the changes?',
        [{ text: 'Discard', color: 'Red', textColor: 'white' }, { text: 'Cancel' }],
      );

      if (discard == 'Cancel') {
        this.tyreStatusValue = oldValue;
        return false;
      }
      this.tyreConfigSelectedVehicleLayout = null;
      this.tyreConfigSelectedVehicleNo = '';
      this.tyreReplaceValue = null;
      this.clearTyreConfigFieldsOnly();
    },
    tyreTypeSelected(newItem, oldItem) {
      if (newItem && oldItem && cleanText(newItem['Part Type']) == cleanText(oldItem['Part Type'])) return;
      const { keyValue: replaceType } = this.tyreReplaceValue || {};
      if (replaceType != 'NEW') return;
      const { 'Unit Price': UnitPrice } = this.tyreConfigTyreTypeValue || {};
      this.tyreConfigUnitPriceValue = this.$currencyFormat(UnitPrice ?? '0');
      this.tyreConfigJobPriceValue = this.tyreConfigUnitPriceValue;
    },
    //#endregion

    //#region helpers
    async cancelEdit(warningText = null) {
      if (!warningText) warningText = 'You have unsaved changes.<br>Do you wish to discard the changes?';
      const discard = await this.$showAlert('warning', 'Discard Changes', warningText, [
        { text: 'Discard', color: 'Red', textColor: 'white' },
        { text: 'Cancel' },
      ]);

      if (discard == 'Cancel') return false;
      return true;
    },

    async tabClick(_tabItem, tabIndex) {
      if (this.fieldsChanged) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }
      this.selectedTabIndex = tabIndex;
      this.tabFieldsCancel();
    },

    async tableRowClicked(_buttonText, rowClicked) {
      if (this.fieldsChanged && !this.viewMode) {
        const discard = await this.cancelEdit();
        if (!discard) return;
      }
      this.tabFieldsAction = this.viewMode ? 'V' : 'E';
      this.tabFieldsCancel();
      switch (this.tabItems[this.selectedTabIndex].title) {
        case this.docTabTitle:
          this.docTableSelectedRows = [rowClicked];
          if (this.docSelectedItem != rowClicked) {
            try {
              await this.loadDocumentFiles(rowClicked);
            } catch (error) {
              this.popupEntryErrorMessages.push(error.message || 'Unable to load this attachment.');
              return;
            }
            this.populateDocumentFields(rowClicked);
            if (this.tabFieldsAction == 'E') this.docTableData = this.reorderTableNo(this.docTableData);
            this.docSelectedItem = rowClicked;
            this.docTableSelectedRows = [rowClicked];
          }
          break;
        case this.taskTabTitle:
          this.taskTableSelectedRows = [rowClicked];
          if (this.taskSelectedItem != rowClicked) {
            this.populateTaskFields(rowClicked);
            this.taskSelectedItem = rowClicked;
            this.taskTableSelectedRows = [rowClicked];
          }
          break;
        case this.partTabTitle:
          this.partTableSelectedRows = [rowClicked];
          if (this.partSelectedItem != rowClicked) {
            const part = this.partTableData.find((item) => item.seqNo == rowClicked.seqNo);
            this.populatePartFields(part);
            this.partSelectedItem = part;
            this.partTableSelectedRows = [rowClicked];
          }
          break;
      }

      this.tabsFields = true;
    },

    convertToArray(item) {
      if (!item) return [];
      return Array.isArray(item) ? item : [item];
    },

    combineDateTime(date, time) {
      //the time is to be specifically HHMM
      const dateToArray = date?.split('T')[0].split('-') || null;
      return dateToArray ?
          new Date(dateToArray[0], dateToArray[1] - 1, dateToArray[2], time.substring(0, 2), time.substring(2, 4))
        : null;
    },

    reorderTableNo(table) {
      return table.map((item, idx) => {
        const copy = { ...item };
        delete copy['#'];
        return { '#': idx + 1, ...copy };
      });
    },

    tyrePositionInfo(tyreIndex) {
      const align =
        tyreIndex <= 49 ? 'L'
        : tyreIndex <= 99 ? 'R'
        : 'S';

      const rowNo = tyreIndex < 100 ? (tyreIndex % 10) + 1 : 0;

      let tyrePosition = '';
      if (align == 'L') {
        tyrePosition = rowNo >= 1 && rowNo <= 5 ? 'FL' : 'RL';
      } else if (align == 'R') {
        tyrePosition = rowNo >= 1 && rowNo <= 5 ? 'FR' : 'RR';
      } else if (align == 'S') {
        tyrePosition = 'SP';
      } else {
        tyrePosition = rowNo == 0 ? 'FC' : 'RC';
      }

      let columnNo = 0;
      if (tyreIndex < 100) {
        const strIndex = tyreIndex.toString();
        const firstDigit = strIndex.length == 1 ? 0 : parseInt(strIndex[0], 10);
        columnNo = firstDigit + 1;
      }

      return { align, tyrePosition, rowNo, columnNo };
    },

    tyreConfigToStructure(tyreConfig, tyreLayout) {
      const tyreConfigList = this.convertToArray(tyreConfig).map((tyre) => {
        const {
          TyreIndex,
          SeqNo,
          'Serial No': serialNo,
          'Part No': PartNo,
          Model,
          Manufacturer,
          MadeIn,
          Size,
          Position,
          'Mfg Date': mfgdate,
          DatePurchase,
          'Date Change': dateChange,
          Align,
          '# of Retread': noOfRetread,
          'In Use': inuse,
          Remark,
          InventoryNo,
          TyreGUID,
          JobNo,
          RepairType,
          WHSE_Code,
          OldSerialNo,
          StartMileage,
          EndMileage,
          TyreType,
          UnitPrice,
          Supplier,
          WhseCode,
          ProdTxnId,
          PartPrice,
          DateUpdated,
          OtherInventoryNo,
          OtherJobNo,
        } = tyre || {};

        const { description } = tyreLayout.find((item) => item.index == TyreIndex) || {};
        const repairType = cleanText(RepairType);

        return {
          serialno: cleanText(serialNo),
          partno: PartNo,
          model: Model,
          manufacturer: Manufacturer,
          madein: MadeIn,
          tyresize: Size,
          tyreposition: description || Position,
          mfgdate: mfgdate?.split('T')[0] || null,
          datepurchase: DatePurchase?.split('T')[0] || null,
          datechange: dateChange?.split('T')[0] || null,
          align: Align,
          noofretread: noOfRetread,
          inuse,
          remark: Remark,
          inventoryno: cleanText(InventoryNo),
          seqno: parseInt(SeqNo),
          tyreguid: TyreGUID,
          tyreindex: cleanText(TyreIndex),
          label: `${description} - ${cleanText(serialNo)}`,
          action: 'O',
          jobno: cleanText(JobNo),
          type: typeof repairType == 'string' ? repairType.toUpperCase() : repairType,
          storein: cleanText(WHSE_Code),
          oldserialno: cleanText(OldSerialNo),
          startmileage: StartMileage,
          endmileage: EndMileage,
          tyretype: TyreType,
          unitprice: UnitPrice,
          supplier: Supplier,
          whsecode: WhseCode,
          prodtxnid: ProdTxnId,
          partprice: PartPrice,
          dateupdated: DateUpdated,
          otherinventoryno: cleanText(OtherInventoryNo),
          otherjobno: cleanText(OtherJobNo),
        };
      });

      return tyreConfigList;
    },

    toUTCDateTime(localDateStr) {
      if (localDateStr && localDateStr instanceof Date && !isNaN(localDateStr)) {
        return localDateStr.formatString('yyyy-MM-ddTHH:mm:ss');
      }
      return localDateStr;
    },
    //#endregion

    getFilterStartDate(endDate) {
      const startDate = new Date(endDate);
      const weeks = Math.max(0, Math.floor(Number(this.defaultWeeksToShow) || 0));
      startDate.setDate(startDate.getDate() - weeks * 7);
      return startDate;
    },

    async getDefaultWeeksToDisplay() {
      const result = await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=EMS_JobFilterMonth');
      const { SettingsValue } = result[0] || {};
      if (SettingsValue === null || SettingsValue === undefined || SettingsValue === '') return;
      this.defaultWeeksToShow = Number(SettingsValue);
      this.activeStartDate = this.getFilterStartDate(this.activeEndDate);
      this.filterStartDate = this.getFilterStartDate(this.filterEndDate);
    },
    async getDateTimeInDefaultSetting() {
      const result = await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=EMS_AutoDateIn');
      this.dateTimeInDefaultEnabled = ['1', 'TRUE', 'YES', 'Y'].includes(String(result?.[0]?.SettingsValue ?? '').trim().toUpperCase());
    },
    async openFromParams() {
      const params = new URLSearchParams(window.location.search);

      const newValue = params.get('New');
      if (newValue != null) this.menuClicked({ key: 'New' });

      const View = params.get('View');

      const Edit = params.get('Edit');

      if (View || Edit) {
        this.jobNoFilterActive = true;
        this.dateFilterActive = false;
        this.vehicleFilterActive = false;
        this.jobNoFilterValue = View || Edit;
        await this.filterRefreshClick();
        const selectedJob = this.workshopJobsTableData.find(
          (item) => cleanText(item['Job #']) == cleanText(View || Edit),
        );
        this.workshopJobsTableSelectedRows = selectedJob ? [selectedJob] : [];
        if (View) this.menuClicked({ key: 'View' });
        if (Edit) this.menuClicked({ key: 'Edit' });
      }
    },
  },

  async mounted() {
    await this.getSAS();
    const listPrerequisites = [
      this.getVehicleList(),
      this.getDefaultWeeksToDisplay(),
    ];
    const deferredLists = [
      this.getVehicleCategoryList(),
      this.getEmployeeList(),
      this.getSupplierList(),
      this.getDocType(),
      this.getDocCode(),
      this.getServices(),
      this.getServicesTask(),
      this.getPartType(),
      this.getCountry(),
      this.getManufacturer(),
      this.getWarehouse(),
      this.getLocalCurrency(),
      this.getWorkOrderApprovalSetup(),
      this.getDateTimeInDefaultSetting(),
    ];
    const promiseList = [...listPrerequisites, ...deferredLists];
    let finished = 0;
    for (const p of promiseList) {
      p.then(() => {
        finished++;
        this.$topLoading((finished / promiseList.length) * 0.9);
      });
    }

    await Promise.all(listPrerequisites);
    await this.getWorkshopJobs();
    await Promise.all(deferredLists);
    this.workshopPageLoading = false;
    this.$topLoading(1);

    this.openFromParams();
  },

  beforeDestroy() {
    this.cancelPartSerialNoCheck();
    this.cancelTyreConfigSerialNoCheck();
  },
  watch: {
    jobDateValue(value) {
      if (this.menuMode == 'New' && this.dateTimeInDefaultEnabled && !this.dateTimeInDefaultOverridden) {
        this.dateTimeInValue = value ? new Date(value) : null;
      }
    },
    tyreReplaceValue() {
      this.cancelTyreConfigSerialNoCheck();
    },
    tyreConfigPopupShow(value) {
      if (!value) this.cancelTyreConfigSerialNoCheck();
    },
    jobInventoryValue() {
      this.maintenanceImportRequestId++;
      this.maintenanceImportShow = false;
      this.maintenanceDueRows = [];
    },
    showPopup(value) {
      if (!value) {
        this.cancelPartSerialNoCheck();
        this.cancelTyreConfigSerialNoCheck();
        this.maintenanceImportRequestId++;
        this.taskAddShow = false;
        this.maintenanceImportShow = false;
        this.vehicleTodoPopupShow = false;
        this.vehicleTodoPendingChanges = { toLink: [], toUnlink: [], updates: [] };
        this.vehicleTodoPendingSelection = null;
        this.vehicleTodoAttachedCount = 0;
        this.vehicleTodoPopupSelectionInitialized = false;
        this.vehicleTodoPopupShowAttached = false;
        this.vehicleTodoPopupShowCancelled = false;
      }
    },

    tyreRotateOtherVehicleWorkshopJobValue() {
      if (this.tyreRotateOtherVehicleWorkshopJobInputValue) this.getTyreConfigOtherVehicle();
    },

    tyreConfigSelectedVehicleLayout() {
      const findInLayout = this.tyreConfigSelectedVehicleLayout?.find(
        (item) => this.tyreConfigRotateSelectedTyre?.index == item.index,
      );
      if (findInLayout?.state != 'saved') {
        this.clearTyreConfigFieldsOnly();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.serial-no-input {
  display: flex;
  align-items: center;
  gap: 6px;

  > .input-main {
    flex: 1;
    min-width: 0;
  }

  > .serial-no-status {
    display: flex;
    flex: 0 0 14px;
    align-items: center;
  }
}
.main {
  padding: 10px 45px;
  padding-right: 4px;
  display: flex;
  flex-direction: row;
  gap: 4px;
  font-size: 12px;

  > .left-menu-container {
    height: 100%;
    width: fit-content;
  }

  .right-container {
    display: flex;
    flex-direction: column;
    gap: 4px;

    width: 100%;
    height: 100%;

    min-width: 0;
    .right-inner-container {
      width: 100%;
      padding: 6px;
      border-radius: 5px;
      background-color: white;
    }
  }
}

.popup-entry {
  .top-info-container {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    margin: 0 5px;
    gap: 10px;
    > .vehicle-selector-container { flex: 1; min-width: 240px; }
  }

  .job-date-summary {
    flex: 0 0 auto;
  }

  .job-total-summary {
    border: 1px solid gray;
    border-radius: 5px;
    padding: 2px 10px;
    white-space: nowrap;
  }
  .job-price-approval-summary {
    margin-left: auto;
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }
  .job-approval-status { font-size: 12px; padding: 3px 10px; }

  .job-confirmation-actions {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin: 5px 0 8px 5px;
  }

  .job-details-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: 12px;
    min-height: 0;
    &.with-approval-history {
      grid-template-columns: minmax(300px, 3fr) minmax(0, 2fr);
    }
    &.with-audit-warning { grid-template-rows: minmax(0, 1fr) auto; }
  }
  .job-audit-warning { grid-column: 1 / -1; padding-top: 6px; border-top: 1px solid #bdbdbd; font-size: 11px; line-height: 15px; color: #555; white-space: pre-line; }
  .job-details-fields {
    min-height: 0;
    min-width: 0;
    overflow: auto;
  }
  .job-approval-history { min-height: 0; min-width: 0; padding: 0 8px 8px; font-size: 12px; border: 1px solid #bdbdbd; box-sizing: border-box; }
  .job-approval-history-title { font-weight: bold; margin: 8px 0; }
  .job-approval-history-empty { margin: 8px 0; color: #555; }
  .job-approval-history ::v-deep .row-overlay { display: none; }

  .vehicle-selection-note {
    margin-top: 5px;
    padding: 6px 8px;
    border-left: 3px solid #d6a700;
    background: #fff4b8;
    color: #5f4700;
    font-size: 12px;
    line-height: 16px;
  }

  .tab-component-container {
    margin-top: 5px;
    height: calc(100% - 30px); // deduct height of Inventory No and Inventory Code inputs

    > .tab-component {
      height: 100%;

      .tab-container {
        height: 100%;
      }
    }
  }

  .table-tab-container {
    display: grid;
    grid-template-columns: 3fr 2fr;
    gap: 10px;
    > *:only-child {
      grid-column: 1/-1;
    }

    .cta-group {
      display: flex;
      gap: 4px;
      justify-content: end;
      margin-bottom: 10px;
      align-items: center;
    }

    .table-container {
      display: flex;
      flex-direction: column;
      min-width: 0;
      min-height: 0;
    }

    .part-fields-container {
      min-width: 0;
      min-height: 0;
      align-self: start;
    }
  }
}

.vehicle-todo-popup {
  position: fixed;
  top: $top-nav-height;
  left: 0;
  width: 100%;
  height: calc(100% - #{$top-nav-height});
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 42px;
  box-sizing: border-box;
  background-color: rgba(0, 0, 0, 0.3);
}

.vehicle-todo-popup-panel {
  width: 100%;
  max-width: 1100px;
  height: 85%;
  max-height: 760px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 1px solid #b4b4b4;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.maintenance-import-panel {
  max-width: 1280px;
  height: 75%;
  max-height: 80%;
}

.maintenance-import-list {
  flex: 1;
  min-height: 0;
  padding: 12px 14px;

  ::v-deep .top-block {
    margin: 0 -14px;
    padding: 0 14px;
    border-bottom: 2px solid rgb(180, 180, 180);

    .title {
      font-size: 1em;
    }
  }
}

.task-add-panel {
  max-width: 1200px;
}

.task-add-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 12px 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.task-add-panel .vehicle-todo-popup-header {
  border-bottom: 2px solid rgb(180, 180, 180);
}

.task-add-panel .vehicle-todo-popup-title {
  font-size: 1em;
}

.task-add-toolbar {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 10px;
}

.task-add-service {
  flex: 1 1 280px;
  min-width: 0;
}

.task-add-list {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;
}

.task-add-layout {
  display: grid;
  flex: 1;
  min-height: 0;
  grid-template-columns: minmax(0, 1fr);
  gap: 14px;
}

.task-add-layout-editor-open {
  grid-template-columns: minmax(0, 1.1fr) minmax(340px, 0.9fr);
}

.task-add-list-actions {
  flex-shrink: 0;
  margin-left: auto;
  margin-bottom: 0;
}

.task-add-editor {
  align-self: start;
}

@media (max-width: 900px) {
  .popup-entry .job-details-layout.with-approval-history {
    grid-template-columns: minmax(0, 1fr);
    overflow: auto;
  }
  .popup-entry .job-details-fields {
    overflow: visible;
  }
  .popup-entry .job-approval-history {
    min-height: 240px;
  }
  .task-add-layout-editor-open {
    grid-template-columns: minmax(0, 1fr);
  }
}

.vehicle-todo-popup-header {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 10px 14px;
  border-bottom: 1px solid #d8dee8;
  color: #2d3348;
}

.vehicle-todo-popup-title-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.vehicle-todo-popup-title {
  font-size: 16px;
  font-weight: bold;
}

.vehicle-todo-popup-body {
  display: flex;
  flex: 1;
  min-height: 0;
  padding: 10px 10px 0;
}

.vehicle-todo-popup-content {
  flex: 1;
  width: 100%;
  min-height: 0;
  text-align: left;
}

.vehicle-todo-popup-footer {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: space-between;
  gap: 4px;
  padding: 8px 10px;
}

.vehicle-todo-popup-filters,
.vehicle-todo-popup-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.vehicle-todo-popup-actions {
  margin-left: auto;
}

.workshop-task-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.workshop-task-import,
.workshop-task-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.workshop-task-actions {
  margin-bottom: 0 !important;
  gap: 20px;
}

.vehicle-todo-attached-count {
  color: #555;
  white-space: nowrap;
}

.cta-group {
  display: flex;
  gap: 4px;
  justify-content: end;
  align-items: center;
  margin-bottom: 8px;
}

.tyre-config-tab {
  display: flex;
  flex-direction: column;
  gap: 8px;
  .tyre-history-notice {
    align-self: start;
    height: fit-content;
    padding: 8px 10px;
    border-left: 3px solid #d6a700;
    background: #fff4b8;
    color: #5f4700;
    font-size: 12px;
  }
}

.tyre-config-tab-container {
  min-height: 0;
  flex: 1;
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 8px;
  .tyre-config-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .table-wrapper {
    min-height: 0;
    min-width: 0;
    display: flex;
    flex-direction: column;
    .table-box-main {
      min-height: 0;
    }
  }
}

.popup-entry-main {
  padding-right: 4px;
}

.tyre-config {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 50%);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  .tyre-config-inner {
    background: white;
    height: fit-content;
    max-height: 90dvh;
    box-sizing: border-box;
    min-width: 600px;
    max-width: none;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
    top: 24px;
    padding: 8px 0 0;
    border-radius: 5px;

    > .tyre-form-row {
      display: flex;
      width: 100%;
      min-width: 0;
    }

    > .tyre-form-row > .tyre-grid-container,
    > .tyre-form-row > .form-container {
      padding: 0 8px 8px;
      box-sizing: border-box;
      width: 100%;
      flex: 1 1 100%;
    }

    > .tyre-form-row > .tyre-grid-container {
      display: flex;
      flex-direction: column;
      padding-right: 0;

      > .box-container-main {
        height: 100%;
      }
    }

    > .tyre-form-row-with-grid > .tyre-grid-container,
    > .tyre-form-row-with-grid > .form-container {
      flex: 1 1 0;
      width: 50%;
    }

    > .tyre-form-row-with-grid > .form-container {
      flex: 1 1 560px;
      min-width: 0;
    }

    > .cta-group {
      border-top: 1px solid rgba(128, 128, 128, 0.4);
    }

    .cta-group {
      display: flex;
      gap: 5px;
      justify-content: end;
      align-items: center;
      margin-bottom: 0;
      position: sticky;
      bottom: 0;
      padding: 8px;
      background-color: white;

      margin-top: -4px;

      .tyre-config-error-messages {
        display: flex;
        flex: 1;
        min-width: 0;
        flex-direction: column;
        align-items: flex-start;
        gap: 2px;
      }
    }

    .tyre-config-list {
      display: flex;

      height: 100%;
      // max-height: 400px;
      gap: 5px;
      width: 100%;
    }

    .tyre-info-container {
      gap: 4px;

      > .tyre-config-box {
        width: 100%;
        box-sizing: border-box;
      }
    }
  }
}

.rotate-other-vehicle-popup {
  position: fixed;
  z-index: 9999;
  background: rgba(0, 0, 0, 50%);
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .popup-title {
    font-size: 1.05rem;
    font-weight: bold;
    margin-bottom: 8px;
  }

  .rotate-other-vehicle-popup-inner {
    background: white;
    height: fit-content;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 5px;
    position: relative;
    top: 20px;
    padding: 8px;
    border-radius: 5px;
  }

  .cta-group {
    display: flex;
    gap: 5px;
    justify-content: end;
    align-items: center;
    margin-bottom: 0;
  }
}

.flex-container {
  display: flex;
  gap: 8px;
}

.align-items-center {
  align-items: center;
}

.flex-column {
  flex-direction: column;
}

.min-height-0 {
  min-height: 0;
}

.flex-1 {
  flex: 1;
}

.error-text {
  color: red;
}

</style>
